import React, { useMemo, useState, useEffect, useRef, createElement } from 'react';
import moment from 'moment';
import { View, Text, Pressable, TextInput, Image } from '../lib/react-native-compat';
import {
    loadHEETLabor, loadHeavyJobCards, loadJobTimecards, saveJobTicketFile, pNum,
    loadBillingSignatures, sendBillingSignature, billingSignatureAction,
    loadBillingSignatureDocument, loadAttendanceRoster, loadApprovedTimeCard, sendForceReportEmail,
    fieldTicketReconCapture, loadEquipmentLog,
} from '../db/company';
import { showToast } from '../helpers/toast';
import { OutlineButton, SkeletonRows } from './procurementFilters';
import { TAP_API_BASE } from './tapInvoiceViewer';
import {
    ticketFrom, ticketForShift, buildFieldTicketFile, ticketOverflowWarning, peopleFrom, rollUp,
    buildForceReportFile, forceUnclassifiedWarning, rosterShiftOf, rosterClsOf, staffClsOf,
    applyApprovedCard, cardShiftOf, cardClsOf,
} from './billingDailyLabor';
import { TIER3 } from './billingTier3';
import {
    rulesFor, mergeDayFeeds, foldRows, applyPeople, fullJobName, attendanceChecks,
    splitDayChecks, staleRosterShift, contextNotes,
} from './billingCardSource';
import { mergeRateSchedule, stRate, otRate, round2, DEFAULT_SETTINGS } from './billingRates';
import { contractOn, equipRowAs } from './billingContract';
import { equipLogRollup, equipUnmappedWarning } from './billingEquipLog';
import { personDaysFromHeet, runChecks, qaItems, QaTag, QaFindings, HIDDEN_CODES } from './billingQa';
import BillingAiChat from './billingAiChat';
import TicketMap from './billingTicketMap';
import { reconRowsForDay, envStatus } from './billingRecon';
import {
    parseDocs, coverageMap, envelopesByDoc, cellDocStatus, cellKey, splitCell, sortCells, docRows,
    docName, docFileName, cellPayload as buildCellPayload,
    jobSigners, signerSlots, docSigners, signerProgress, MAX_DOC_BYTES,
    partCells, partDocId, partEnvelopes, docStage, cellProgress, PART_SHORT, isSummaryDoc,
    testSigners, testDocId, signersForLabels,
    parseEquipWeeks, equipForDate, equipWeekRows, parseEquipSheet, EQUIP_PHASE,
    docWentOut, normalizeSrc, isSupplementDoc, narrowTicket, onlyIds, weekEnding,
} from './billingTicketDocs';
import { nameEquipment } from './billingEquipNames';
import { dayBilling, invoiceStatus, lumpInvoices } from './billingWeekTracker';
import CoverageGrid, { rampLevel } from './billingCoverageGrid';

// Time Card Approval view for the Billing tab.
//
// The field's work day produces a time sheet, which gets summarized and sent
// out for the client's signature (DocuSign), and until now nobody could see
// which days were still waiting on what. This view is that tracker, organized
// by work day: one line per date — a week strip marking which weekday it was,
// the day's HeavyJob (HEET) headcount and hours, and three deliverable chips
// (the DAY time sheet, the NIGHT time sheet and the Force Report), each with
// a view and an export control. The two crews sign separately, so each shift
// is its own deliverable: its own tracked stage, its own envelope, its own
// exports. HEET carries no shift split of its own — the split comes from the
// HeavyJob card the hours were entered on, correctable per person from the
// preview (the 'tcshift' override rows below), because the card is where the
// hours were TYPED, not necessarily the shift that was WORKED.
// Nothing expands in place: view opens the deliverable in a
// preview modal, and everything you can do to it — advance its stage, send
// it to the client for signature, chase or void that envelope, read its
// history — lives in that modal, so the day list stays one scannable line
// per day. The signature deliverable is Strike's own Field Ticket PDF (built
// by buildTicketPdf below) — the preview mirrors it and the DocuSign
// envelope carries it as-is. The client's Multi Phase Field Ticket workbook
// lives on behind Export Excel and the SharePoint filing, produced by
// patching the day's values into their own template — billingDailyLabor's
// buildFieldTicketFile. The Force Report is the Tier 3
// roll-up of the same day, the way the client's Daily Force Report counts
// heads by classification — and unlike the time sheets it is NOT a signature
// deliverable: it goes to the client as a plain email with the workbook
// attached, to the recipients saved on the job's billing settings.
//
// Storage rides the Billing tab's Forecast_User_Data rows (JSON-in-Notes,
// same pattern as invoices — see the storage docblock in billing.js):
//   fieldName 'tcsheet'  rowId 'tcs_<YYYY-MM-DD>_<Day|Night>'
//     Value = current status, Notes = { d, sh, st, h }
//     h = stage stamps { s: status, u: user, t: 'YYYY-MM-DD HH:mm', n?: note }
//   fieldName 'tcshift'  rowId 'tcshift_<YYYY-MM-DD>'
//     Notes = { d, o: { <staffId>: 'Day'|'Night' } } — the day's manual
//     shift corrections, applied over the roster/HCSS feed everywhere the
//     day is rendered or exported.
// Rows are keyed by date+shift, so re-tracking a removed sheet reuses the
// row, and days tracked under the merged one-sheet layout simply read as
// that day's Day sheet.
//
// The day/night split itself is resolved per person, in this order:
//   1. the day's manual override ('tcshift' above),
//   2. the client's attendance roster (Attendance & Force Report - Master on
//      the job subsite, 02.05 - Daily Force Report — its "Days or Nights"
//      column is the assignment project controls actually maintains),
//   3. the HeavyJob card the hours came in on (readHEETLabor's HCSS join).
//
// HEET (the pre-GL timecard feed) keeps a rolling window of roughly the last
// week, so it supplies the recent day list and the expandable detail. Days
// outside the window fall back to ACTUAL COST (JDE cost history, mapped to
// the same Record_Type grain by readHEETLabor.sql) — flagged In_Actuals in
// the dates feed — so a ticket can still be built long after HEET purges.
// Days neither source has are added by hand with the date picker and show
// without hours.
//
// SharePoint: the job's 02.06 - Timecards folder (readJobTimecards, the same
// listing the Submit-to-AR evidence picker uses) is matched to tracked days
// by the date in each file/folder name, so a day shows at a glance whether
// its signed sheet is actually filed. The preview's "Save to SharePoint"
// files the generated ticket into the same folder, which is what makes the
// round trip work.

export const SHIFTS = ['Day', 'Night'];
// The shift a shift-less action belongs to: days whose feed carries no shift
// at all default to Day.
export const SHIFT_DEFAULT = 'Day';
// Declined mirrors the summary workflow's Kicked Back: the client looked at
// the sheet and refused it, which is a different thing from nobody having
// looked yet, and the difference is the whole point of watching for the
// signature to come back.
export const STAGES = ['Pending', 'Summarized', 'Sent for Signature', 'Signed', 'Declined'];

// Pull a date out of a timecard file or folder name — "WE 8.24.25",
// "08-24-2025", "2025-08-24"… PMs are not consistent. '' when none found.
// Shared with billing.js's Submit-to-AR evidence preselect.
export const tcNameDate = (name) => {
    const s = String(name || '');
    let y; let mo; let d;
    let m = /(20\d{2})[-._/ ](\d{1,2})[-._/ ](\d{1,2})(?!\d)/.exec(s);
    if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
    else {
        m = /(?:^|[^\d])(\d{1,2})[-._/ ](\d{1,2})[-._/ ](20\d{2}|\d{2})(?!\d)/.exec(s);
        if (m) { mo = +m[1]; d = +m[2]; y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; }
    }
    if (!m || mo < 1 || mo > 12 || d < 1 || d > 31) return '';
    const dt = moment.utc({ year: y, month: mo - 1, day: d });
    return dt.isValid() ? dt.format('YYYY-MM-DD') : '';
};
// A file's best date: its own name first, then the deepest dated folder it
// sits in — "Sept 2026/WE9.20.26/9.15.26" is the 15th, not the WE Sunday.
export const tcFileDate = (f) => tcNameDate(f.name)
    || String(f.path || '').split('/').reverse().map(tcNameDate).find(Boolean) || '';

// Forward moves walk the sheet toward Signed; back moves undo a step when a
// sheet was advanced by mistake or a signature gets kicked back. Remove is
// only offered while Pending — once real progress is stamped on the row the
// history should not quietly disappear.
//
// sign:true opens the signature dialog instead of the plain note prompt: the
// move is not just a stamp, it builds the day's ticket and puts a real
// DocuSign envelope in front of the client. The manual "Mark Signed" stays
// alongside it — a sheet signed on paper in the field still has to be
// recordable without an envelope existing.
export const MOVES = {
    Pending: [
        { to: 'Summarized', label: 'Mark Summarized' },
        { to: 'Removed', label: 'Remove', back: true },
    ],
    Summarized: [
        { to: 'Sent for Signature', label: 'Send for Signature', sign: true },
        { to: 'Pending', label: 'Undo', back: true },
    ],
    'Sent for Signature': [
        { to: 'Signed', label: 'Mark Signed' },
        { to: 'Summarized', label: 'Undo', back: true },
    ],
    Signed: [
        { to: 'Sent for Signature', label: 'Reopen', back: true },
    ],
    // A declined sheet gets fixed and goes back out — the new envelope is a
    // new row in the signature history, so the refusal stays on the record.
    Declined: [
        { to: 'Sent for Signature', label: 'Send Again', sign: true },
        { to: 'Summarized', label: 'Undo', back: true },
    ],
};

const BADGES = {
    Pending: { bg: '#fef3c7', fg: '#92400e' },
    Summarized: { bg: '#ede9fe', fg: '#5b21b6' },
    'Sent for Signature': { bg: '#dbeafe', fg: '#1e40af' },
    Signed: { bg: '#dcfce7', fg: '#166534' },
    Declined: { bg: '#fee2e2', fg: '#991b1b' },
};

// ── DocuSign envelope status, as the tracker shows it ──
// DocuSign's own vocabulary ('delivered' means the recipient opened it, not
// that a mail server accepted it) is misleading at a glance, so each status
// gets a plain-language label and the tracker stage it implies. Stage null =
// leave the sheet's stage alone.
export const ENV_STATUS = {
    created: { label: 'Draft — not sent', bg: '#f1f5f9', fg: '#475569', stage: null },
    sent: { label: 'Out for signature', bg: '#dbeafe', fg: '#1e40af', stage: 'Sent for Signature' },
    delivered: { label: 'Opened by client', bg: '#e0f2fe', fg: '#075985', stage: 'Sent for Signature' },
    completed: { label: 'Signed', bg: '#dcfce7', fg: '#166534', stage: 'Signed' },
    declined: { label: 'Declined', bg: '#fee2e2', fg: '#991b1b', stage: 'Declined' },
    voided: { label: 'Voided', bg: '#f1f5f9', fg: '#64748b', stage: null },
    expired: { label: 'Expired', bg: '#fef3c7', fg: '#92400e', stage: null },
    failed: { label: 'Send failed', bg: '#fee2e2', fg: '#991b1b', stage: null },
};
const envInfo = (s) => ENV_STATUS[String(s || '').toLowerCase()]
    || { label: s || 'unknown', bg: '#f1f5f9', fg: '#475569', stage: null };

// History lines describe EVENTS, which are not the same vocabulary as
// statuses — 'filed' happens at completed status but saying "Signed" for it
// would read as a second signature.
const EVENT_LABELS = {
    drafted: 'Draft created',
    sent: 'Sent for signature',
    delivered: 'Opened by client',
    completed: 'Signed by client',
    declined: 'Declined by client',
    voided: 'Voided',
    expired: 'Expired',
    reminded: 'Reminder sent',
    filed: 'Signed copy filed to SharePoint',
    'file-failed': 'Could not file the signed copy',
    // The client's own SharePoint (VoltaGrid's Volta-grid site).
    'filed-client': 'Signed copy filed to the client site',
    'client-file-failed': 'Could not file the client copy',
    // The Invoice Log beside a signed invoice approval on the client site.
    'invoice-log': 'Invoice Log updated on the client site',
    'invoice-log-failed': 'Could not update the Invoice Log',
    'send-failed': 'Send failed',
    // Per-signer events — one envelope, several people.
    signed: 'Signature added',
    'declined-by': 'Signer declined',
    opened: 'Signer opened it',
};
const eventLabel = (e) => EVENT_LABELS[String(e || '').toLowerCase()] || e || 'event';
// Tried the client's site and has not got there since.
const owesClientCopy = (env) => {
    const ev = (env?.events || []).map((v) => String(v.Event).toLowerCase());
    return ev.includes('client-file-failed') && !ev.includes('filed-client');
};
// Statuses DocuSign will not move off on its own.
const envClosed = (s) => ['completed', 'declined', 'voided', 'expired', 'failed']
    .includes(String(s || '').toLowerCase());

const card = {
    backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 16, paddingBottom: 16, paddingLeft: 18, paddingRight: 18, marginBottom: 16,
};

const Badge = ({ status }) => {
    const c = BADGES[status] || BADGES.Pending;
    return (
        <View style={{ backgroundColor: c.bg, borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, alignSelf: 'flex-start' }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: c.fg }}>{status}</Text>
        </View>
    );
};

// Envelope status pill — the live DocuSign state, sitting next to the sheet's
// tracker stage rather than replacing it: the stage is what project controls
// decided, the pill is what the client actually did.
const EnvBadge = ({ status }) => {
    const c = envInfo(status);
    return (
        <View style={{ backgroundColor: c.bg, borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, alignSelf: 'flex-start' }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: c.fg }}>{c.label}</Text>
        </View>
    );
};

// The signature tables store genuine UTC (SYSUTCDATETIME()), unlike the naive
// server-local datetimes elsewhere in this app — so these DO convert to local.
const fmtUtc = (t) => (t ? moment.utc(t).local().format('M/D h:mma') : '');
const fmtUtcLong = (t) => (t ? moment.utc(t).local().format('YYYY-MM-DD HH:mm') : '');

// The envelope's journey as three ticks — Sent → Signed → Filed to
// SharePoint — so pressing Send gives feedback you can see from across the
// room, and the filing step (which otherwise only shows as a history line)
// gets a light of its own. A declined/voided envelope turns its step red
// rather than disappearing.
const SigProgress = ({ env }) => {
    if (!env) return null;
    const st = String(env.Status || '').toLowerCase();
    const events = env.events || [];
    const filedEvent = events.find((v) => String(v.Event).toLowerCase() === 'filed');
    const fileFailed = !filedEvent && events.some((v) => String(v.Event).toLowerCase() === 'file-failed');
    const sent = Boolean(env.Sent_At_Utc) || ['sent', 'delivered', 'completed', 'declined', 'expired'].includes(st);
    const steps = [
        st === 'created' ? { label: 'Draft — not sent yet', state: 'pending' }
            : st === 'failed' ? { label: 'Send failed', state: 'fail' }
            : { label: 'Sent', state: sent ? 'done' : 'pending', at: env.Sent_At_Utc },
        st === 'completed' ? { label: 'Signed', state: 'done', at: env.Completed_At_Utc }
            : st === 'declined' ? { label: 'Declined', state: 'fail', at: env.Declined_At_Utc }
            : st === 'voided' ? { label: 'Voided', state: 'fail', at: env.Voided_At_Utc }
            : { label: st === 'delivered' ? 'Opened by client' : 'Awaiting signature', state: 'pending' },
        (env.Signed_Doc_Path || env.Signed_Doc_Url)
            ? { label: 'Filed to SharePoint', state: 'done', at: filedEvent?.Occurred_At_Utc }
            : fileFailed ? { label: 'Filing to SharePoint failed', state: 'fail' }
            : { label: 'Filed to SharePoint', state: 'pending' },
    ];
    const dot = (state) => (
        <View
            style={{
                width: 15, height: 15, borderRadius: 999, alignItems: 'center', justifyContent: 'center',
                backgroundColor: state === 'done' ? '#16a34a' : state === 'fail' ? '#991b1b' : '#fff',
                borderWidth: state === 'pending' ? 1.5 : 0, borderStyle: 'solid', borderColor: '#cbd5e1', flexShrink: 0,
            }}
        >
            {state !== 'pending' ? (
                <Text style={{ color: '#fff', fontSize: 9, fontWeight: '700', lineHeight: 1 }}>{state === 'done' ? '✓' : '✕'}</Text>
            ) : null}
        </View>
    );
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingTop: 8, paddingBottom: 4 }}>
            {steps.map((s, i) => (
                <View key={s.label} style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {i ? <View style={{ width: 26, height: 1, backgroundColor: '#e2e8f0', marginLeft: 8, marginRight: 8 }} /> : null}
                    {dot(s.state)}
                    <View style={{ marginLeft: 5 }}>
                        <Text style={{ fontSize: 10, fontWeight: '600', whiteSpace: 'nowrap', color: s.state === 'fail' ? '#991b1b' : s.state === 'done' ? '#0f172a' : '#94a3b8' }}>
                            {s.label}
                        </Text>
                        {s.at ? <Text style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtUtc(s.at)}</Text> : null}
                    </View>
                </View>
            ))}
        </View>
    );
};

export const sheetKey = (d, sh) => `tcs_${d}_${sh}`;

// How long before the live HeavyJob cards are asked again after a failed
// read, or 0 to stop. HeavyJob's rate limit names its own wait ("Rate limit
// is exceeded. Try again in 37 seconds."); anything else waits half a minute.
// Three tries, then it waits for Retry or the 10-minute refresh.
export const cardRetryWait = (msg, tries) => {
    if (tries >= 3) return 0;
    const m = /try again in (\d+) seconds?/i.exec(String(msg || ''));
    if (m) return Math.min(Number(m[1]) + 3, 120) * 1000;
    return 30 * 1000;
};

// The grid's note on its feeds when one is still loading or failed — null
// when there is nothing to say. HEET carries every day; the cards carry last
// week and this one, and win there; the envelopes carry what was sent.
export const feedsNote = ({ heet, cards, sigs }) => {
    const parts = [];
    let loading = false;
    if (sigs && sigs.readFailed && !sigs.loading) {
        parts.push(`The DocuSign envelopes didn't load (${/no answer/i.test(sigs.error) ? 'the server did not answer in time' : sigs.error.slice(0, 120)}), so sent and signed summaries and invoices may be missing below.`);
    }
    if (heet.loading) { loading = true; parts.push('Loading the job\'s days from HEET…'); }
    else if (heet.error) parts.push(`The job's days didn't load from HEET (${/no answer|timed? ?out/i.test(heet.error) ? 'it did not answer in time' : heet.error.slice(0, 120)}).`);
    if (cards.loading && !cards.dates.length) { loading = true; parts.push('Loading this week\'s HeavyJob cards…'); }
    else if (cards.error && !cards.loading) {
        const why = /rate limit|429/i.test(cards.error) ? 'HeavyJob\'s rate limit was hit'
            : /no answer/i.test(cards.error) ? 'the server did not answer in time' : cards.error.slice(0, 120);
        const again = cards.retryAt ? `, trying again at ${moment(cards.retryAt).format('h:mm:ss A')}` : '';
        parts.push(`Last week's and this week's HeavyJob cards didn't load (${why}${again}).`);
    }
    if (!parts.length) return null;
    const failed = Boolean((!heet.loading && heet.error) || (!cards.loading && cards.error) || (sigs && !sigs.loading && sigs.readFailed));
    return { text: parts.join(' '), loading: loading && !failed, failed };
};

/**
 * Tracker rows out of the tab's Forecast_User_Data rows. Removed sheets stay
 * stored (their history survives) but do not come back as rows.
 *
 * Exported for the tests: the parse and the serialize below are the contract
 * with the stored blobs, and breaking either strands live tracker rows.
 */
export const parseSheets = (userRows) => {
    const out = [];
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'tcsheet') return;
        let p = null;
        try { p = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
        if (!p || !p.d || !SHIFTS.includes(p.sh)) return;
        const st = STAGES.includes(p.st) ? p.st : p.st === 'Removed' ? 'Removed' : 'Pending';
        if (st === 'Removed') return;
        out.push({ key: sheetKey(p.d, p.sh), d: p.d, sh: p.sh, st, h: Array.isArray(p.h) ? p.h : [] });
    });
    // Newest day first; Day before Night within a day.
    return out.sort((a, b) => b.d.localeCompare(a.d) || SHIFTS.indexOf(a.sh) - SHIFTS.indexOf(b.sh));
};

/**
 * The Notes blob for a sheet, oldest history dropped until it fits the
 * 1024-char Notes column — a truncated JSON blob would lose the whole row.
 */
export const sheetNotes = (sheet) => {
    let h = sheet.h || [];
    let notes = JSON.stringify({ d: sheet.d, sh: sheet.sh, st: sheet.st, h });
    while (h.length > 1 && notes.length > 1000) {
        h = h.slice(1);
        notes = JSON.stringify({ d: sheet.d, sh: sheet.sh, st: sheet.st, h });
    }
    return notes;
};

const fmtDay = (d) => moment(d, 'YYYY-MM-DD').format('ddd M/D/YY');
const fmtShort = (d) => moment(d, 'YYYY-MM-DD').format('M/D/YY');

// ── Packet page dress ──
// The palette is the logo's: Strike blue carries the accents, the red appears
// once as the tip of the header rule. Shared by the ticket page and the
// summary cover so the two read as one document.
const PDF_INK = [23, 32, 42];
const PDF_MUTE = [120, 128, 138];
const PDF_BLUE = [21, 82, 150];
const PDF_RED = [206, 32, 40];

/**
 * The header every packet page opens with: both marks over the brand rule,
 * the title with its shift badge, a right-hand date or week line, and the job
 * line under it.
 *
 * This is a port of drawHeader in localserver/scripts/ticket-packet-*.js and
 * has to stay identical to it: those scripts and this tab build the same
 * client-facing packet, and a client comparing the two must not be able to
 * tell which one made the PDF in their hand.
 *
 * The longer titles ("… WEEK SUMMARY") plus a badge can run into the right
 * text, so the title steps down in half points until the row fits.
 */
const drawPacketHeader = (doc, {
    strikeLogo, clientLogo, title, badge, rightText, subLeft, subRight, M = 40,
}) => {
    const W = doc.internal.pageSize.getWidth();
    const usable = W - 2 * M;
    if (strikeLogo) doc.addImage(strikeLogo, 'JPEG', M, M - 2, 100, 23);
    if (clientLogo) doc.addImage(clientLogo, 'JPEG', W - M - 112, M + 2, 112, 16);
    doc.setFillColor(...PDF_BLUE);
    doc.rect(M, M + 30, usable - 46, 2.2, 'F');
    doc.setFillColor(...PDF_RED);
    doc.rect(W - M - 40, M + 30, 40, 2.2, 'F');
    const y = M + 52;
    doc.setFont('helvetica', 'bold').setFontSize(7.5);
    const bw = badge ? doc.getTextWidth(badge) + badge.length + 14 : 0;
    doc.setFontSize(9.5);
    const roomForTitle = usable - doc.getTextWidth(rightText || '') - 20 - 12 - bw;
    let titleSize = 16;
    doc.setFontSize(titleSize);
    while (titleSize > 11 && doc.getTextWidth(title) + title.length * 1.5 > roomForTitle) {
        titleSize -= 0.5;
        doc.setFontSize(titleSize);
    }
    doc.setTextColor(...PDF_INK);
    doc.text(title, M, y, { charSpace: 1.5 });
    const titleW = doc.getTextWidth(title) + title.length * 1.5;
    if (badge) {
        doc.setFontSize(7.5);
        const bx = M + titleW + 12;
        doc.setFillColor(...PDF_BLUE);
        doc.roundedRect(bx, y - 11, bw, 15, 7.5, 7.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(badge, bx + 7, y - 0.5, { charSpace: 1 });
    }
    doc.setFont('helvetica', 'bold').setFontSize(9.5).setTextColor(...PDF_INK);
    if (rightText) doc.text(rightText, W - M, y, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...PDF_MUTE);
    // The job line and the right-hand note share a line when they fit; when
    // they'd collide (a full job name beside a week's "Days included") the
    // job line wraps at the page width and the note drops to its own line —
    // the scripts' drawHeader rule. Never more than two job lines.
    const sub = String(subLeft || '');
    const right = String(subRight || '');
    const gap = 16;
    if (!right || doc.getTextWidth(sub) + doc.getTextWidth(right) + gap <= usable) {
        doc.text(sub, M, y + 14);
        if (right) doc.text(right, W - M, y + 14, { align: 'right' });
        return y + 34;
    }
    const lines = doc.splitTextToSize(sub, usable).slice(0, 2);
    lines.forEach((ln, i) => doc.text(ln, M, y + 14 + i * 10));
    const ry = y + 14 + lines.length * 10;
    doc.text(right, W - M, ry, { align: 'right' });
    return ry + 20;
};
const hrs = (n) => (pNum(n) ? pNum(n).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '');
const h2 = (n) => (pNum(n) ? pNum(n).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '');
// Row summary is a glance, not a figure: 101 people and 1,075 hours reads
// faster than "101 staff / 1,075.0 hrs in HEET" and says the same thing.
const h0 = (n) => (pNum(n) ? Math.round(pNum(n)).toLocaleString('en-US') : '0');

// How many days' HEET pulls run at once (cellTickets). Each is a heavy SQL
// read; more than a couple in flight and they queue on the server until the
// 180s request timeout kills them.
const DAY_LOAD_CONCURRENCY = 2;

// The ticket preview mirrors the exported Field Ticket PDF — the signature
// deliverable the DocuSign envelope carries and the Export PDF button saves.
// Same dress as the jsPDF draw: dark header band with rotated code columns,
// hairline row rules, zebra, a strong rule over the totals foot, the shift
// summary band, the weekly-payroll disclaimer and the signature block. The
// preview adds one control the paper doesn't have: the Shift column's D/N
// toggle, which moves a person between the day and night sheets. The Excel
// export still patches the client's own Multi Phase Field Ticket workbook —
// that format lives on unchanged behind Export Excel. The Force Report
// preview mirrors the client's "VoltaGrid MP Report" sheet the same way.
const TK = {
    ink: '#17202a', mute: '#78808a', blue: '#155296', red: '#ce2028',
    line: '#dfe3e8', fill: '#f6f7f9', zebra: '#fafbfc',
};
// Screen column widths for the PDF mirror's matrix.
const TKW = {
    name: 150, id: 52, cls: 132, shift: 62, lead: 118,
    flag: 26, code: 46, tot: 52, bill: 82,
};
const TKW_LEFT = TKW.name + TKW.id + TKW.cls + TKW.shift + TKW.lead + TKW.flag * 3;

// ── Daily Force Report mirror (the client's VoltaGrid MP Report sheet) ──
// Fill colors read out of the client's own styles.xml: the green header
// bands, the gold TOTAL WORKING column and TOTAL rows.
const FR = { green: '#8DC63F', gold: '#E5D15C', line: '#7f7f7f' };
const FR_COLS = { craft: 190, n: 36, tot: 46 };  // craft label, count cols, TOTAL WORKING
const FR_BAND_W = FR_COLS.craft + FR_COLS.n * 3 + FR_COLS.tot + FR_COLS.n * 3;
// Vertical column headings, bottom-up like the sheet's 90° rotation.
const frVert = (text, w, extra = {}) => (
    <View
        key={text}
        style={{
            width: w, height: 96, borderColor: FR.line, borderRightWidth: 1, borderBottomWidth: 1,
            alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 4, flexShrink: 0,
            backgroundColor: extra.fill || '#fff',
        }}
    >
        <Text style={{
            fontSize: 9, fontWeight: '700', color: extra.color || '#000', textAlign: 'center',
            writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'pre-line',
        }}
        >
            {text}
        </Text>
    </View>
);
const frCell = (v, w, extra = {}) => (
    <Text style={{
        width: w, fontSize: 11, color: extra.color || '#000', flexShrink: 0,
        paddingTop: 2, paddingBottom: 2, borderColor: FR.line, borderRightWidth: 1, borderBottomWidth: 1,
        backgroundColor: extra.fill || '#fff', fontWeight: extra.bold ? '700' : '400',
        paddingLeft: extra.left ? 6 : 0, textAlign: extra.left ? 'left' : 'center',
    }}
    >
        {v}
    </Text>
);
// The sheet's number format prints 0 as "-" — the mirror does the same.
// The Sunday a work date bills into — the week the reviewed time card covers.
const weekEndFor = (d) => {
    if (!d) return '';
    const m = moment(d, 'YYYY-MM-DD');
    if (!m.isValid()) return '';
    return (m.day() === 0 ? m : m.clone().day(7)).format('YYYY-MM-DD');
};

const frNum = (n) => (n ? String(n) : '-');

const BillingTimecardApproval = ({
    jobKey, jobLabel, ratemap, userRows, saveRows, versionLocked, userName,
    userEmail, settings, staffCls: tier4Cls, staffClsFile = null,
    // { kind, cells, nonce } — days picked on the Reconciliation view, to
    // open on the Ticket Map as a Draft (TicketMap's openHandoff).
    prefill = null, onPrefillUsed,
    // (unit, description) => { name }: the rate sheet item a unit prints
    // under on tickets and week summaries (billingEquipNames equipNamer).
    equipName = null,
    // The Billing tab's summaries and line snapshots (parseBillingRows), for
    // the day list's INVOICE column.
    invoices = [], billed = null,
}) => {
    const [movePrompt, setMovePrompt] = useState(null); // { sheet, mv } awaiting its note
    const [moveNote, setMoveNote] = useState('');
    const [gridSel, setGridSel] = useState(null);
    // The open preview: { d, kind } where kind is a shift name or 'Force'.
    // One modal serves all three chips — it is the only place a deliverable
    // is looked at or acted on.
    const [preview, setPreview] = useState(null);
    // Force preview: when the day's report is already filed in SharePoint the
    // modal shows THAT document; this flips it back to the generated render.
    const [showGenForce, setShowGenForce] = useState(false);
    const [savingTicket, setSavingTicket] = useState(false);
    // { d, sh, sheet, mv } for the shift whose signature request is being
    // composed, plus the editable request itself.
    const [sendPrompt, setSendPrompt] = useState(null);
    const [sendForm, setSendForm] = useState(null);
    const [sending, setSending] = useState(false);

    // Job switch: collapse everything from the last job.
    useEffect(() => {
        setPreview(null); setGridSel(null);
        setSendPrompt(null); setSendForm(null);
    }, [jobKey]);

    // SharePoint timecard docs for the job (same folder the Submit-to-AR
    // evidence picker reads: 02 - Project Controls / 02.06 - Timecards).
    const [spDocs, setSpDocs] = useState({ job: null, loading: false, data: null, at: '' });
    const syncDocs = (job) => {
        // Background refreshes keep the last result on screen instead of
        // blanking the per-day doc chips while the fetch runs.
        setSpDocs((s) => (s.job === job ? { ...s, loading: true } : { job, loading: true, data: null, at: '' }));
        // No answer (postRequest's undefined for a timeout) or an error body
        // is a failed sync, not a job without a Timecards folder.
        const failed = () => setSpDocs((s) => (s.job === job
            ? { job, loading: false, data: s.data ? { ...s.data, error: true } : { error: true }, at: s.at } : s));
        Promise.resolve(loadJobTimecards({ job: String(job) }))
            .then((res) => (!res || res.error ? failed()
                : setSpDocs((s) => (s.job === job ? { job, loading: false, data: res, at: moment().format('h:mm A') } : s))))
            .catch(failed);
    };
    // Sync on job switch, then auto-resync every 5 minutes so sheets filed to
    // SharePoint show up without anyone pressing the button.
    useEffect(() => {
        if (!jobKey) return undefined;
        syncDocs(jobKey);
        const t = setInterval(() => syncDocs(jobKey), 5 * 60 * 1000);
        return () => clearInterval(t);
    }, [jobKey]);
    const docsByDate = useMemo(() => {
        const m = {};
        (spDocs.data?.files || []).forEach((f) => {
            const d = tcFileDate(f);
            if (d) (m[d] = m[d] || []).push(f);
        });
        return m;
    }, [spDocs.data]);

    // The day's Force Report as filed in SharePoint, when there is one — the
    // signed copy the webhook filed, or a scan project controls dropped in.
    // Signed beats unsigned; newer beats older.
    const forceDocFor = (d) => {
        const docs = (docsByDate[d] || []).filter((f) => /force\s*report/i.test(String(f.name || '')));
        if (!docs.length) return null;
        return docs.sort((a, b) => {
            const sa = /signed/i.test(a.name) ? 1 : 0;
            const sb = /signed/i.test(b.name) ? 1 : 0;
            if (sa !== sb) return sb - sa;
            return String(b.lastModified || '').localeCompare(String(a.lastModified || ''));
        })[0];
    };
    // Streamed through the jobTimecardFile proxy (the browser can't call
    // Graph itself); Office formats come back rendered to PDF.
    const tcFileUrl = (f) => {
        const raw = `${TAP_API_BASE}api/jobTimecardFile?driveId=${encodeURIComponent(f.driveId)}&id=${encodeURIComponent(f.id)}`;
        return /\.(pdf|jpe?g|png)$/i.test(String(f.name || '')) ? raw : `${raw}&as=pdf`;
    };

    // HEET dates for the job — the recent-day list and the staff/hours
    // summaries on days still inside HEET's rolling window.
    // A call that fails is kept as `error`, not read as a job with no days:
    // readHEETLabor runs past Azure's 230 s limit on the big jobs (10/23/26),
    // which postRequest hands back as undefined, and a SQL error comes back as
    // { error } — both used to leave the grid's hours empty with no word why.
    //
    // Only the newest call of each feed writes (feedSeq): a stale answer — an
    // older call, or the first of the two calls React's StrictMode makes in
    // development — once overwrote a good list with its own failure.
    const feedSeq = useRef({ heet: 0, cards: 0, sigs: 0 });
    const [heet, setHeet] = useState({ job: null, loading: false, dates: [], validation: [], jobInfo: null, error: null });
    const loadHeet = (job) => {
        const seq = ++feedSeq.current.heet;
        const mine = () => seq === feedSeq.current.heet;
        setHeet((h) => (h.job === job ? { ...h, loading: true, error: null } : { job, loading: true, dates: [], validation: [], jobInfo: null, error: null }));
        // 'list': the dates list, header and validation only — no day rows.
        Promise.resolve(loadHEETLabor({ id: String(job), parts: 'list' }))
            .then((res) => mine() && setHeet((h) => (h.job !== job ? h : !res || res.error
                ? { ...h, loading: false, error: res ? String(res.error) : 'no answer from the server in time' }
                : { job, loading: false, dates: res.dates || [], validation: res.validation || [], jobInfo: res.job || null, error: null })))
            .catch((e) => mine() && setHeet((h) => (h.job === job ? { ...h, loading: false, error: String(e?.message || e) } : h)));
    };
    useEffect(() => { if (jobKey) loadHeet(jobKey); }, [jobKey]);
    // The live HeavyJob cards for the open weeks (last week and this one) —
    // the tickets' basis. HEET's list above still carries the older days and
    // the per-day compare; this is what the day rows count from where it has
    // the day. It also warms the server's card cache, so opening a day after
    // it costs one header read. Refreshed every 10 minutes while the tab is up.
    //
    // A failed read tries again on its own, a few times: HeavyJob's rate limit
    // ("Try again in 37 seconds", shared with every other caller of the key)
    // after the wait it names, anything else after half a minute.
    const [hjDates, setHjDates] = useState({ job: null, loading: false, dates: [], cards: [], error: null, at: null, retryAt: null, from: null });
    const hjRetry = useRef({ t: null, n: 0 });
    const loadHjDates = (job, { manual = false } = {}) => {
        const end = moment().format('YYYY-MM-DD');
        const start = moment().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
        clearTimeout(hjRetry.current.t);
        if (manual) hjRetry.current.n = 0;
        const seq = ++feedSeq.current.cards;
        const mine = () => seq === feedSeq.current.cards;
        setHjDates((h) => (h.job === job ? { ...h, loading: true, retryAt: null } : { job, loading: true, dates: [], cards: [], error: null, at: null, retryAt: null, from: start }));
        const failed = (msg) => {
            if (!mine()) return;
            const wait = cardRetryWait(msg, hjRetry.current.n);
            if (wait) {
                hjRetry.current.n += 1;
                hjRetry.current.t = setTimeout(() => loadHjDates(job), wait);
            }
            setHjDates((h) => (h.job === job ? { ...h, loading: false, error: msg, retryAt: wait ? Date.now() + wait : null } : h));
        };
        Promise.resolve().then(() => loadHeavyJobCards({ job: String(job), start, end }))
            .then((res) => {
                if (!mine()) return;
                // Not configured on this service is a deliberate HEET-only setup, not a failure.
                if (res && res.configured === false) {
                    setHjDates((h) => (h.job === job ? { ...h, loading: false, error: null, from: start } : h));
                    return;
                }
                if (!res || res.error) { failed(String(res?.error || 'no answer from the server in time')); return; }
                hjRetry.current.n = 0;
                setHjDates((h) => (h.job !== job ? h
                    : { job, loading: false, dates: res.dates || [], cards: res.cards || [], error: null, at: res.fetchedAt || null, retryAt: null, from: start }));
            })
            .catch((e) => failed(String(e?.message || e)));
    };
    useEffect(() => {
        if (!jobKey) return undefined;
        hjRetry.current.n = 0;
        loadHjDates(jobKey);
        const t = setInterval(() => loadHjDates(jobKey), 10 * 60 * 1000);
        return () => { clearInterval(t); clearTimeout(hjRetry.current.t); };
    }, [jobKey]);
    const heetBy = useMemo(() => {
        const m = {};
        (heet.dates || []).forEach((r) => { m[r.Work_Date] = r; });
        // A day the live cards have: counts off the cards (API_*), and a day
        // HEET's list hasn't reached yet still gets a row that builds.
        (hjDates.dates || []).forEach((x) => {
            if (!(Number(x.hours) > 0)) return;
            const api = { API_Staff: x.people, API_Hours: x.hours, API_Cards: x.cards, API_Approved: x.approved, API_Modified: x.lastModified };
            m[x.Work_Date] = m[x.Work_Date]
                ? { ...m[x.Work_Date], ...api }
                : { Work_Date: x.Work_Date, In_HEET: 0, In_Actuals: 0, In_HJ: 1, Staff: x.people, Hours: x.hours, HJ_Staff: x.people, HJ_Hours: x.hours, ...api };
        });
        return m;
    }, [heet.dates, hjDates.dates]);
    // Days HEET has purged still arrive in the dates list off the HeavyJob
    // raw feed, flagged In_HEET = 0. When actual cost has posted the day
    // (In_Actuals = 1) the detail comes back from the GL; otherwise the raw
    // feed itself answers (In_HJ = 1) — HeavyJob lands a day ahead of HEET
    // and keeps every card since job start, so yesterday and purged days
    // both still build, as unapproved straight-time.
    const inHeet = (d) => Boolean(heetBy[d] && heetBy[d].In_HEET !== 0);
    const hasDetail = (d) => Boolean(heetBy[d] && (heetBy[d].In_HEET !== 0 || heetBy[d].In_Actuals === 1 || heetBy[d].In_HJ === 1));
    // A day no feed has answered for: HEET's list is loading or failed, and
    // the day is outside the cards' weeks or the cards have not answered
    // either. Its empty hours cell is "not known", not "no time".
    const hoursUnknown = (d) => {
        if (heetBy[d]) return false;
        if (!heet.loading && !heet.error) return false;
        if (heet.dates.length) return false; // an earlier answer still stands
        const cardsAnswered = Boolean(hjDates.from && d >= hjDates.from && !hjDates.error && hjDates.at);
        return !cardsAnswered;
    };
    const retryFeeds = () => {
        if (!jobKey) return;
        if (heet.error && !heet.loading) loadHeet(jobKey);
        if (hjDates.error && !hjDates.loading) loadHjDates(jobKey, { manual: true });
        if (sigs.readFailed && !sigs.loading) loadSigs(jobKey);
    };
    // The two live sources disagreeing on a day is worth a flag either way:
    // HEET under HeavyJob is the morning's partial pull still catching up,
    // HEET over HeavyJob has been a double load (one card, two HEET GUIDs).
    const dayMismatch = (r) => {
        if (!r || r.In_HEET !== 1 || r.In_HJ !== 1) return 0;
        const diff = (Number(r.HEET_Hours) || 0) - (Number(r.HJ_Hours) || 0);
        return Math.abs(diff) > 0.5 ? diff : 0;
    };

    // ── The day/night split's two stored sources ──
    // The client's attendance roster: staff id -> Day/Night, refreshed on
    // job switch. Its absence is normal (young job, no roster filed yet).
    //
    // A read that FAILS is not the same thing: the request helper swallows
    // errors and hands back nothing, and treating that as "no roster" once put
    // a whole day crew on nights — every shift fell through to the HeavyJob
    // card and a client packet was built with one person on it. So a failure
    // is kept as `error`, and nothing builds a ticket on top of it
    // (sourcesReady below).
    const [roster, setRoster] = useState({ job: null, loading: false, data: null, error: null });
    // What the long-running builders read. A packet takes minutes; the closures
    // it started with are a snapshot of one render, and the roster or the
    // reviewed card landing mid-build must still be seen. Filled in below.
    const live = useRef({ jobKey, roster: { loading: true }, cards: {}, weeks: [], rosterShift: {}, shiftOv: {}, staffCls: {} });
    const rosterFetch = useRef(null);
    const fetchRoster = (force = false) => {
        if (!jobKey) return Promise.resolve(null);
        const k = String(jobKey);
        if (force || rosterFetch.current?.k !== k) {
            setRoster({ job: jobKey, loading: true, data: null, error: null });
            const settle = (v) => {
                if (live.current.jobKey === jobKey) setRoster({ job: jobKey, loading: false, ...v });
                return v;
            };
            rosterFetch.current = {
                k,
                // Inside a then, so a throw from the call itself settles as an
                // error too instead of leaving `loading` set for good.
                p: Promise.resolve().then(() => loadAttendanceRoster({ job: k }))
                    .then((res) => settle(res ? { data: res, error: null } : { data: null, error: 'no response from the server' }))
                    .catch((e) => settle({ data: null, error: String(e?.message || e) })),
            };
        }
        return rosterFetch.current.p;
    };
    useEffect(() => { fetchRoster(true); }, [jobKey]);
    const rosterShift = useMemo(() => rosterShiftOf(roster.data, jobKey), [roster.data, jobKey]);

    // ── The job's standing ticket rules (billingCardSource TICKET_RULES) ──
    // Phase folds, per-person name/class overrides, whose master supplies the
    // billing classification, and the sister job the crew also cards to.
    const rules = useMemo(() => rulesFor(jobKey), [jobKey]);
    const peopleCls = useMemo(() => Object.fromEntries(Object.entries(rules.people || {})
        .filter(([, o]) => o.cls).map(([id, o]) => [String(id), o.cls])), [rules]);
    // 54026071 bills under 54026070's rate-sheet names — read that job's
    // master too. A failed read here only costs the pin (71's own master
    // still classifies), so it never blocks a build the way the roster does.
    const [clsRoster, setClsRoster] = useState({ job: null, data: null, error: null });
    useEffect(() => {
        const cj = rules.clsJob;
        if (!cj) { setClsRoster({ job: null, data: null, error: null }); return undefined; }
        let dead = false;
        setClsRoster({ job: cj, data: null, error: null, loading: true });
        loadAttendanceRoster({ job: String(cj) })
            .then((res) => { if (!dead) setClsRoster({ job: cj, data: res || null, error: res ? null : 'no response from the server' }); })
            .catch((e) => { if (!dead) setClsRoster({ job: cj, data: null, error: String(e?.message || e) }); });
        return () => { dead = true; };
    }, [rules]);
    const clsJobCls = useMemo(
        () => (clsRoster.data?.found ? rosterClsOf(clsRoster.data, rules.clsJob) : {}),
        [clsRoster.data, rules],
    );
    // The job's full name: JDE and HeavyJob cut it at 30 characters, the job
    // subsite's title doesn't.
    const jobTitle = (info) => fullJobName(info?.job?.Job_Description || heet.jobInfo?.Job_Description, roster.data?.site?.name, jobKey) || jobLabel || '';
    const jobClient = (info) => String(info?.job?.Client_Name || heet.jobInfo?.Client_Name || '').trim();

    // ── Project controls' reviewed time card ──
    // The week project controls has signed off: their hours, shift and
    // classification beat the HeavyJob cards, the attendance roster and the
    // field workbook, because that sheet is what the client is invoiced from.
    // One fetch per week of the loaded feed; a week with no reviewed card yet
    // simply has no entry and that week still bills off HeavyJob.
    //
    // Looked up one week at a time, newest first: each lookup walks SharePoint
    // through Graph, and a month of them at once — beside the roster read and
    // the folder sync — is how Graph starts throttling all three.
    const [cards, setCards] = useState({});
    const cardFetches = useRef({});
    useEffect(() => { setCards({}); cardFetches.current = {}; }, [jobKey]);
    const weeks = useMemo(() => [...new Set(Object.keys(heetBy)
        .map((d) => weekEndFor(d)).filter(Boolean))].sort(), [heetBy]);
    const fetchCard = (we, force = false) => {
        const k = `${jobKey}|${we}`;
        if (force) delete cardFetches.current[k];
        if (!cardFetches.current[k]) {
            setCards((c) => ({ ...c, [we]: { loading: true } }));
            const settle = (v) => {
                if (live.current.jobKey === jobKey) setCards((c) => ({ ...c, [we]: v }));
                return v;
            };
            cardFetches.current[k] = Promise.resolve()
                .then(() => loadApprovedTimeCard({ job: String(jobKey), weekEnd: we }))
                .then((res) => settle({ loading: false, data: res?.found ? res : null, error: null }))
                .catch((e) => settle({ loading: false, data: null, error: String(e?.message || e) }));
        }
        return cardFetches.current[k];
    };
    useEffect(() => {
        if (!jobKey || !weeks.length) return undefined;
        let dead = false;
        (async () => {
            for (const we of [...weeks].reverse()) {
                if (dead) return;
                await fetchCard(we); // eslint-disable-line no-await-in-loop
            }
        })();
        return () => { dead = true; };
    }, [jobKey, weeks]);
    const cardFor = (d) => live.current.cards[weekEndFor(d)]?.data || null;

    // The classification a ticket prints: the reviewed card first, then the
    // roster's rate-sheet column, then the field workbook's Tier 4 (the
    // tier4Cls prop) — newest approved source wins.
    // `src` is a ticket document's source choices (billingTicketDocs
    // SRC_FIELDS): 'workbook' bills off the Staff List alone.
    // Under the rules a person override (Jance) beats everything, and a
    // class job's master (70's, for 71) beats the job's own roster column.
    const clsFor = (d, src) => {
        if (normalizeSrc(src).cls === 'workbook') return { ...(live.current.tier4Cls || {}) };
        return { ...staffClsOf(live.current.staffCls, cardClsOf(cardFor(d))), ...live.current.peopleCls };
    };
    // Where one person's printed classification came from, for the preview,
    // the assistant and the packet manifest.
    const clsSourceOf = (d, id) => {
        const k = String(id);
        if (live.current.peopleCls?.[k]) return 'job rule';
        if (cardClsOf(cardFor(d))[k]) return 'reviewed time card';
        if (live.current.clsJobCls?.[k]) return `${rules.clsJob} attendance master`;
        if (rosterClsOf(live.current.roster?.data, jobKey)[k]) return 'attendance master';
        if (live.current.tier4Cls?.[k]) return 'Staff List workbook';
        return 'pay class map';
    };
    const staffCls = useMemo(
        () => ({ ...staffClsOf(tier4Cls, { ...rosterClsOf(roster.data, jobKey), ...clsJobCls }), ...peopleCls }),
        [tier4Cls, roster.data, jobKey, clsJobCls, peopleCls],
    );

    // Manual per-day corrections, stored one row per day ('tcshift' rows).
    const shiftOv = useMemo(() => {
        const m = {};
        (userRows || []).forEach((x) => {
            if (x?.Forecast_Field_Name !== 'tcshift') return;
            try {
                const p = x.Notes ? JSON.parse(x.Notes) : null;
                if (p && p.d && p.o) m[p.d] = p.o;
            } catch (e) { /* malformed blob — ignore */ }
        });
        return m;
    }, [userRows]);
    // Toggle one person's shift for one day. Setting them to what the roster
    // already says drops the override instead of storing a redundant one —
    // the Notes blob is capped, so it only carries genuine exceptions.
    const saveShift = (d, staffId, sh) => {
        if (versionLocked) return;
        const id = String(staffId);
        const o = { ...(shiftOv[d] || {}) };
        if (rosterShift[id] === sh) delete o[id];
        else o[id] = sh;
        saveRows([{
            rowId: `tcshift_${d}`, fieldName: 'tcshift',
            value: String(Object.keys(o).length),
            notes: JSON.stringify({ d, o }),
        }]);
    };
    // Per-day record of which feed answered and what the cards said (see
    // billingCardSource mergeDayFeeds) — a ref, because the builders read it
    // mid-build, the same way they read `live`.
    const dayFeeds = useRef({});
    useEffect(() => { dayFeeds.current = {}; }, [jobKey]);
    // One day's rows: the live HeavyJob cards (and the sister job's, for the
    // split-day check) beside HEET, merged. HEET is only waited on so long
    // when the cards already have the day — it's the comparison and the job
    // header, not the hours — so a slow actual-cost day doesn't hold a ticket.
    const HEET_WAIT_MS = 20000;
    const loadDayFeeds = (d) => {
        const job = String(jobKey);
        const sister = rules.sister ? String(rules.sister) : null;
        const hjP = Promise.resolve().then(() => loadHeavyJobCards({ jobs: sister ? [job, sister] : [job], start: d, end: d }))
            .catch((e) => ({ error: String(e?.message || e) }));
        const heetP = Promise.resolve().then(() => loadHEETLabor({ id: job, date: d, parts: 'day' }))
            .then((res) => res || { error: 'no response from the server' })
            .catch((e) => ({ error: String(e?.message || e) }));
        return hjP.then(async (hj) => {
            const hasCards = Boolean(hj && !hj.error && (hj.data || []).some((r) => String(r.Job) === job && Number(r.Hours) > 0));
            const heetRes = hasCards
                ? await Promise.race([heetP, new Promise((res) => { setTimeout(() => res({ error: `HEET still loading after ${HEET_WAIT_MS / 1000} s — compare skipped` }), HEET_WAIT_MS); })])
                : await heetP;
            const mine = hj && !hj.error ? { ...hj, data: (hj.data || []).filter((r) => String(r.Job) === job), cards: (hj.cards || []).filter((c) => String(c.job) === job) } : hj;
            const m = mergeDayFeeds({ date: d, heet: heetRes, hj: mine });
            if (m.error) throw new Error(m.error);
            const sisterRows = sister && hj && !hj.error ? (hj.data || []).filter((r) => String(r.Job) === sister) : [];
            const feed = {
                ...m.feed,
                sister,
                sisterIds: [...new Set(sisterRows.filter((r) => r.Staff_ID && Number(r.Hours) > 0).map((r) => String(r.Staff_ID)))],
                splitDays: sister ? splitDayChecks(m.rows, sisterRows) : [],
            };
            dayFeeds.current[d] = feed;
            return { loading: false, rows: m.rows, job: m.job || heet.jobInfo || null, feed };
        });
    };

    // The merged override map ticketFrom applies for a day: the attendance
    // roster, then the reviewed time card, then the day's manual corrections.
    // A document's source choices can leave the card's shift out ('roster')
    // or everything but the HeavyJob card ('hj'); the day's manual
    // corrections win under every setting.
    //
    // Under 'auto' the day's HeavyJob cards join in, but only where they mean
    // something (billingCardSource cardShiftTrust): per-foreman cards place
    // their crew above the roster, whose Days/Nights column is today's
    // assignment rather than the date's. A roll-up card (the office keying
    // both crews onto one shift-2 card, as 54026070 did through 9/20) says
    // nothing — anyone the roster doesn't place then defaults to Day, the
    // way the packet scripts do, rather than riding the roll-up's shift 2
    // onto the night sheet.
    const overridesFor = (d, src) => {
        const s = normalizeSrc(src);
        const L = live.current;
        const manual = L.shiftOv[d] || {};
        if (s.shift === 'hj') return { ...manual };
        if (s.shift === 'roster') return { ...L.rosterShift, ...manual };
        const feed = dayFeeds.current[d];
        const onCards = feed?.basis === 'cards';
        const trusted = onCards && feed.trust?.trusted ? feed.cardShift || {} : {};
        const untrusted = onCards && !feed.trust?.trusted
            ? Object.fromEntries(Object.keys(feed.cardShift || {}).map((id) => [id, 'Day'])) : {};
        const prior = priorShiftFor(d);
        return { ...untrusted, ...L.rosterShift, ...(prior ? cardShiftOf(prior.card) : {}), ...trusted, ...cardShiftOf(cardFor(d)), ...manual };
    };
    // A roll-up-card day with no reviewed card of its own, rebuilt after the
    // attendance master has moved on: the master's Days/Nights column is its
    // state at its last save, not the date's (54026070's was re-saved 9/21
    // with everyone on Days; on 9/18 39 people worked nights). The latest
    // reviewed card before the date is then better truth for who was on
    // nights — it's what project controls signed off a week earlier.
    const priorShiftFor = (d) => {
        const L = live.current;
        const feed = dayFeeds.current[d];
        if (!feed || feed.basis !== 'cards' || feed.trust?.trusted || cardFor(d)) return null;
        const we = weekEndFor(d);
        const saved = String(L.roster?.data?.file?.lastModified || '').slice(0, 10);
        if (!saved || saved <= we) return null;
        const prev = Object.keys(L.cards || {}).filter((w) => w < we && L.cards[w]?.data).sort().pop();
        if (!prev) return null;
        const card = L.cards[prev].data;
        return Object.keys(cardShiftOf(card)).length ? { we: prev, card, saved } : null;
    };
    // Where one person's shift came from under 'auto', same order as above.
    const shiftSourceFor = (d, id) => {
        const k = String(id);
        const L = live.current;
        if (L.shiftOv[d]?.[k]) return 'manual correction';
        if (cardShiftOf(cardFor(d))[k]) return 'reviewed time card';
        const feed = dayFeeds.current[d];
        if (feed?.basis === 'cards' && feed.trust?.trusted && feed.cardShift?.[k]) return 'foreman card';
        const prior = priorShiftFor(d);
        if (prior && cardShiftOf(prior.card)[k]) return `WE ${moment(prior.we, 'YYYY-MM-DD').format('M/D')} reviewed card`;
        if (L.rosterShift[k]) return 'attendance master';
        if (feed?.basis === 'cards' && !feed.trust?.trusted) return 'default (roll-up card)';
        return 'HeavyJob card';
    };
    // Whether the week's reviewed card has hours for this date — the same
    // test applyApprovedCard makes before it touches a day.
    const cardCovers = (card, d) => Boolean(card && (card.labor || []).some((p) => Number(p.days?.[d]) > 0.005));
    // One day's HEET rows reconciled to the reviewed card. Idempotent, so rows
    // that already came through fetchDay pass straight back out. A document
    // set to the reviewed card ONLY refuses a day the card does not cover,
    // out loud, rather than falling back to HeavyJob; one set to HeavyJob
    // only never looks at the card.
    // Then the job rules: per-person names (Jance) and the phase folds
    // (100105 → 100101), after the card so its trims still see 100105.
    const rowsFor = (d, rows, src) => {
        const s = normalizeSrc(src);
        const R = live.current.rules || {};
        const finish = (list) => foldRows(applyPeople(list, R.people), R.fold).rows;
        if (s.labor === 'hj') return finish(rows || []);
        const card = cardFor(d);
        if (s.labor === 'card' && !cardCovers(card, d)) {
            throw new Error(`no reviewed time card covers ${moment(d, 'YYYY-MM-DD').format('M/D')} and this ticket is set to the reviewed card only`);
        }
        return finish(applyApprovedCard(rows || [], card, d));
    };
    // This render's data, for whatever is still running from an earlier one.
    live.current = { jobKey, roster, cards, weeks, rosterShift, shiftOv, staffCls, tier4Cls, peopleCls, clsJobCls, rules };

    // What one day's ticket will be built from under a document's source
    // choices — named source by source, for the Ticket Map's Sources panel
    // and the packet manifest. Reads the live refs, so a card that landed
    // mid-build is seen.
    const daySources = (d, src) => {
        const s = normalizeSrc(src);
        const L = live.current;
        const r = heetBy[d];
        const got = dayFeeds.current[d];
        const feed = got?.basis === 'cards' || (!got && r?.API_Staff != null) ? 'HeavyJob cards (live)'
            : !r ? 'none' : r.In_HEET !== 0 ? 'HEET' : r.In_Actuals === 1 ? 'actuals' : r.In_HJ === 1 ? 'HeavyJob' : 'none';
        const we = weekEndFor(d);
        const c = L.cards[we];
        const card = c?.data || null;
        const covers = cardCovers(card, d);
        const file = card ? { name: card.file?.name || '', modifiedBy: card.file?.modifiedBy || '', lastModified: card.file?.lastModified || '', we } : null;
        const manual = Object.keys(L.shiftOv[d] || {}).length;
        const rosterFound = Boolean(L.roster.data?.found);
        const useCard = s.labor !== 'hj' && covers;
        const workbook = Boolean(equipWeeks?.[we]) && s.equip !== 'hj';
        return {
            d,
            feed,
            // The dates feed is one slow read per job; until it lands, no
            // day can say which feed it is on.
            feedLoading: Boolean(heet.loading) && !r,
            cardLoading: Boolean(c?.loading),
            cardError: c?.error || null,
            cardFile: file,
            card: covers ? file : null,
            labor: s.labor === 'hj' ? feed : covers ? 'reviewed time card' : s.labor === 'card' ? 'refused — no reviewed card' : file ? `${feed} (card has no hours this day)` : feed,
            shift: [
                ...(manual ? [`${manual} manual`] : []),
                ...(useCard && s.shift === 'auto' ? ['reviewed card'] : []),
                ...(s.shift === 'auto' && got?.basis === 'cards' && got.trust?.trusted ? ['foreman cards'] : []),
                ...(s.shift !== 'hj' && rosterFound ? ['attendance roster'] : []),
                s.shift === 'auto' && got?.basis === 'cards' && !got.trust?.trusted ? 'Day (roll-up card ignored)' : 'HeavyJob card',
            ].join(' → '),
            cls: s.cls === 'workbook' ? 'Staff List workbook' : [
                ...(Object.keys(L.peopleCls || {}).length ? ['job rule'] : []),
                ...(useCard ? ['reviewed card'] : []),
                ...(L.rules?.clsJob && Object.keys(L.clsJobCls || {}).length ? [`${L.rules.clsJob} master`] : []),
                ...(rosterFound ? ['roster rate-sheet column'] : []),
                'Staff List workbook',
            ].join(' → '),
            cardsApproved: got?.cards ? `${got.cards.filter((c) => c.isApproved).length}/${got.cards.length}` : null,
            equip: workbook ? 'reviewed workbook' : 'HeavyJob',
            equipWorkbook: workbook,
            // HEET vs the raw feed only matters on a day built off HEET; a
            // live-card day ignores HEET's count (the preview names who it lacks).
            mismatch: /live/.test(feed) ? 0 : dayMismatch(r),
            manual,
            rosterFound,
        };
    };

    // Nothing builds a ticket until it knows who is on which shift. Waits for
    // the roster and for the reviewed card of every week the dates touch, gives
    // a failed read one more try, and then refuses — out loud — rather than
    // fall back to the HeavyJob cards. "Not found" is a real answer and passes;
    // "could not read" is not.
    const sourcesReady = async (dates) => {
        const need = [...new Set((dates || []).map(weekEndFor).filter(Boolean))];
        const started = Date.now();
        let retried = false;
        for (;;) {
            const L = live.current;
            const known = need.filter((we) => L.weeks.includes(we));
            const pending = L.roster.loading || known.some((we) => !L.cards[we] || L.cards[we].loading);
            if (!pending) {
                const badCards = known.filter((we) => L.cards[we]?.error);
                // The roster only matters for a week with no reviewed card.
                const needRoster = !known.length || known.some((we) => !L.cards[we]?.data);
                const badRoster = Boolean(needRoster && L.roster.error);
                if (!badCards.length && !badRoster) return;
                if (retried) {
                    const what = [
                        ...(badRoster ? [`the attendance roster (${L.roster.error})`] : []),
                        ...badCards.map((we) => `the reviewed time card for WE ${moment(we, 'YYYY-MM-DD').format('M/D')} (${L.cards[we].error})`),
                    ].join(' and ');
                    throw new Error(`could not read ${what}. Nothing was built — without it everyone lands on the HeavyJob card's shift. Try again in a moment.`);
                }
                retried = true;
                await Promise.all([ // eslint-disable-line no-await-in-loop
                    ...(badRoster ? [fetchRoster(true)] : []),
                    ...badCards.map((we) => fetchCard(we, true)),
                ]);
            }
            if (Date.now() - started > 120000) throw new Error('timed out waiting for the attendance roster and the reviewed time card');
            await new Promise((r) => { setTimeout(r, 250); }); // eslint-disable-line no-await-in-loop
        }
    };

    // Per-day HeavyJob detail, fetched on first expand or preview and cached.
    const [dayCache, setDayCache] = useState({});
    useEffect(() => { setDayCache({}); }, [jobKey]);
    const loadDay = (d, refetch = false) => {
        setDayCache((c) => ((!refetch && c[d]) ? c : { ...c, [d]: { loading: true } }));
        if (!refetch && dayCache[d]) return;
        // A deliberate refetch must not be answered from fetchDay's shared load.
        if (refetch && dayFetches.current) delete dayFetches.current[`${jobKey}|${d}`];
        // A missing response is a failure, not an empty day — caching it as
        // rows:[] would pin "No HeavyJob hours" on a day that has plenty
        // until the next job switch. loadDayFeeds throws when neither the
        // cards nor HEET answered.
        loadDayFeeds(d)
            .then((info) => setDayCache((c) => ({ ...c, [d]: info })))
            .catch((e) => setDayCache((c) => ({ ...c, [d]: { loading: false, error: String(e?.message || e) } })));
    };

    // The tracking start for the job — a stored date, so the day list runs
    // from a line the user drew rather than from whatever HEET still holds.
    // One row per job (rowId 'tcstart'), value the YYYY-MM-DD date.
    const projStart = useMemo(() => {
        const r = (userRows || []).find((x) => x?.Forecast_Field_Name === 'tcstart');
        const v = String(r?.Forecast_Value || '');
        return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
    }, [userRows]);

    const sheets = useMemo(() => parseSheets(userRows), [userRows]);
    const tracked = useMemo(() => {
        const m = {};
        sheets.forEach((s) => { m[s.key] = s; });
        return m;
    }, [sheets]);

    // One block per work day, newest first. This is the whole page — the day
    // is the unit of work here, so every calendar day between the first known
    // day and today gets a row, HEET hours or not: a shift that ran on a day
    // HEET has nothing for (a weekend, a crew that entered time late) still
    // has a sheet to track, and hunting for it in a date picker hid it.
    const days = useMemo(() => {
        const set = new Set();
        Object.keys(heetBy).forEach((d) => set.add(d));
        sheets.forEach((s) => set.add(s.d));
        // The stored tracking start anchors the list: every day from it to
        // today shows whether or not any feed still remembers the day.
        if (projStart) set.add(projStart);
        if (set.size) {
            // Fill the gaps. Capped so one very old tracked day can't turn the
            // page into a thousand empty rows.
            const today = moment().format('YYYY-MM-DD');
            const all = [...set];
            const last = all.reduce((a, b) => (b > a ? b : a), today);
            const first = all.reduce((a, b) => (b < a ? b : a), last);
            const cur = moment(first, 'YYYY-MM-DD');
            const end = moment(last, 'YYYY-MM-DD');
            for (let i = 0; i < 400 && !cur.isAfter(end); i += 1) {
                set.add(cur.format('YYYY-MM-DD'));
                cur.add(1, 'day');
            }
        }
        // Two time sheets per work day — the day and night crews sign
        // separately, so each shift is tracked, sent and filed on its own.
        // Days tracked under the merged one-sheet layout read as that day's
        // Day sheet; the old Night rows keep their history under NIGHT.
        return [...set].sort((a, b) => b.localeCompare(a)).map((d) => ({
            d,
            heet: heetBy[d] || null,
            sheets: {
                Day: tracked[sheetKey(d, 'Day')] || null,
                Night: tracked[sheetKey(d, 'Night')] || null,
            },
        }));
    }, [sheets, heetBy, tracked, projStart]);

    const stamp = (s, n) => ({
        s,
        ...(n ? { n: String(n).slice(0, 120) } : {}),
        u: String(userName || '').slice(0, 30),
        t: moment().format('YYYY-MM-DD HH:mm'),
    });

    const persist = (sheet) => {
        saveRows([{
            rowId: sheet.key, fieldName: 'tcsheet',
            value: sheet.st, notes: sheetNotes(sheet),
        }]);
    };

    const track = (d, sh = SHIFT_DEFAULT) => {
        if (versionLocked) return;
        if (!d) { showToast('Pick a date first.', 'warn'); return; }
        if (tracked[sheetKey(d, sh)]) {
            showToast(`${fmtDay(d)} ${sh} is already tracked.`, 'warn');
            return;
        }
        persist({ key: sheetKey(d, sh), d, sh, st: 'Pending', h: [stamp('Pending')] });
    };

    const applyMove = () => {
        const { sheet, mv } = movePrompt;
        persist({ ...sheet, st: mv.to, h: [...sheet.h, stamp(mv.to, moveNote.trim())] });
        setMovePrompt(null);
        setMoveNote('');
    };

    const lastOf = (s) => (s.h.length ? s.h[s.h.length - 1] : null);

    // ── Field ticket signatures (DocuSign) ──
    // Envelopes live in PC.Billing_Signature_* rather than on the tracker row:
    // the Connect webhook that reports a signature has no forecast version to
    // write a Forecast_User_Data row against, and that row's Notes blob is
    // capped tightly enough that it already drops the oldest stamps to fit.
    // So the tracker row still owns the STAGE, and the envelope owns the
    // record of what the client actually did with the sheet.
    const [sigs, setSigs] = useState({ job: null, loading: false, list: [], configured: true, error: '', consoleUrlBase: '' });
    const [sigBusy, setSigBusy] = useState(0); // envelope RowID mid-action
    // Which signing flow a Ticket Map send uses: 'client' (the job's signer
    // map, after the check and approval) or 'test' (only me, no gates, never
    // touches the ticket). Picked in the Upload dialog or the Ticket Map;
    // session-only on purpose so a reload always comes back to the real flow.
    const [sigFlow, setSigFlow] = useState('client');

    const loadSigs = (job) => {
        if (!job) return;
        const seq = ++feedSeq.current.sigs;
        const mine = () => seq === feedSeq.current.sigs;
        setSigs((s) => ({ ...s, list: s.job === job ? s.list : [], job, loading: true }));
        // A read that fails keeps the last list and says so. postRequest hands
        // back undefined for a timeout, and reading that as no envelopes
        // showed every week summary and invoice as never sent.
        const failed = (msg) => mine() && setSigs((s) => (s.job === job ? { ...s, loading: false, error: msg, readFailed: true } : s));
        Promise.resolve(loadBillingSignatures({ job: String(job) }))
            .then((res) => {
                if (!mine()) return;
                if (!res) { failed('no answer from the server in time'); return; }
                if (res.error && !res.envelopes) { failed(String(res.error)); return; }
                setSigs((s) => (s.job !== job ? s : {
                    job,
                    loading: false,
                    list: res.envelopes || [],
                    configured: res.configured !== false,
                    error: res.error ? String(res.error) : '',
                    consoleUrlBase: String(res.consoleUrlBase || ''),
                    readFailed: false,
                }));
            })
            .catch((e) => failed(String(e?.message || e)));
    };
    useEffect(() => { loadSigs(jobKey); }, [jobKey]); // loadSigs is stable per render; only jobKey should retrigger

    // The Force Report used to ride this table under Shift = 'Force' before
    // it became a plain email deliverable — filter any such legacy rows out
    // so they never read as a sheet's envelope.
    // Ticket Map documents' envelopes (Shift 'Doc') belong to their document,
    // not to a date+shift key — they're read through sigByDoc below.
    const sheetSigs = useMemo(() => (sigs.list || []).filter((e) => e.Shift !== 'Force' && e.Shift !== 'Doc'), [sigs.list]);

    // ── Ticket documents (Ticket Map) ──
    // A document covers any set of work-day × shift cells and is signed as one
    // packet; a cell it covers takes the document's status on the day chips.
    // See billingTicketDocs.js for the model and billingTicketMap.js for the
    // modal where documents are built, checked by Claude, and sent.
    const [mapOpen, setMapOpen] = useState(null); // null | { focus, nonce? }
    useEffect(() => { setMapOpen(null); }, [jobKey]);
    // After the job reset above, so a hand-off that mounts this tab wins.
    useEffect(() => {
        if (!prefill?.cells?.length) return;
        setMapOpen({ focus: { cells: prefill.cells, kind: prefill.kind }, nonce: prefill.nonce });
        if (onPrefillUsed) onPrefillUsed();
    }, [prefill?.nonce]); // one hand-off per nonce
    const ticketDocs = useMemo(() => parseDocs(userRows), [userRows]);
    // The reviewed weekly time cards, by week ending. Where one exists it is
    // the equipment of record for that week's tickets; where none does, the
    // tickets stay on HeavyJob's equipment lines.
    const equipWeeks = useMemo(() => parseEquipWeeks(userRows), [userRows]);
    // One date's equipment, reviewed workbook first. Returns null when that
    // week has no workbook, so callers fall through to the HeavyJob rows.
    const sheetEquip = (d) => equipForDate(equipWeeks, d);
    // The day's ticket data for every packet page: HeavyJob's pivot, with the
    // reviewed workbook's equipment swapped in where that week has one. The
    // workbook bills each unit's whole day on one phase code, so that code
    // joins the matrix when it isn't already there.
    const dayTicket = (info, d, src) => {
        const t = ticketFrom(rowsFor(d, info.rows, src), ratemap, { overrides: overridesFor(d, src), staffCls: clsFor(d, src), equipName });
        if (normalizeSrc(src).equip === 'hj') return t;
        // The reviewed workbook names units in the field's words; the client
        // document names them the way the rate sheet does.
        const fromSheet = sheetEquip(d);
        const reviewed = fromSheet && equipName ? nameEquipment(fromSheet, equipName).equipment : fromSheet;
        if (!reviewed) return t;
        const codes = !reviewed.length || t.codes.some((c) => c.code === EQUIP_PHASE.code)
            ? t.codes : [...t.codes, { ...EQUIP_PHASE }];
        return { ...t, equipment: reviewed, codes };
    };
    // Who signs what on this job (Rates & Rules › Markup & settings). The
    // ticket PDFs print one signature line per person for the shift, and
    // every envelope goes to the people whose role covers its shifts.
    const signerMap = useMemo(() => jobSigners(settings), [settings]);
    const coverage = useMemo(() => coverageMap(ticketDocs), [ticketDocs]);
    const sigByDoc = useMemo(() => envelopesByDoc(sigs.list), [sigs.list]);
    // The week summary and the invoice over each work date, for the day
    // list's last two chips — read off the envelopes that went out and the
    // Billing tab's summaries (billingWeekTracker dayBilling).
    const billingAt = useMemo(
        () => dayBilling({ envelopes: sigs.list, docs: ticketDocs, invoices, billed }),
        [sigs.list, ticketDocs, invoices, billed],
    );
    // Newest envelope per (date, shift). The read path already orders newest
    // first, so the first hit for a key wins and older sends stay available
    // underneath it for the history panel.
    const sigBy = useMemo(() => {
        const m = {};
        sheetSigs.forEach((e) => {
            const k = `${e.Work_Date}_${e.Shift}`;
            if (!m[k]) m[k] = e;
        });
        return m;
    }, [sheetSigs]);
    // Every envelope a shift has ever had, oldest first — a declined sheet
    // that went back out three times should read as three attempts.
    const sigAllBy = useMemo(() => {
        const m = {};
        sheetSigs.forEach((e) => { (m[`${e.Work_Date}_${e.Shift}`] = m[`${e.Work_Date}_${e.Shift}`] || []).unshift(e); });
        return m;
    }, [sheetSigs]);
    // ── Force Report email sends ──
    // The Force Report goes out as a plain email, so its record is a
    // Forecast_User_Data row per day (fieldName 'tcforce', rowId
    // 'tcforce_<date>'), Notes = { d, h: [{ u, t, to }] } — who mailed it,
    // when, and to whom. Same capped-blob treatment as sheetNotes.
    const forceSends = useMemo(() => {
        const m = {};
        (userRows || []).forEach((x) => {
            if (x?.Forecast_Field_Name !== 'tcforce') return;
            try {
                const p = x.Notes ? JSON.parse(x.Notes) : null;
                if (p && p.d && Array.isArray(p.h)) m[p.d] = p.h;
            } catch (e) { /* malformed blob — ignore */ }
        });
        return m;
    }, [userRows]);
    const recordForceSend = (d, to) => {
        let h = [...(forceSends[d] || []), {
            u: String(userName || '').slice(0, 30),
            t: moment().format('YYYY-MM-DD HH:mm'),
            to: String(to).slice(0, 160),
        }];
        let notes = JSON.stringify({ d, h });
        while (h.length > 1 && notes.length > 1000) {
            h = h.slice(1);
            notes = JSON.stringify({ d, h });
        }
        saveRows([{ rowId: `tcforce_${d}`, fieldName: 'tcforce', value: String(h.length), notes }]);
    };

    // Let the envelope drive the tracker stage. Without this the client could
    // sign a sheet on Tuesday and the tracker would still read "Sent for
    // Signature" until somebody clicked a button — which is the exact blind
    // spot this whole feature exists to close.
    useEffect(() => {
        if (versionLocked || !sigs.list.length) return;
        Object.entries(sigBy).forEach(([k, env]) => {
            const stage = envInfo(env.Status).stage;
            if (!stage) return;
            const sheet = tracked[`tcs_${k}`];
            if (!sheet || sheet.st === stage) return;
            persist({
                ...sheet,
                st: stage,
                h: [...sheet.h, {
                    s: stage,
                    n: `DocuSign: ${envInfo(env.Status).label}`.slice(0, 120),
                    u: 'DocuSign',
                    t: fmtUtcLong(env.Completed_At_Utc || env.Declined_At_Utc || env.Sent_At_Utc)
                        || moment().format('YYYY-MM-DD HH:mm'),
                }],
            });
        });
    }, [sigBy, tracked, versionLocked]); // deliberately narrow: setters/refs are stable

    // Same for documents: the client signing (or declining) the packet moves
    // the document, and through it every day/shift it covers.
    useEffect(() => {
        if (versionLocked || !sigs.list.length) return;
        ticketDocs.forEach((doc) => {
            const envs = partEnvelopes(doc, sigByDoc);
            // The three sends together decide the stage: signed once every
            // one of them is, out as soon as one is (docStage).
            const live = Object.values(envs).filter(Boolean);
            if (!live.length) return;
            const stage = docStage(doc, envs);
            if (!stage || stage === doc.st) return;
            // The newest thing that happened is what the history line names.
            const env = live.slice().sort((a, b) => String(a.Sent_At_Utc || '').localeCompare(String(b.Sent_At_Utc || '')))[live.length - 1];
            // A dead envelope only matters to a document that thinks it's out;
            // after that the Ticket Map's check owns Draft vs Validated.
            const dead = live.every((e) => ['voided', 'expired', 'failed'].includes(String(e.Status || '').toLowerCase()));
            if (dead && doc.st !== 'Sent for Signature') return;
            saveRows(docRows({
                ...doc,
                st: stage,
                rev: (doc.rev || 0) + 1,
                h: [...doc.h, {
                    s: stage,
                    n: `DocuSign: ${envInfo(env.Status).label}`.slice(0, 120),
                    u: 'DocuSign',
                    t: fmtUtcLong(env.Completed_At_Utc || env.Declined_At_Utc || env.Voided_At_Utc || env.Sent_At_Utc)
                        || moment().format('YYYY-MM-DD HH:mm'),
                }],
            }));
        });
    }, [sigByDoc, ticketDocs, versionLocked]); // deliberately narrow deps

    // Remind / void / refresh, straight through to DocuSign.
    const runSigAction = async (env, action, extra = {}) => {
        if (sigBusy) return;
        setSigBusy(env.RowID);
        try {
            const res = await billingSignatureAction({
                job: String(jobKey), rowId: env.RowID, action, actor: userName, ...extra,
            });
            if (res?.error) throw new Error(res.error);
            showToast({
                resend: 'Reminder sent.',
                void: 'Envelope voided.',
                refresh: 'Status refreshed.',
                send: 'Sent to the client.',
            }[action] || 'Done.', 'success');
            loadSigs(jobKey);
        } catch (e) {
            showToast(`${action} failed — ${e?.message || e}`, 'error');
        } finally {
            setSigBusy(0);
        }
    };

    // Open the envelope's PDF in a new tab: DocuSign's own conversion of our
    // workbook before signing, the signed copy with its certificate after.
    const openSigDoc = async (env) => {
        if (sigBusy) return;
        setSigBusy(env.RowID);
        try {
            const res = await loadBillingSignatureDocument({ job: String(jobKey), rowId: env.RowID });
            if (res?.error || !res?.contentBase64) throw new Error(res?.error || 'no document returned');
            const bin = atob(res.contentBase64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
            const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            window.open(url, '_blank', 'noopener,noreferrer');
            // Not revoked immediately — the new tab still needs to fetch it.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            showToast(`Could not open the document — ${e?.message || e}`, 'error');
        } finally {
            setSigBusy(0);
        }
    };

    // ── Ticket export / SharePoint filing, from the preview ──
    // preview.kind is the shift being looked at ('Day' | 'Night') or 'Force'.
    const previewDate = preview?.d || '';
    const previewShift = preview && SHIFTS.includes(preview.kind) ? preview.kind : null;
    const openPreview = (d, kind) => {
        setPreview({ d, kind });
        setShowGenForce(false);
        // Refetch through a cached failure, and through a cached empty day
        // that the summary says has hours — both are stale, not truth. A day
        // with no detail source left is legitimately empty: refetching it
        // would just loop.
        const have = dayCache[d];
        const stale = Boolean(have && !have.loading
            && (have.error || (have.rows && !have.rows.length && hasDetail(d))));
        loadDay(d, stale);
    };
    // The cache holds HEET's rows as they came; what anything READS is those
    // rows reconciled to the week's reviewed card, which can land (or be
    // replaced) long after the day was cached.
    const dayViews = useMemo(() => Object.fromEntries(Object.entries(dayCache).map(([d, c]) => [
        d, c && c.rows ? { ...c, rows: applyApprovedCard(c.rows, cards[weekEndFor(d)]?.data || null, d) } : c,
    ])), [dayCache, cards]);
    const dayInfo = dayViews[previewDate] || {};
    // The whole day's ticket (both crews, shift overrides applied) — the
    // shift previews and exports cut it down with ticketForShift.
    const ticket = useMemo(
        () => (previewDate && dayInfo.rows
            ? ticketFrom(rowsFor(previewDate, dayInfo.rows), ratemap, { overrides: overridesFor(previewDate), staffCls: clsFor(previewDate), equipName })
            : null),
        [previewDate, dayInfo.rows, ratemap, rosterShift, shiftOv, staffCls, cards, equipName], // overridesFor/rowsFor read rosterShift + shiftOv + cards
    );
    // The previewed shift's cut of it — what the export for this chip holds.
    const shiftTicket = useMemo(
        () => (ticket && previewShift ? ticketForShift(ticket, previewShift) : ticket),
        [ticket, previewShift],
    );
    // The Force Report side of the same day: the Tier 3 roll-up, Direct then
    // Indirect, which is how the client counts heads.
    const force = useMemo(
        () => (previewDate && dayInfo.rows ? rollUp(peopleFrom(dayInfo.rows, ratemap)) : null),
        [previewDate, dayInfo.rows, ratemap],
    );

    // ── What the day's ticket knows (billingCardSource contextNotes) ──
    // The feed that answered, HEET vs the cards, how shifts and classes were
    // placed, the phase fold, the attendance master's marks, split days
    // across the sister job and anyone left off — one record the preview
    // panel, the assistant and the packet manifest all read.
    const dayContext = (d, tk, info) => {
        const c = info || dayCache[d] || {};
        const feed = dayFeeds.current[d] || c.feed || null;
        if (!c.rows) return { feed, notes: [] };
        const byId = {};
        (c.rows || []).forEach((r) => { if (r.Staff_ID && !byId[String(r.Staff_ID)]) byId[String(r.Staff_ID)] = r.Staff_Name || ''; });
        (tk?.labor || []).forEach((p) => { byId[String(p.id)] = p.name; });
        const carded = applyApprovedCard(c.rows, cardFor(d), d);
        const fold = foldRows(carded, rules.fold);
        const cardIds = [...new Set(carded.filter((r) => r.Staff_ID && (r.Record_Type === 'Regular' || r.Record_Type === 'Overtime') && Number(r.Hours) > 0)
            .map((r) => String(r.Staff_ID)))];
        const attendance = attendanceChecks({ roster: roster.data, date: d, job: jobKey, cardIds, sisterIds: feed?.sisterIds || [] });
        // A person placed by the reviewed card or a manual fix isn't "stale";
        // only where the foreman card is what beat the master.
        const placedElse = { ...cardShiftOf(cardFor(d)), ...(shiftOv[d] || {}) };
        const stale = feed?.basis === 'cards' && feed.trust?.trusted
            ? staleRosterShift(rosterShift, feed.cardShift).filter((x) => !placedElse[x.id]).map((x) => ({ ...x, name: byId[x.id] || x.id }))
            : [];
        const overrides = Object.entries(rules.people || {})
            .filter(([id]) => byId[id] !== undefined || c.rows.some((r) => String(r.Staff_ID) === id))
            .map(([id, o]) => ({ id, name: o.name, cls: o.cls, card: (c.rows.find((r) => String(r.Staff_ID) === id) || {}).Staff_Name }));
        const prior = priorShiftFor(d);
        const priorInfo = prior ? {
            we: moment(prior.we, 'YYYY-MM-DD').format('M/D'),
            saved: moment(prior.saved, 'YYYY-MM-DD').format('M/D'),
            night: Object.values(cardShiftOf(prior.card)).filter((x) => x === 'Night').length,
        } : null;
        const notes = contextNotes({
            feed,
            prior: priorInfo,
            fold,
            attendance,
            stale,
            splitDays: feed?.splitDays || [],
            dropped: tk?.dropped || [],
            overrides,
            clsJob: rules.clsJob || null,
            clsJobFound: Boolean(clsRoster.data?.found),
            sister: rules.sister || null,
        });
        return { feed, fold, attendance, stale, overrides, notes };
    };

    // ── The preview's chat (billingAiChat) ──
    // What the assistant in the ticket preview can see, and the one edit it
    // can propose. The hours come from HeavyJob and the classifications from
    // the field's Staff List — neither is ours to change from here — so the
    // shift correction is the whole write surface, which is also the error
    // this document actually has: HeavyJob codes nearly every card Shift 1,
    // so the night crew lands on the day sheet. People are addressed by the
    // index they carry in the context rather than by name, so a name the
    // model reads off the sheet can never resolve to the wrong person.
    const ticketAi = (d, sh, tk) => {
        const people = tk ? tk.labor : [];
        const pick = (p) => {
            const list = (Array.isArray(p.people) ? p.people : [])
                .map((i) => people[Number(i)])
                .filter((x) => x && x.id);
            if (!list.length) throw new Error('none of those people are on this sheet');
            return list;
        };
        return {
            locked: versionLocked,
            context: (qa) => {
                const ctx = dayContext(d, tk);
                const feed = ctx.feed;
                const marks = Object.fromEntries((roster.data?.roster || [])
                    .filter((r) => r.marks && r.marks[d] !== undefined).map((r) => [String(r.id), r.marks[d]]));
                return {
                    document: 'Multi Phase Field Ticket — the sheet the client signs for this shift',
                    job: String(jobKey),
                    jobName: jobTitle(dayInfo),
                    client: jobClient(dayInfo),
                    workDate: d,
                    shift: sh,
                    stage: tracked[sheetKey(d, sh)]?.st || 'not tracked',
                    // How this ticket was built — read this before judging a
                    // number. The notes are the checks the packet scripts
                    // run before a send (fail / warn / info).
                    howBuilt: {
                        hoursBasis: feed?.basis === 'cards'
                            ? 'live HeavyJob time cards (HCSS API) — the card wins over HEET; hours, PD/TP/RR adders and equipment are exactly what the foremen keyed'
                            : 'HEET / actual cost (the HeavyJob API did not answer or has no cards for the day)',
                        cards: (feed?.cards || []).map((c) => ({
                            foreman: c.foreman, shift: c.shift === 2 ? 'Night (2)' : 'Day (1)', people: c.people, hours: c.hours,
                            equipHours: c.equipHours, approved: c.isApproved, revision: c.revision, lastModified: c.lastModified,
                        })),
                        cardShiftTrusted: feed?.trust || null,
                        heetVsCards: feed?.compare ? { card: feed.compare.card, heet: feed.compare.heet, onlyOnCards: feed.compare.onlyCard, onlyInHeet: feed.compare.onlyHeet, rekeyed: feed.compare.hours } : null,
                        reviewedTimeCard: cardFor(d) ? (cardFor(d).file?.name || 'yes') : 'none for this week yet',
                        attendanceMaster: roster.data?.found ? `${roster.data.file?.path || ''} / ${roster.data.file?.name || ''}` : 'not found',
                        shiftPrecedence: 'manual correction > reviewed time card > foreman card (when the day has per-foreman cards) > attendance master Days/Nights > Day',
                        classPrecedence: `job rule > reviewed time card > ${rules.clsJob ? `${rules.clsJob}'s attendance master > ` : ''}attendance master rate-sheet column > Staff List workbook > pay class map`,
                        jobRules: {
                            phaseFolds: Object.entries(rules.fold || {}).map(([from, to]) => `${from} billed on ${to.code} ${to.desc}`),
                            people: Object.entries(rules.people || {}).map(([id, o]) => ({ empId: id, printsAs: o.name, cls: o.cls, why: o.why })),
                            sisterJob: rules.sister || null,
                        },
                        notes: ctx.notes,
                    },
                    phaseCodes: (tk ? tk.codes : []).map((c) => ({ code: c.code, desc: c.desc })),
                    laborQa: qa || 'not run',
                    headcount: people.length,
                    labor: people.map((x, i) => ({
                        i,
                        empId: x.id || '',
                        name: x.name,
                        billedAs: x.cls || '',
                        classFrom: clsSourceOf(d, x.id),
                        jdePayClass: x.payClass || '',
                        crewLead: x.crewLead || '',
                        shift: x.shift || '(none on the card)',
                        shiftFrom: shiftSourceFor(d, x.id),
                        attendanceMark: marks[String(x.id)] ?? '(not on the master)',
                        hoursByPhase: x.hours,
                        total: round2(x.total),
                        ot: round2(x.ot),
                        ...(x.pd ? { perDiem: true } : {}),
                        ...(x.tp ? { truckPay: true } : {}),
                        ...(x.rr ? { rigPay: true } : {}),
                    })),
                    leftOff: (tk?.dropped || []),
                    equipment: (tk ? tk.equipment : []).map((e) => ({
                        unit: e.unit, desc: e.desc, rental: e.rental,
                        hoursByPhase: e.hours, total: round2(e.total),
                    })),
                };
            },
            actions: {
                set_shift: {
                    args: '{people, shift}',
                    desc: 'move people to the day or night sheet for this work date. shift is "Day" or '
                        + '"Night". This is a correction to the card, not a re-entry of hours — the hours '
                        + 'follow the person onto the other sheet. Setting someone to what the attendance '
                        + 'roster already says drops the override instead of storing one.',
                    apply: (p) => {
                        const want = p.shift === 'Night' ? 'Night' : p.shift === 'Day' ? 'Day' : null;
                        if (!want) throw new Error('shift must be "Day" or "Night"');
                        pick(p).forEach((x) => saveShift(d, x.id, want));
                    },
                },
                reload_cards: {
                    args: '{}',
                    desc: 'reload this work date from the live HeavyJob cards (and HEET for the comparison) — '
                        + 'use when a foreman says they just fixed a card, or the cards were edited after this '
                        + 'ticket was opened (see howBuilt.cards lastModified).',
                    apply: () => { loadDay(d, true); },
                },
            },
        };
    };

    // Both take the date explicitly (defaulting to the open preview) because
    // the signature dialog — and the export icon on any day's chip — builds a
    // ticket for a day that is not necessarily the one being previewed.
    //
    // The export icons fire straight off the row, so the day's HeavyJob detail
    // may never have been fetched: this resolves it first rather than quietly
    // building an empty ticket out of a cache miss.
    //
    // In-flight and finished loads are also shared through a ref, because the
    // Ticket Map asks for several days in one go and the state cache doesn't
    // update inside a single async pass — without it one ticket covering a
    // day's two shifts pulls that day twice (a minute each on actuals days).
    const dayFetches = useRef({});
    useEffect(() => { dayFetches.current = {}; }, [jobKey]);
    const fetchDay = async (d, src) => {
        await sourcesReady([d]);
        const raw = await fetchDayRaw(d);
        return { ...raw, rows: rowsFor(d, raw.rows, src) };
    };
    const fetchDayRaw = async (d) => {
        const have = dayCache[d];
        if (have && have.rows) return have;
        const k = `${jobKey}|${d}`;
        if (!dayFetches.current[k]) {
            dayFetches.current[k] = loadDayFeeds(d)
                .then((info) => {
                    setDayCache((c) => ({ ...c, [d]: info }));
                    return info;
                })
                .catch((e) => { delete dayFetches.current[k]; throw e; });
        }
        return dayFetches.current[k];
    };

    // Every packet page needs its day's HEET pull, and the loops below used to
    // take them strictly one at a time — a week of days that have aged out of
    // HeavyJob is then a minute each, in series, before a single page is
    // drawn. Ask for them all up front, a few at a time; fetchDay dedupes
    // in-flight and finished loads, so the draw loops that follow read
    // straight from the cache. Bounded because HEET is the same database the
    // rest of the tab is reading.
    const DAY_CONCURRENCY = 4;
    const prefetchDays = async (dates, say) => {
        const list = [...new Set((dates || []).filter(Boolean))];
        if (list.length < 2) return;
        const queue = [...list];
        let done = 0;
        say?.(`reading ${list.length} days of hours`, 0, list.length);
        const worker = async () => {
            while (queue.length) {
                const d = queue.shift();
                // A day that fails here is not fatal: the draw loop asks for
                // it again and reports the failure where it happens.
                try { await fetchDay(d); } catch (e) { /* reported at draw time */ }
                done += 1;
                say?.(`reading hours — ${done} of ${list.length} days`, done, list.length);
            }
        };
        await Promise.all(Array.from({ length: Math.min(DAY_CONCURRENCY, list.length) }, worker));
    };

    const buildTicket = async (d = previewDate, sh = previewShift) => {
        const info = await fetchDay(d);
        return buildFieldTicketFile({
            rows: info.rows || [],
            ratemap,
            date: d,
            jobKey,
            jobName: jobTitle(info),
            client: jobClient(info),
            shift: sh || null,
            overrides: overridesFor(d),
            staffCls,
            equipName,
        });
    };
    const ticketName = (d = previewDate, sh = previewShift) => (
        `Multi Phase Field Ticket ${moment(d, 'YYYY-MM-DD').format('M.D.YY')}${sh ? ` (${sh})` : ''}.xlsx`
    );

    // xlsx bytes -> base64, chunked: String.fromCharCode.apply blows the call
    // stack on a whole workbook at once.
    const toBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        const CHUNK = 0x8000;
        let bin = '';
        for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        return btoa(bin);
    };

    // ── Upload to the job's Timecards folder ──
    // One path for every upload on this tab: the Ticket Map's backup picker
    // and a signed copy for a ticket document. Files land in 02.06 / Month / WE /
    // day (the WE folder for level 'week') and come back with the Graph item
    // id, so a ticket document can take the file as backup straight away.
    const uploadFileToJob = async (file, date, opts = {}) => {
        if (!file) throw new Error('no file chosen');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) throw new Error('pick the work date it belongs to');
        if (file.size > MAX_DOC_BYTES) throw new Error(`${file.name} is over 15 MB`);
        const contentBase64 = toBase64(await file.arrayBuffer());
        const res = await saveJobTicketFile({
            job: String(jobKey), date, name: opts.name || file.name, contentBase64, level: opts.level === 'week' ? 'week' : 'day',
        });
        if (res?.error || !res?.saved) throw new Error(res?.error || 'save failed');
        return { id: res.id || null, driveId: res.driveId || null, name: res.name || opts.name || file.name, path: res.path || '', webUrl: res.webUrl || '' };
    };
    // ── The reviewed weekly time card ──
    // The Labor + Equipment workbook a week's time card is reviewed into —
    // the same file the field ticket packet scripts take as SHEET_FILE. Only
    // its Equipment tab is stored: labor stays on the HeavyJob feed so the
    // crew and its hours have one source, while equipment has none (HeavyJob
    // never says a unit sat on rent for the calendar day).
    const importReviewedTimecard = async (file, we) => {
        if (!file) throw new Error('no file chosen');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(we || ''))) throw new Error('no week ending to file this against');
        const mod = await import('xlsx');
        const XLSX = mod.default || mod;
        const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
        const tab = wb.SheetNames.find((x) => /equipment/i.test(x));
        if (!tab) throw new Error(`${file.name} has no Equipment tab`);
        const grid = XLSX.utils.sheet_to_json(wb.Sheets[tab], { header: 1, raw: true, defval: null });
        const units = parseEquipSheet(grid, we);
        saveRows(equipWeekRows(we, units));
        return units;
    };

    const exportTicket = async (d = previewDate, sh = previewShift) => {
        try {
            const f = await buildTicket(d, sh);
            if (!f) { showToast(`No ${sh ? `${sh.toLowerCase()}-shift ` : ''}hours to ticket for this day.`, 'warn'); return; }
            const over = ticketOverflowWarning(f.overflow);
            if (over) showToast(over, 'warn');
            const blob = new Blob([f.buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = ticketName(d, sh);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            showToast('Ticket export failed - ' + (e?.message || e), 'error');
        }
    };

    // ── Field Ticket (PDF) ──
    // The client-facing signature document: a portrait page per SHIFT — the
    // day and night tickets go to different signers, so a day that ran both
    // crews exports two separate PDFs, each badged with its shift next to
    // the title (blue for day, ink for night, so the two can't be confused
    // in a stack of paper). Strike logo left, VoltaGrid right, one compact
    // job line, the hours matrix, the shift summary, the weekly-payroll
    // disclaimer and the signature block. The label strings under the
    // signature lines are the DocuSign anchor text — keep them
    // byte-identical.
    //
    // Equipment hours carry no shift in HEET (readHEETLabor's HCSS join is
    // per employee), so equipment prints on the day ticket and the night
    // ticket is labor only — except on a night-only day, where it rides the
    // only ticket there is.
    //
    // One labor matrix and one equipment matrix — every phase code gets a
    // column in the same table, with code columns narrowing (and their font
    // stepping down) as the count grows rather than spilling into a second
    // "continued" table or the workbook's hard cap of 14.
    //
    // Each labor row carries a BILLABLE estimate priced off the job's rate
    // schedule: ST hours × sheet ST rate + OT hours × sheet OT rate (the
    // sheet rate IS the billing rate, markup baked in — same pricing as
    // billing.js) + per diem at HEET's paid amount. OT and per diem
    // finalize weekly at payroll, which is what the legal line above the
    // signature block discloses. Equipment shows no dollars: HEET rows
    // carry no tier, so their rate can't be resolved here.
    const ticketPdfName = (d = previewDate, sh = previewShift) => (
        `Multi Phase Field Ticket ${moment(d, 'YYYY-MM-DD').format('M.D.YY')}${sh ? ` (${sh})` : ''}.pdf`
    );
    // Builds the day's ticket PDF(s) — one per shift the day ran, or just
    // `sh` when a shift is named. Returns null when the day has no hours at
    // all, and { tickets: [] } when the named shift is empty; callers turn
    // both into their own toasts. Each ticket is { shift, doc, name } — the
    // export saves the doc, the signature send uploads its bytes.
    // opts.sigMode 'shift' prints only the shift rep's lines — for pages that
    // ride under a summary the sign-everything people sign once.
    const buildTicketPdf = async (d = previewDate, sh = previewShift, opts = {}) => {
        const info = await fetchDay(d, opts.src);
        // A supplement's page carries its named people alone (opts.only).
        const t = narrowTicket(dayTicket(info, d, opts.src), opts.only);
        if (!t.labor.length && !t.equipment.length) return null;

        // No pricing here: the client's ticket carries hours only. The
        // billable estimate still shows on the on-screen preview below,
        // where it is an internal check rather than a page the client signs.

        const { jsPDF } = await import('jspdf');
        const autoTableMod = await import('jspdf-autotable');
        const autoTable = autoTableMod.default || autoTableMod.autoTable;

        // The flattened jpegs, not the pngs: jsPDF embeds a jpeg as-is
        // but re-encodes a png into megabytes of raw bitmap.
        const logoBytes = async (file) => {
            try {
                const r = await fetch(`${process.env.PUBLIC_URL || ''}/templates/${file}`);
                return r.ok ? new Uint8Array(await r.arrayBuffer()) : null;
            } catch (x) { return null; /* logos are decoration */ }
        };
        const [strikeLogo, clientLogo] = await Promise.all([
            logoBytes('strike-logo-flat.jpeg'), logoBytes('voltagrid-logo.jpeg'),
        ]);

        const nightCrew = t.labor.filter((p) => p.night);
        const dayCrew = t.labor.filter((p) => !p.night);
        let tickets = [];
        if (dayCrew.length && nightCrew.length) {
            tickets.push({ shift: 'Day', labor: dayCrew, equipment: t.equipment });
            tickets.push({ shift: 'Night', labor: nightCrew, equipment: [] });
        } else if (nightCrew.length) {
            tickets.push({ shift: 'Night', labor: nightCrew, equipment: t.equipment });
        } else {
            // No shift feed at all (pre-HCSS days) leaves the badge off
            // rather than claiming a day shift nobody entered.
            tickets.push({ shift: dayCrew.some((p) => p.shift) ? 'Day' : '', labor: dayCrew, equipment: t.equipment });
        }
        // Exporting from one shift's chip draws just that shift's ticket
        // (an unbadged no-feed day counts as the day sheet).
        if (sh) {
            tickets = tickets.filter((x) => (sh === 'Night' ? x.shift === 'Night' : x.shift !== 'Night'));
            if (!tickets.length) return { tickets: [] };
        }

        // The palette is the logo's: Strike blue carries the accents, the
        // red appears once as the tip of the header rule. Everything else
        // is near-black ink and quiet grays — this page gets signed and
        // often printed, so restraint over decoration.
        const INK = [23, 32, 42];
        const MUTE = [120, 128, 138];
        const BLUE = [21, 82, 150];
        const RED = [206, 32, 40];
        const LINE = [223, 227, 232];
        const FILL = [246, 247, 249];

        const drawTicket = ({ shift, labor, equipment }) => {
            // Only the codes this shift touched — the night ticket must
            // not carry the day crew's phase columns.
            const used = new Set();
            labor.forEach((p) => Object.keys(p.hours).forEach((c) => { if (p.hours[c]) used.add(c); }));
            equipment.forEach((e) => Object.keys(e.hours).forEach((c) => { if (e.hours[c]) used.add(c); }));
            const codes = t.codes.filter((c) => used.has(c.code));

            // A wide phase-code matrix doesn't fit portrait letter — go
            // landscape, exactly where the packet script does.
            const orientation = codes.length > 12 ? 'landscape' : 'portrait';
            const doc = new jsPDF({ orientation, unit: 'pt', format: 'letter' });
            const W = doc.internal.pageSize.getWidth();
            const H = doc.internal.pageSize.getHeight();
            const M = 40;
            const usable = W - 2 * M;

            const jobLine = [`Job ${jobKey}`, jobClient(info), jobTitle(info)].filter(Boolean).join('   —   ');
            let y = drawPacketHeader(doc, {
                strikeLogo,
                clientLogo,
                title: 'FIELD TICKET DAY SUMMARY',
                badge: shift ? `${shift.toUpperCase()} SHIFT` : '',
                rightText: moment(d, 'YYYY-MM-DD').format('dddd, MMMM D, YYYY'),
                subLeft: jobLine,
                M,
            });

            // Section heading: a small blue tick, tracked-out small caps,
            // and an optional right-aligned annotation.
            const section = (label, right) => {
                doc.setFillColor(...BLUE);
                doc.rect(M, y - 7, 3, 9, 'F');
                doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...INK);
                doc.text(label, M + 9, y, { charSpace: 1.2 });
                if (right) {
                    doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE);
                    doc.text(right, W - M, y, { align: 'right' });
                }
                y += 10;
            };
            const ensure = (need) => {
                if (y + need > H - M - 28) { doc.addPage(); y = M + 8; }
            };

            // ── The matrix ──
            // Shared table dress: dark header band, hairline row rules
            // only (no vertical grid), a faint zebra, and a strong rule
            // over the totals foot.
            const baseTable = {
                margin: { left: M, right: M, top: M + 8, bottom: M + 30 },
                theme: 'plain',
                styles: {
                    font: 'helvetica', fontSize: 7.3, cellPadding: { top: 3.4, bottom: 3.4, left: 3, right: 3 },
                    textColor: INK, lineColor: LINE, lineWidth: { top: 0.5 }, overflow: 'ellipsize', valign: 'middle',
                },
                headStyles: {
                    fillColor: INK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.2,
                    halign: 'center', cellPadding: { top: 4.5, bottom: 4.5, left: 2, right: 2 }, lineWidth: 0,
                },
                footStyles: { fillColor: FILL, textColor: INK, fontStyle: 'bold', lineColor: INK, lineWidth: { top: 1 } },
                alternateRowStyles: { fillColor: [250, 251, 252] },
                // A multi-page crew repeats the header per page, but the
                // grand totals print once, at the end.
                showFoot: 'lastPage',
            };
            const leftCols = (cols) => (h) => {
                if (h.section !== 'body' && cols.includes(h.column.index)) h.cell.styles.halign = 'left';
            };
            const colSum = (list, code) => h2(list.reduce((a, x) => a + (x.hours[code] || 0), 0)) || '';
            // One labor matrix and one equipment matrix, always — every
            // phase code gets a column in the same table. Code columns
            // shrink as the count grows (probed HEET history: the worst
            // real shift-day carries 8 codes; this stays comfortable to
            // ~10 and legible to ~12 with the tight treatment below).
            const n = codes.length;
            // Adaptive width budget (no crew-lead column on the client
            // view): shrink the text columns before letting code columns
            // fall below a readable width.
            let wName = 116;
            let wCls = 104;
            const wId = 44;
            const wFlag = 14;
            const wTotal = 36;
            let avail = usable - (wName + wId + wCls + 3 * wFlag + wTotal);
            let codeW = n ? Math.max(15, Math.min(30, Math.floor(avail / n))) : 24;
            if (n && codeW * n > avail) {
                wName = 92; wCls = 74;
                avail = usable - (wName + wId + wCls + 3 * wFlag + wTotal);
                codeW = Math.max(11, Math.min(30, Math.floor(avail / n)));
            }
            const tight = codeW < 20;
            // Real phase codes run 6-8 characters ("1952010") — far wider
            // than the hour values under them. When they don't fit flat,
            // the header prints them rotated 90° in a taller head band,
            // the way the client's own workbook does.
            const maxCode = codes.reduce((a, c) => Math.max(a, String(c.code).length), 0);
            const rot = maxCode > 4;
            // Rotated headers carry the code AND its description as a
            // second line.
            const descFor = (c) => String(c.desc || '').slice(0, 24);
            const maxDesc = codes.reduce((a, c) => Math.max(a, descFor(c).length), 0);
            const headH = Math.max(maxCode * 3.2, maxDesc * 2.7) + 14;
            // Header treatment: the whole head row is white with everything
            // anchored to the bottom — the phase codes stand rotated in dark
            // ink, and the label columns get only a slim black band along the
            // bottom edge. Code columns get tighter side padding than the
            // rest, foot totals ("171.5") always step a font size down, and
            // body hours shrink only when the columns themselves are tight.
            const BAND = 15;
            const codeCells = (first, labels, leftSet) => ({
                didParse: (h) => {
                    const i = h.column.index;
                    const isCode = i >= first && i < first + n;
                    if (h.section === 'head') {
                        h.cell.styles.fillColor = [255, 255, 255];
                        h.cell.styles.valign = 'bottom';
                        h.cell.text = [''];
                        if (isCode && rot) h.cell.styles.minCellHeight = headH;
                    }
                    if (!isCode) return;
                    if (h.section === 'foot') h.cell.styles.fontSize = 6.4;
                    else if (h.section === 'body' && tight) h.cell.styles.fontSize = 6.4;
                    h.cell.styles.cellPadding = {
                        top: h.section === 'head' ? 4.5 : 3.4, bottom: h.section === 'head' ? 4.5 : 3.4,
                        left: 1.2, right: 1.2,
                    };
                },
                didDraw: (h) => {
                    if (h.section !== 'head') return;
                    const i = h.column.index;
                    const isCode = i >= first && i < first + n;
                    const yBot = h.cell.y + h.cell.height;
                    if (isCode) {
                        const c = codes[i - first];
                        if (!rot) {
                            doc.setFont('helvetica', 'bold').setFontSize(tight ? 5.6 : 6.2).setTextColor(...INK);
                            doc.text(String(c.code), h.cell.x + h.cell.width / 2, yBot - 5, { align: 'center' });
                            return;
                        }
                        const desc = descFor(c);
                        const cx = h.cell.x + h.cell.width / 2;
                        const y0 = yBot - 4;
                        doc.setFont('helvetica', 'bold').setFontSize(5.8).setTextColor(...INK);
                        doc.text(String(c.code), cx + (desc ? 5.2 : 2.5), y0, { angle: 90 });
                        if (desc) {
                            doc.setFont('helvetica', 'normal').setFontSize(4.8).setTextColor(...MUTE);
                            doc.text(desc, cx - 1.2, y0, { angle: 90 });
                        }
                        return;
                    }
                    const label = labels[i];
                    if (label == null) return;
                    doc.setFillColor(...INK);
                    doc.rect(h.cell.x, yBot - BAND, h.cell.width, BAND, 'F');
                    doc.setFont('helvetica', 'bold').setFontSize(6.2).setTextColor(255, 255, 255);
                    if (leftSet.has(i)) doc.text(label, h.cell.x + 3, yBot - 5, { charSpace: 0.4 });
                    else doc.text(label, h.cell.x + h.cell.width / 2, yBot - 5, { align: 'center', charSpace: 0.4 });
                },
            });

            const laborTotal = labor.reduce((a, p) => a + p.total, 0);
            const equipTotal = equipment.reduce((a, e) => a + e.total, 0);

            if (labor.length) {
                section('LABOR', `${labor.length} ${labor.length === 1 ? 'person' : 'people'} — ${h2(laborTotal) || 0} hours`);
                const CENTER = { halign: 'center' };
                const first = 6;
                const colStyles = {
                    0: { cellWidth: wName }, 1: { cellWidth: wId, ...CENTER }, 2: { cellWidth: wCls },
                };
                for (let i = 0; i < 3; i += 1) colStyles[first - 3 + i] = { cellWidth: wFlag, ...CENTER };
                codes.forEach((c, i) => { colStyles[first + i] = { cellWidth: codeW, ...CENTER }; });
                colStyles[first + n] = { cellWidth: wTotal, halign: 'center', fontStyle: 'bold' };
                const laborLabels = ['EMPLOYEE', 'ID', 'CLASSIFICATION', 'PD', 'TP', 'RR',
                    ...codes.map(() => null), 'TOTAL'];
                const laborCodeCells = codeCells(first, laborLabels, new Set([0, 2]));
                autoTable(doc, {
                    ...baseTable,
                    startY: y,
                    head: [['EMPLOYEE', 'ID', 'CLASSIFICATION', 'PD', 'TP', 'RR',
                        ...codes.map((c) => String(c.code)), 'TOTAL']],
                    body: labor.map((p) => [
                        p.name, p.id, p.cls,
                        p.pd ? 'X' : '', p.tp ? 'X' : '', p.rr ? 'X' : '',
                        ...codes.map((c) => h2(p.hours[c.code]) || ''), h2(p.total) || '',
                    ]),
                    foot: [['Totals', '', '', '', '', '',
                        ...codes.map((c) => colSum(labor, c.code)), h2(laborTotal) || '']],
                    columnStyles: colStyles,
                    didParseCell: (h) => {
                        leftCols([0, 2])(h);
                        if (h.section === 'foot' && h.column.index >= first - 3) h.cell.styles.halign = 'center';
                        laborCodeCells.didParse(h);
                    },
                    didDrawCell: laborCodeCells.didDraw,
                });
                y = doc.lastAutoTable.finalY + 10;
                // The rotated header only has room for a truncated
                // description, so the codes are spelled out once underneath.
                if (codes.length) {
                    const legend = `Phase ${codes.length > 1 ? 'codes' : 'code'}: ${codes.map((c) => `${c.code} — ${c.desc || 'no description'}`).join('    ·    ')}`;
                    doc.setFont('helvetica', 'normal').setFontSize(6.5).setTextColor(...MUTE);
                    const wrapped = doc.splitTextToSize(legend, usable);
                    doc.text(wrapped, M, y);
                    y += wrapped.length * 8 + 10;
                } else {
                    y += 8;
                }
            }

            if (equipment.length) {
                ensure(70);
                section('EQUIPMENT', `${equipment.length} ${equipment.length === 1 ? 'unit' : 'units'} — ${h2(equipTotal) || 0} hours`);
                let wDesc = 240;
                let eAvail = usable - (wDesc + 54 + 34);
                let eW = n ? Math.max(15, Math.min(30, Math.floor(eAvail / n))) : 24;
                if (n && eW * n > eAvail) {
                    wDesc = Math.max(140, usable - 54 - 34 - n * 13);
                    eAvail = usable - (wDesc + 54 + 34);
                    eW = Math.max(11, Math.floor(eAvail / n));
                }
                const cs = { 0: { cellWidth: wDesc }, 1: { cellWidth: 54, halign: 'center' } };
                codes.forEach((c, i) => { cs[2 + i] = { cellWidth: eW, halign: 'center' }; });
                cs[2 + n] = { cellWidth: 34, halign: 'center', fontStyle: 'bold' };
                const equipCodeCells = codeCells(2, ['EQUIPMENT', 'UNIT NO', ...codes.map(() => null), 'TOTAL'], new Set([0]));
                autoTable(doc, {
                    ...baseTable,
                    startY: y,
                    head: [['EQUIPMENT', 'UNIT NO', ...codes.map((c) => String(c.code)), 'TOTAL']],
                    body: equipment.map((e) => [
                        e.rental ? `${e.desc}  (Rental)` : e.desc, e.unit,
                        ...codes.map((c) => h2(e.hours[c.code]) || ''), h2(e.total) || '',
                    ]),
                    foot: [['Total Hours', '', ...codes.map((c) => colSum(equipment, c.code)), h2(equipTotal) || '']],
                    columnStyles: cs,
                    didParseCell: (h) => {
                        leftCols([0])(h);
                        if (h.section === 'foot' && h.column.index >= 2) h.cell.styles.halign = 'center';
                        equipCodeCells.didParse(h);
                    },
                    didDrawCell: equipCodeCells.didDraw,
                });
                y = doc.lastAutoTable.finalY + 18;
            }

            // ── Shift summary — the numbers a signer actually checks ──
            ensure(180);
            // Hours only, no dollars: this page goes to the client, and the
            // packet script's page — which it has to match — prices nothing.
            const stats = [
                ['LABOR HOURS', h2(laborTotal) || '0'],
                ...(equipment.length ? [['EQUIPMENT HOURS', h2(equipTotal) || '0']] : []),
                ['CREW SIZE', String(labor.length)],
            ];
            doc.setFillColor(...FILL);
            doc.roundedRect(M, y, usable, 40, 4, 4, 'F');
            const step = (usable - 28) / stats.length;
            stats.forEach(([label, value], i) => {
                const x = M + 14 + i * step;
                doc.setFont('helvetica', 'bold').setFontSize(6.3).setTextColor(...MUTE);
                doc.text(label, x, y + 12, { charSpace: 0.8 });
                doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(...BLUE);
                doc.text(String(value), x, y + 28);
            });
            y += 66;

            // ── Signature block — one line per signer for this shift; the
            // labels are what the DocuSign tabs anchor on ──
            const slots = signerSlots(signerMap, shift || SHIFT_DEFAULT, opts.sigMode, d);
            slots.forEach((slot) => {
                ensure(70);
                doc.setDrawColor(...INK).setLineWidth(1);
                doc.line(M, y, M + 250, y);
                doc.line(M + 310, y, M + 410, y);
                doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE);
                doc.text(`${slot.label} - Signature`, M, y + 11);
                doc.text('Date', M + 310, y + 11);
                y += 40;
                doc.setLineWidth(1).line(M, y, M + 250, y);
                doc.text(`${slot.label} - Print`, M, y + 11);
                if (slot.name) doc.text(slot.name, M + 250, y + 11, { align: 'right' });
                y += 24;
            });

            // Footer on every page: identity (with the shift) left, page
            // count right.
            const pages = doc.getNumberOfPages();
            for (let p = 1; p <= pages; p += 1) {
                doc.setPage(p);
                doc.setDrawColor(...LINE).setLineWidth(0.6);
                doc.line(M, H - 32, W - M, H - 32);
                doc.setFont('helvetica', 'normal').setFontSize(6.8).setTextColor(...MUTE);
                doc.text(`Strike — Field Ticket Day Summary${shift ? ` (${shift} Shift)` : ''} — Job ${jobKey} — ${fmtShort(d)}`, M, H - 22);
                doc.text(`Page ${p} of ${pages}`, W - M, H - 22, { align: 'right' });
            }

            return { shift, doc, name: ticketPdfName(d, shift || null), labels: slots.map((s) => s.label) };
        };

        return { tickets: tickets.map(drawTicket) };
    };

    const exportTicketPdf = async (d = previewDate, sh = previewShift) => {
        try {
            const built = await buildTicketPdf(d, sh);
            if (!built) { showToast('No HeavyJob hours to ticket for this day.', 'warn'); return; }
            if (!built.tickets.length) { showToast(`No ${String(sh || '').toLowerCase()}-shift hours to ticket for this day.`, 'warn'); return; }
            built.tickets.forEach(({ doc, name }) => doc.save(name));
            if (built.tickets.length > 1) showToast('Day and Night shift tickets exported.', 'success');
        } catch (e) {
            showToast('Ticket PDF export failed — ' + (e?.message || e), 'error');
        }
    };

    // ── Week packet (PDF) ──
    // One document for a whole pay week and one crew shift: a summary cover
    // (person × day hours matrix, PD/TP/RR day counts, week totals, its own
    // signature block) followed by each day's Field Ticket. Client dress —
    // hours only, no dollars. Every page in the packet carries the DocuSign
    // anchor labels, so a signature field lands on every page.
    const weekEndOf = (d) => {
        const m = moment(d, 'YYYY-MM-DD');
        return (m.day() === 0 ? m : m.clone().day(7)).format('YYYY-MM-DD');
    };
    const weekDaysOf = (d) => {
        const we = weekEndOf(d);
        const start = moment(we, 'YYYY-MM-DD').subtract(6, 'days');
        const days = [];
        for (let i = 0; i < 7; i += 1) {
            const dd = start.clone().add(i, 'days').format('YYYY-MM-DD');
            if (hasDetail(dd)) days.push(dd);
        }
        return { we, days };
    };
    const weekPacketName = (d, sh) => (
        `Field Tickets WE ${moment(weekEndOf(d), 'YYYY-MM-DD').format('M.D.YY')} (${sh}).pdf`
    );
    // opts.dates builds the same packet over any set of dates — a Ticket Map
    // document's cover — titled with opts.title instead of the week ending.
    // sh 'All' summarises both crews on one cover (a SHIFT column says which
    // each person worked); opts.coverOnly leaves the day tickets off, for a
    // document whose per-shift sections follow. opts.sigMode is buildTicketPdf's.
    const buildWeekPacket = async (d, sh, opts = {}) => {
        const wk = opts.dates ? { we: opts.dates[opts.dates.length - 1], days: opts.dates } : weekDaysOf(d);
        const { we, days } = wk;
        if (!days.length) return null;
        const coverTitle = opts.title || `Week Ending ${moment(we, 'YYYY-MM-DD').format('MMMM D, YYYY')}`;
        const footerTag = opts.title || `WE ${moment(we, 'YYYY-MM-DD').format('M/D/YY')}`;

        // Each day once: the shift's crew for the matrix, plus the day's
        // drawn ticket for the packet body. A day the shift didn't work
        // simply doesn't appear.
        // Narrated a day at a time: the fetchDay behind each one is the slow
        // part — and may be queued behind the live loader's own pulls — so the
        // caller can say which day it is on instead of showing a dead button.
        const say = opts.onStep;
        const tag = opts.stepTag ? `${opts.stepTag} · ` : '';
        await prefetchDays(days, (msg, done, total) => say?.({ msg: `${tag}${msg}`, done, total }));
        const perDay = [];
        // The header's job line comes off the first day actually read here —
        // not dayViews, which is the render the build started in and misses
        // every day fetched since.
        let info0 = null;
        for (let i = 0; i < days.length; i += 1) {
            const dd = days[i];
            const md = moment(dd, 'YYYY-MM-DD').format('M/D');
            say?.({ msg: `${tag}reading ${md} hours`, done: i, total: days.length });
            const info = await fetchDay(dd, opts.src); // eslint-disable-line no-await-in-loop
            if (!info0 && info?.job) info0 = info;
            const t = narrowTicket(dayTicket(info, dd, opts.src), opts.only);
            const crew = sh === 'All' ? t.labor : t.labor.filter((p) => (sh === 'Night' ? p.night : !p.night));
            // A day nobody on this shift has hours on is not a day of this
            // cover — no empty date column (a 9/20 riding a 9/20–9/21 cover
            // on a crew that didn't work Sunday).
            if (!crew.some((p) => Number(p.total) > 0.005)) continue;
            // Days under a cover print the shift rep only — the people who
            // sign everything sign the cover once, not every page.
            if (!opts.coverOnly) say?.({ msg: `${tag}drawing the ${md} ticket`, done: i, total: days.length });
            const built = opts.coverOnly ? null : await buildTicketPdf(dd, sh, { ...opts, sigMode: 'shift' }); // eslint-disable-line no-await-in-loop
            const tk = built?.tickets?.[0] || null;
            // Equipment rides the day tickets, so the week totals below take
            // it from the day-shift pass only — a Night cover carries none.
            perDay.push({
                d: dd, crew, equipment: sh === 'Night' ? [] : (t.equipment || []),
                doc: tk?.doc || null, ticketLabels: tk?.labels || [],
            });
        }
        if (!perDay.length) return null;
        say?.({ msg: `${tag}laying out the summary page`, done: days.length, total: days.length });

        const { jsPDF } = await import('jspdf');
        const autoTableMod = await import('jspdf-autotable');
        const autoTable = autoTableMod.default || autoTableMod.autoTable;
        const logoBytes = async (file) => {
            try {
                const r = await fetch(`${process.env.PUBLIC_URL || ''}/templates/${file}`);
                return r.ok ? new Uint8Array(await r.arrayBuffer()) : null;
            } catch (x) { return null; /* logos are decoration */ }
        };
        const [strikeLogo, clientLogo] = await Promise.all([
            logoBytes('strike-logo-flat.jpeg'), logoBytes('voltagrid-logo.jpeg'),
        ]);

        // People across the week, hours per day, PD/TP/RR as day counts.
        const people = {};
        perDay.forEach((day) => {
            day.crew.forEach((p) => {
                const id = String(p.id);
                if (!people[id]) people[id] = { id: p.id, name: p.name, cls: p.cls, byDate: {}, total: 0, pd: 0, tp: 0, rr: 0, shifts: new Set() };
                const pp = people[id];
                pp.shifts.add(p.night ? 'N' : 'D');
                pp.byDate[day.d] = (pp.byDate[day.d] || 0) + p.total;
                pp.total += p.total;
                if (p.pd) pp.pd += 1;
                if (p.tp) pp.tp += 1;
                if (p.rr) pp.rr += 1;
            });
        });
        const rows = Object.values(people).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        const grand = rows.reduce((a, p) => a + p.total, 0);

        const INK = [23, 32, 42];
        const MUTE = [120, 128, 138];
        const BLUE = [21, 82, 150];
        const RED = [206, 32, 40];
        const LINE = [223, 227, 232];
        const FILL = [246, 247, 249];
        const h2 = (n) => (pNum(n) ? pNum(n).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '');

        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const W = doc.internal.pageSize.getWidth();
        const H = doc.internal.pageSize.getHeight();
        const M = 40;
        const usable = W - 2 * M;

        const isAll = sh === 'All';
        const jobLine = [`Job ${jobKey}`, jobClient(info0), jobTitle(info0)].filter(Boolean).join('   —   ');
        let y = drawPacketHeader(doc, {
            strikeLogo,
            clientLogo,
            title: 'FIELD TICKET WEEK SUMMARY',
            badge: isAll ? 'ALL SHIFTS' : `${String(sh || 'Day').toUpperCase()} SHIFT`,
            rightText: coverTitle,
            subLeft: jobLine,
            subRight: `Days included: ${perDay.map((x) => fmtShort(x.d)).join(', ')}`,
            M,
        });

        doc.setFillColor(...BLUE);
        doc.rect(M, y - 7, 3, 9, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...INK);
        doc.text('LABOR — WEEK TOTALS', M + 9, y, { charSpace: 1.2 });
        doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE);
        doc.text(`${rows.length} people — ${h2(grand)} hours`, W - M, y, { align: 'right' });
        y += 10;

        const n = perDay.length;
        const shiftTag = (p) => (p.shifts.size > 1 ? 'D+N' : [...p.shifts][0] || '');
        // The SHIFT column only exists on an All Shifts cover; everything
        // after the classification shifts one place right when it does.
        // Widths are budgeted off the real page rather than fixed, so a
        // 7-day week and a 2-day weekend both fill the sheet.
        const wsShift = isAll ? 24 : 0;
        const wsCnt = 19;
        let wsName = 104;
        let wsCls = 96;
        let dateW = n ? Math.floor((usable - (wsName + 40 + wsCls + wsShift + 3 * wsCnt + 40)) / n) : 40;
        if (dateW < 30) {
            wsName = 90; wsCls = 76;
            dateW = Math.max(24, Math.floor((usable - (wsName + 40 + wsCls + wsShift + 3 * wsCnt + 40)) / n));
        }
        dateW = Math.min(dateW, 40);
        const fixed = isAll ? 7 : 6;
        const colStyles = {
            0: { cellWidth: wsName }, 1: { cellWidth: 40, halign: 'center' }, 2: { cellWidth: wsCls },
        };
        if (isAll) colStyles[3] = { cellWidth: wsShift, halign: 'center' };
        [0, 1, 2].forEach((k) => { colStyles[fixed - 3 + k] = { cellWidth: wsCnt, halign: 'center' }; });
        perDay.forEach((x, i) => { colStyles[fixed + i] = { cellWidth: dateW, halign: 'center' }; });
        colStyles[fixed + n] = { cellWidth: 40, halign: 'center', fontStyle: 'bold' };
        autoTable(doc, {
            margin: { left: M, right: M, top: M + 8, bottom: M + 30 },
            theme: 'plain',
            styles: {
                font: 'helvetica', fontSize: 7.3, cellPadding: { top: 3.4, bottom: 3.4, left: 2, right: 2 },
                textColor: INK, lineColor: LINE, lineWidth: { top: 0.5 }, overflow: 'ellipsize', valign: 'middle',
            },
            headStyles: {
                fillColor: INK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.2,
                halign: 'center', cellPadding: { top: 4.5, bottom: 4.5, left: 2, right: 2 }, lineWidth: 0,
            },
            footStyles: {
                fillColor: FILL, textColor: INK, fontStyle: 'bold', lineColor: INK, lineWidth: { top: 1 }, fontSize: 6.6,
            },
            alternateRowStyles: { fillColor: [250, 251, 252] },
            showFoot: 'lastPage',
            startY: y,
            head: [['EMPLOYEE', 'ID', 'CLASSIFICATION', ...(isAll ? ['SHIFT'] : []), 'PD', 'TP', 'RR',
                ...perDay.map((x) => fmtShort(x.d)), 'TOTAL']],
            body: rows.map((p) => [
                p.name, p.id, p.cls, ...(isAll ? [shiftTag(p)] : []),
                p.pd ? String(p.pd) : '', p.tp ? String(p.tp) : '', p.rr ? String(p.rr) : '',
                ...perDay.map((x) => h2(p.byDate[x.d]) || ''),
                h2(p.total) || '',
            ]),
            foot: [['Totals', '', '', ...(isAll ? [''] : []),
                String(rows.reduce((a, p) => a + p.pd, 0) || ''), String(rows.reduce((a, p) => a + p.tp, 0) || ''), String(rows.reduce((a, p) => a + p.rr, 0) || ''),
                ...perDay.map((x) => h2(x.crew.reduce((a, p) => a + p.total, 0)) || ''),
                h2(grand) || '']],
            columnStyles: colStyles,
            didParseCell: (hk) => {
                if (hk.section !== 'body' && [0, 2].includes(hk.column.index)) hk.cell.styles.halign = 'left';
                // columnStyles don't reach the foot — center its numbers by hand.
                if (hk.section === 'foot' && hk.column.index >= fixed - 3) hk.cell.styles.halign = 'center';
                // Day totals like "1,344.25" overflow the narrow date columns.
                if (hk.section === 'foot' && String(hk.cell.raw || '').length > 6) {
                    hk.cell.styles.fontSize = 5.6;
                    hk.cell.styles.cellPadding = { top: 3.4, bottom: 3.4, left: 0.5, right: 0.5 };
                }
            },
        });
        y = doc.lastAutoTable.finalY + 10;
        if (y > H - 200) { doc.addPage(); y = M + 8; }
        doc.setFont('helvetica', 'normal').setFontSize(6.5).setTextColor(...MUTE);
        doc.text(`${isAll ? 'SHIFT — D day, N night, D+N both this period (see the shift summaries that follow).  ' : ''}PD / TP / RR — number of days with Per Diem / Truck Pay / Rig Pay.`, M, y);
        y += 16;

        // ── Equipment week totals ──
        // Equipment prints on the day ticket, so it belongs on the All and
        // Day covers and never on a Night one.
        const units = {};
        perDay.forEach((day) => (day.equipment || []).forEach((e) => {
            const key = String(e.unit || e.desc || 'unknown');
            const u = units[key] || (units[key] = { key, desc: e.desc, unit: e.unit, rental: e.rental, byDate: {}, total: 0 });
            u.byDate[day.d] = (u.byDate[day.d] || 0) + e.total;
            u.total += e.total;
        }));
        const unitRows = Object.values(units).sort((a, b) => String(a.desc).localeCompare(String(b.desc))
            || String(a.unit).localeCompare(String(b.unit), undefined, { numeric: true }));
        const equipHours = unitRows.reduce((a, u) => a + u.total, 0);
        if (unitRows.length) {
            if (y > H - 140) { doc.addPage(); y = M + 8; }
            y += 6;
            doc.setFillColor(...BLUE);
            doc.rect(M, y - 7, 3, 9, 'F');
            doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...INK);
            doc.text('EQUIPMENT — WEEK TOTALS', M + 9, y, { charSpace: 1.2 });
            doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE);
            doc.text(`${unitRows.length} units — ${h2(equipHours)} hours`, W - M, y, { align: 'right' });
            y += 10;
            const eDateW = Math.min(40, Math.floor((usable - (190 + 70 + 44 + 40)) / perDay.length));
            const eCols = {
                0: { cellWidth: usable - (70 + 44 + 40 + eDateW * perDay.length) },
                1: { cellWidth: 70, halign: 'center' },
                2: { cellWidth: 44, halign: 'center' },
            };
            perDay.forEach((x, i) => { eCols[3 + i] = { cellWidth: eDateW, halign: 'center' }; });
            eCols[3 + perDay.length] = { cellWidth: 40, halign: 'center', fontStyle: 'bold' };
            autoTable(doc, {
                margin: { left: M, right: M, top: M + 8, bottom: M + 30 },
                theme: 'plain',
                styles: {
                    font: 'helvetica', fontSize: 7.3, cellPadding: { top: 3.4, bottom: 3.4, left: 2, right: 2 },
                    textColor: INK, lineColor: LINE, lineWidth: { top: 0.5 }, overflow: 'ellipsize', valign: 'middle',
                },
                headStyles: {
                    fillColor: INK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.2,
                    halign: 'center', cellPadding: { top: 4.5, bottom: 4.5, left: 2, right: 2 }, lineWidth: 0,
                },
                footStyles: {
                    fillColor: FILL, textColor: INK, fontStyle: 'bold', lineColor: INK, lineWidth: { top: 1 }, fontSize: 6.6,
                },
                alternateRowStyles: { fillColor: [250, 251, 252] },
                showFoot: 'lastPage',
                startY: y,
                head: [['EQUIPMENT', 'UNIT NO', 'TYPE', ...perDay.map((x) => fmtShort(x.d)), 'TOTAL']],
                body: unitRows.map((u) => [u.desc, u.unit, u.rental ? 'Rental' : 'Fleet',
                    ...perDay.map((x) => h2(u.byDate[x.d]) || ''), h2(u.total) || '']),
                foot: [['Total Hours', '', '',
                    ...perDay.map((x) => h2(unitRows.reduce((a, u) => a + (u.byDate[x.d] || 0), 0)) || ''),
                    h2(equipHours) || '']],
                columnStyles: eCols,
                didParseCell: (hk) => {
                    if (hk.section !== 'body' && hk.column.index === 0) hk.cell.styles.halign = 'left';
                    if (hk.section === 'foot' && hk.column.index >= 1) hk.cell.styles.halign = 'center';
                },
            });
            y = doc.lastAutoTable.finalY + 16;
            if (y > H - 170) { doc.addPage(); y = M + 8; }
        }

        const stats = [
            ['WEEK HOURS', h2(grand) || '0'],
            ...(unitRows.length ? [['EQUIPMENT HOURS', h2(equipHours) || '0']] : []),
            ['CREW', String(rows.length)],
            ['DAYS', String(n)],
        ];
        doc.setFillColor(...FILL);
        doc.roundedRect(M, y, usable, 40, 4, 4, 'F');
        const step = (usable - 28) / stats.length;
        stats.forEach(([label, value], i) => {
            const x = M + 14 + i * step;
            doc.setFont('helvetica', 'bold').setFontSize(6.3).setTextColor(...MUTE);
            doc.text(label, x, y + 12, { charSpace: 0.8 });
            doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(...BLUE);
            doc.text(String(value), x, y + 28);
        });
        y += 64;

        // Signature block — one line per signer for this shift; the labels
        // are what the DocuSign tabs anchor on. The cover speaks for every
        // day it reports, so a signer is on it if their day scope covers ANY
        // of them: the weekday rep still signs the cover of a Mon-Sun packet
        // (they signed its weekday pages), but not one built from Sat+Sun.
        const slots = signerSlots(signerMap, sh, opts.sigMode, perDay.map((x) => x.d));
        slots.forEach((slot) => {
            if (y > H - M - 90) { doc.addPage(); y = M + 8; }
            doc.setDrawColor(...INK).setLineWidth(1);
            doc.line(M, y, M + 250, y);
            doc.line(M + 310, y, M + 410, y);
            doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE);
            doc.text(`${slot.label} - Signature`, M, y + 11);
            doc.text('Date', M + 310, y + 11);
            y += 40;
            doc.setLineWidth(1).line(M, y, M + 250, y);
            doc.text(`${slot.label} - Print`, M, y + 11);
            if (slot.name) doc.text(slot.name, M + 250, y + 11, { align: 'right' });
            y += 24;
        });

        const pages = doc.getNumberOfPages();
        for (let p = 1; p <= pages; p += 1) {
            doc.setPage(p);
            doc.setDrawColor(...LINE).setLineWidth(0.6);
            doc.line(M, H - 32, W - M, H - 32);
            doc.setFont('helvetica', 'normal').setFontSize(6.8).setTextColor(...MUTE);
            doc.text(`Strike — Field Ticket Week Summary (${isAll ? 'All Shifts' : `${sh} Shift`}) — Job ${jobKey} — ${footerTag}`, M, H - 22);
            doc.text(`Page ${p} of ${pages}`, W - M, H - 22, { align: 'right' });
        }

        // Cover first, then each day's ticket — one signable document.
        const { PDFDocument } = await import('pdf-lib');
        const out = await PDFDocument.load(doc.output('arraybuffer'));
        for (const day of perDay) {
            if (!day.doc) continue;
            const src = await PDFDocument.load(day.doc.output('arraybuffer')); // eslint-disable-line no-await-in-loop
            const copied = await out.copyPages(src, src.getPageIndices());     // eslint-disable-line no-await-in-loop
            copied.forEach((p) => out.addPage(p));
        }
        const bytes = await out.save();
        const labels = [...slots.map((s) => s.label),
            ...perDay.flatMap((x) => x.ticketLabels || [])];
        return { name: weekPacketName(d, sh), bytes, weekEnd: we, days: perDay.map((x) => x.d), labels: [...new Set(labels)] };
    };

    const exportWeekPdf = async (d = previewDate, sh = previewShift) => {
        try {
            const pk = await buildWeekPacket(d, sh || 'Day');
            if (!pk) { showToast(`No ${String(sh || 'day').toLowerCase()}-shift hours in this week.`, 'warn'); return; }
            const blob = new Blob([pk.bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = pk.name;
            a.click();
            URL.revokeObjectURL(url);
            showToast(`Week packet built — ${pk.days.length} day${pk.days.length > 1 ? 's' : ''}, WE ${moment(pk.weekEnd, 'YYYY-MM-DD').format('M/D/YY')}.`, 'success');
        } catch (e) {
            showToast('Week packet export failed — ' + (e?.message || e), 'error');
        }
    };

    // ── Ticket Map plumbing ──
    // The modal owns the document workflow; these are the pieces of it that
    // need this view's data — the day detail cache, the shift resolution, the
    // ticket PDF drawing and the signature pipeline.
    const openMap = (focus) => setMapOpen({ focus: focus || null });
    const detailDates = useMemo(
        () => Object.values(heetBy).filter((r) => r.In_HEET !== 0 || r.In_Actuals === 1 || r.In_HJ === 1).map((r) => r.Work_Date).sort(),
        [heetBy],
    );
    const rosterList = useMemo(
        () => (roster.data?.roster || []).filter((r) => !r.job || String(r.job) === String(jobKey)),
        [roster.data, jobKey],
    );

    // Each covered cell's ticket, exactly as its PDF would print it.
    //
    // A day's HEET pull is a slow query — up to a minute, longer once the day
    // has fallen out of the HEET window and comes from cost history — so the
    // days go two at a time rather than all at once: six in parallel used to
    // queue behind each other on the server and blow the 180s SQL timeout.
    // `onProgress` gets the tickets built so far after every day, which is
    // what lets the Ticket Map fill its rows in as they land instead of
    // sitting on “loading” for three minutes.
    const cellTickets = async (cells, onProgress) => {
        const list = sortCells(cells);
        const dates = [...new Set(list.map((k) => splitCell(k).d))].sort();
        const byDate = {};
        const build = () => {
            const out = {};
            list.forEach((k) => {
                const { d, sh } = splitCell(k);
                if (!byDate[d]) return; // not loaded yet (or failed) — the row keeps waiting
                const t = ticketFrom(rowsFor(d, byDate[d].rows), ratemap, { overrides: overridesFor(d), staffCls: clsFor(d) });
                out[k] = ticketForShift(t, sh);
            });
            return out;
        };
        const active = new Set();
        const failed = [];
        let done = 0;
        const report = () => onProgress?.({
            done, total: dates.length, active: [...active], tickets: build(), failed: failed.map((f) => ({ ...f })),
        });
        const queue = [...dates];
        const worker = async () => {
            while (queue.length) {
                const d = queue.shift();
                active.add(d);
                report();
                try {
                    byDate[d] = await fetchDay(d); // eslint-disable-line no-await-in-loop
                } catch (e) {
                    failed.push({ d, error: String(e?.message || e) });
                }
                active.delete(d);
                done += 1;
                report();
            }
        };
        await Promise.all(Array.from({ length: Math.min(DAY_LOAD_CONCURRENCY, dates.length) }, worker));
        // A SQL timeout here is contention, not a broken day: retry the ones
        // that fell over, one at a time now that nothing else is in flight.
        const retry = failed.splice(0, failed.length);
        for (const f of retry) {
            active.add(f.d);
            report();
            try {
                byDate[f.d] = await fetchDay(f.d); // eslint-disable-line no-await-in-loop
            } catch (e) {
                failed.push({ d: f.d, error: String(e?.message || e) });
            }
            active.delete(f.d);
            report();
        }
        // A missing day would silently change the validation hash, so the
        // caller still gets a failure — it just gets it with the days that
        // did load already on screen.
        if (failed.length) {
            throw new Error(`${failed.map((f) => moment(f.d, 'YYYY-MM-DD').format('M/D')).join(', ')} — ${failed[0].error}`);
        }
        return build();
    };

    // The labor QA rules (billingQa) over one day/shift against the work day
    // before it. The time sheet preview shows them; the Claude review gets
    // them as its automated checks. Each run also lands on qaByCell so the
    // day's row carries the count.
    const [qaByCell, setQaByCell] = useState({});
    useEffect(() => { setQaByCell({}); }, [jobKey]);
    const prevWorkDay = (d) => [...detailDates].reverse().find((x) => x < d);
    const dayQa = (d, sh, rows, prevD, prevRows) => {
        const sched = mergeRateSchedule(userRows);
        const rateByCls = {};
        sched.labor.forEach((r) => {
            if (r.name && pNum(r.wage) > 0) rateByCls[r.name] = { st: stRate(r.wage, sched.cfg), ot: otRate(r.wage, sched.cfg) };
        });
        const pdPolicy = settings?.pd || DEFAULT_SETTINGS.pd;
        // A code the job folds (100105 → 100101) prints, so it isn't "hidden".
        const folded = Object.keys(rules.fold || {});
        const ctx = {
            ratemap, staffCls, rateByCls, pdRate: pdPolicy.rate, pdPolicy,
            // The job's contract bills a hotel stay for someone with no per
            // diem as per diem — QA lists who worked without it.
            hotelPd: contractOn(settings, jobKey),
            ...(folded.length ? { hiddenCodes: HIDDEN_CODES.filter((c) => !folded.includes(c)) } : {}),
        };
        const night = sh === 'Night';
        const cur = personDaysFromHeet(rows, d, { ...ctx, overrides: overridesFor(d), source: dayFeeds.current[d]?.basis === 'cards' ? 'HeavyJob card' : 'HEET' }).filter((p) => (p.shift === 'Night') === night);
        const hist = prevD ? personDaysFromHeet(prevRows || [], prevD, { ...ctx, overrides: overridesFor(prevD) }) : [];
        // HEET vs the SQL HeavyJob feed only means something on a day built
        // off HEET; on a live-card day the preview's BUILT FROM panel names
        // what HEET lacks instead.
        const validation = dayFeeds.current[d]?.basis === 'cards' ? [] : (heet.validation || []).filter((v) => v.Work_Date === d);
        const areas = runChecks(cur, hist, { ...ctx, validation });
        // A shift nobody worked has nothing to tag.
        setQaByCell((m) => ({ ...m, [`${d}_${sh}`]: cur.length ? { areas, people: cur.length, prevD: hist.length ? prevD : null } : null }));
        return areas;
    };

    // The open time sheet's QA: the day is already loading for the preview;
    // the work day before it is pulled for the comparison (shared through
    // fetchDay, so a Claude review right after doesn't load it again).
    useEffect(() => {
        if (!previewDate || !previewShift || !dayInfo.rows) return undefined;
        let dead = false;
        const prevD = prevWorkDay(previewDate);
        (async () => {
            let prevRows = [];
            if (prevD) {
                try { prevRows = (await fetchDay(prevD)).rows || []; } catch (e) { /* the comparison day is context, not a requirement */ }
            }
            if (!dead) dayQa(previewDate, previewShift, dayInfo.rows, prevRows.length ? prevD : null, prevRows);
        })();
        return () => { dead = true; };
        // dayQa reads the same inputs the ticket does (roster, corrections, Staff List, rate sheet).
    }, [previewDate, previewShift, dayInfo.rows, detailDates, rosterShift, shiftOv, staffCls, cards, ratemap, userRows, settings, heet.validation]);

    // What Claude reviews for one cell: the ticket plus everything this view
    // knows about the day — roster, shift corrections, Staff List, the
    // HeavyJob-vs-HEET gaps, the labor QA rules, and the previous work day.
    const cellPayloadFor = async (k) => {
        const { d, sh } = splitCell(k);
        const info = await fetchDay(d);
        const rows = info.rows || [];
        const dayTicket = ticketFrom(rowsFor(d, rows), ratemap, { overrides: overridesFor(d), staffCls: clsFor(d) });
        const prevD = prevWorkDay(d);
        let prev = null;
        let prevRows = [];
        if (prevD) {
            try {
                prevRows = (await fetchDay(prevD)).rows || [];
                prev = { d: prevD, ticket: ticketFrom(rowsFor(prevD, prevRows), ratemap, { overrides: overridesFor(prevD), staffCls: clsFor(prevD) }) };
            } catch (e) { /* the comparison day is context, not a requirement */ }
        }
        const qa = dayQa(d, sh, rows, prev ? prevD : null, prevRows);
        const onCards = info.feed?.basis === 'cards';
        const payload = buildCellPayload({
            d, sh, ticket: ticketForShift(dayTicket, sh), dayTicket, dayRows: rows,
            dayOv: shiftOv[d] || {}, rosterShift, roster: rosterList, staffCls,
            validation: onCards ? [] : heet.validation, qa, prev,
        });
        // The same "how this was built" record the preview and the chat read.
        const ctx = dayContext(d, dayTicket, info);
        return {
            ...payload,
            context: {
                ...(payload.context || {}),
                howBuilt: {
                    hoursBasis: onCards ? 'live HeavyJob time cards (card wins over HEET)' : 'HEET / actual cost',
                    cards: (info.feed?.cards || []).map((c) => ({ foreman: c.foreman, shift: c.shift, people: c.people, hours: c.hours, approved: c.isApproved, lastModified: c.lastModified })),
                    notes: ctx.notes,
                },
            },
        };
    };

    // The signature packet: an All Shifts summary first when the document
    // covers both crews, then per shift a summary cover + each day's ticket
    // when it spans days (just the ticket for one day), then the backup —
    // only the pages Claude's page map put on these days/shifts, turned
    // upright. Pages the map doesn't place ride along rather than vanish.
    // Every signature line it prints is reported back in `labels`: the
    // envelope's anchors have to be lines the packet really has.
    // onStep({ msg, done, total }) narrates the build — see the Ticket Map's
    // progress line. A packet over a full week is several minutes of day
    // pulls, ticket drawing and backup binding, so silence reads as a hang.
    // opts.part picks what lands in the PDF: 'all' (the whole document, for
    // the approver's preview), 'day' / 'night' (one crew's cover + that
    // crew's tickets + its backup) or 'summary' (the covers on their own).
    // The last three are the Ticket Map's three sends — the client signs a
    // crew's week in one go rather than a 130-page packet of everything, and
    // each goes out as its own envelope. 'tickets' is the old
    // everything-but-the-covers export, kept for callers that still ask.
    const buildDocPacket = async (doc, byCell = {}, onStep, opts = {}) => {
        const part = opts.part || 'all';
        // The document's source choices ride every day pull and draw below,
        // and are reported back per day in `sources` for the manifest.
        const src = normalizeSrc(doc.src);
        // A supplement prints its named people only — nobody else, no
        // equipment — so the client never signs the same hours twice.
        const only = isSupplementDoc(doc) ? (doc.only || []) : null;
        if (only && !only.length) throw new Error('nobody is picked to be on this supplement yet');
        const shiftOnly = part === 'day' ? 'Day' : part === 'night' ? 'Night' : null;
        const wantTickets = part !== 'summary';
        const say = (msg, done = 0, total = 0) => { try { onStep?.({ msg, done, total }); } catch (e) { /* progress is decoration */ } };
        say('starting the packet');
        const { PDFDocument, StandardFonts, degrees, rgb } = await import('pdf-lib');
        const out = await PDFDocument.create();
        const labels = new Set();
        const append = async (bytes) => {
            const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
            (await out.copyPages(src, src.getPageIndices())).forEach((p) => out.addPage(p));
        };
        const byShift = SHIFTS.map((sh) => ({
            sh,
            cells: doc.cells.filter((k) => splitCell(k).sh === sh),
        })).filter((x) => x.cells.length && (!shiftOnly || x.sh === shiftOnly));
        if (!byShift.length) throw new Error(`this ticket has no ${String(shiftOnly || '').toLowerCase()} shift on it`);
        // Both crews on one document: one All Shifts summary carries the
        // people who sign everything, and every page under it prints only
        // its own shift rep.
        const bothShifts = byShift.length > 1;
        const cells = byShift.flatMap((x) => x.cells);
        const dates = [...new Set(cells.map((k) => splitCell(k).d))].sort();
        // Every summary this document will carry — All Shifts first when both
        // crews are on it, then each crew's own for the days it spans. One
        // crew's send carries that crew's cover in full signature mode: with
        // no All Shifts cover above it, the sign-everything people sign it
        // once there rather than on every day page.
        const covers = part === 'tickets'
            ? []
            : [
                ...(bothShifts ? [{ sh: 'All', cells, sigMode: undefined, tag: 'Summary' }] : []),
                ...byShift
                    .filter((x) => x.cells.length > 1)
                    .map((x) => ({ sh: x.sh, cells: x.cells, sigMode: bothShifts ? 'shift' : undefined, tag: x.sh })),
            ];
        // With a summary above them the day pages print the shift rep only —
        // the sign-everything people sign the cover once. On their own (the
        // Day tickets button, or a single day) each page carries every signer.
        const ticketSig = covers.length ? 'shift' : undefined;

        // Every day this packet touches, pulled together before anything is
        // drawn — the covers and the tickets below then read the same cache.
        await prefetchDays(dates, say);

        for (const c of covers) {
            const cDates = [...new Set(c.cells.map((k) => splitCell(k).d))].sort();
            const pk = await buildWeekPacket(cDates[0], c.sh, { // eslint-disable-line no-await-in-loop
                dates: cDates, title: `${docName(c.cells)}${only ? ' Supplement' : ''}`, coverOnly: true, sigMode: c.sigMode, onStep, stepTag: c.tag, src, only,
            });
            if (pk) {
                await append(pk.bytes); // eslint-disable-line no-await-in-loop
                (pk.labels || []).forEach((l) => labels.add(l));
            }
        }

        // Then the tickets, a DATE at a time with that date's crews together
        // — the order the field ticket packet script files them in, so a day
        // and its night ride as one spread instead of 60 pages apart.
        // A cell that is ON the document and prints nothing is never normal —
        // somebody put that day on the ticket. It is how a broken shift split
        // shows up (a whole crew on the other sheet empties this one), so it is
        // named to the user instead of quietly shortening a client document.
        const empty = [];
        if (wantTickets) {
            for (let i = 0; i < dates.length; i += 1) {
                const dd = dates[i];
                const md = moment(dd, 'YYYY-MM-DD').format('M/D');
                for (const { sh, cells } of byShift) {
                    if (!cells.includes(cellKey(dd, sh))) continue;
                    say(`${sh} · drawing the ${md} ticket`, i, dates.length);
                    const tk = (await buildTicketPdf(dd, sh, { sigMode: ticketSig, src, only }))?.tickets?.[0]; // eslint-disable-line no-await-in-loop
                    if (tk) {
                        await append(tk.doc.output('arraybuffer')); // eslint-disable-line no-await-in-loop
                        (tk.labels || []).forEach((l) => labels.add(l));
                    } else {
                        empty.push(`${md} ${sh}`);
                    }
                }
            }
        }
        if (!out.getPageCount()) throw new Error('none of these days/shifts have hours to ticket');
        if (empty.length) {
            showToast(`Left off — no ${empty.length === 1 ? 'crew' : 'crews'} on ${empty.join(', ')}. `
                + 'If those shifts worked, the day/night split is wrong: check the Shift column before sending.', 'error');
        }

        const pageMap = {};
        Object.values(byCell || {}).forEach((r) => (r?.result?.pages || []).forEach((p) => {
            const m = (pageMap[p.fileId] = pageMap[p.fileId] || {});
            if (!m[p.page]) m[p.page] = p;
        }));
        const want = new Set(cells);
        const belongs = (p) => {
            // 'all' binds every page as scanned, page map or not.
            if (src.backup === 'all') return true;
            if (!p || !/^\d{4}-\d{2}-\d{2}$/.test(String(p.date || ''))) return true;
            const shifts = SHIFTS.includes(p.shift) ? [p.shift] : SHIFTS;
            return shifts.some((s) => want.has(cellKey(p.date, s)));
        };
        // ── Backup pages ──
        // A scan is the crew's own signed record of the day, so the client's
        // shift rep signs it too: the scan is laid on a letter page with a
        // band under it carrying that rep's line (both reps when one sheet
        // holds both crews) and a stamp naming the file. The people who sign
        // everything are not here — they sign the summary.
        const PW = 612;
        const PH = 792;
        const font = await out.embedFont(StandardFonts.Helvetica);
        const INKC = rgb(23 / 255, 32 / 255, 42 / 255);
        const MUTEC = rgb(120 / 255, 128 / 255, 138 / 255);
        const docShifts = byShift.map((x) => x.sh);
        // Which crews a page covers: the page map's own shift when it named
        // one, else every shift this document covers that date.
        const slotsFor = (p) => {
            let shifts = docShifts;
            if (p && /^\d{4}-\d{2}-\d{2}$/.test(String(p.date || ''))) {
                const onDate = docShifts.filter((s) => want.has(cellKey(p.date, s)));
                shifts = SHIFTS.includes(p.shift) && onDate.includes(p.shift) ? [p.shift] : onDate;
            }
            const on = p && /^\d{4}-\d{2}-\d{2}$/.test(String(p.date || '')) ? p.date : null;
            return shifts.flatMap((s) => signerSlots(signerMap, s, 'shift', on));
        };
        // Stacked, one per row: DocuSign puts the date box a fixed distance
        // right of the label, so two lines can't share a row.
        const drawBand = (page, slots, stampText) => {
            slots.forEach((slot, i) => {
                const y = 60 + 40 * (slots.length - 1 - i);
                page.drawLine({ start: { x: 40, y }, end: { x: 290, y }, thickness: 1, color: INKC });
                page.drawLine({ start: { x: 350, y }, end: { x: 450, y }, thickness: 1, color: INKC });
                page.drawText(`${slot.label} - Signature`, { x: 40, y: y - 12, size: 7.5, font, color: MUTEC });
                page.drawText('Date', { x: 350, y: y - 12, size: 7.5, font, color: MUTEC });
                if (slot.name) page.drawText(slot.name, { x: 40, y: y - 23, size: 8, font, color: INKC });
            });
            page.drawText(stampText, { x: 12, y: PH - 12, size: 6.5, font, color: rgb(0.28, 0.33, 0.41) });
        };
        const bandFor = (slots) => (slots.length ? 48 + 40 * slots.length : 28);
        // The scan, scaled to the space above the band and turned upright.
        const placeScan = ({ ep, img }, rot, slots, stampText) => {
            const band = bandFor(slots);
            const w = ep ? ep.width : img.width;
            const h = ep ? ep.height : img.height;
            const [vw, vh] = rot % 180 ? [h, w] : [w, h];
            const sc = Math.min((PW - 40) / vw, (PH - 28 - band - 12) / vh, ep ? Infinity : 1);
            const dw = vw * sc;
            const dh = vh * sc;
            const cx = (PW - dw) / 2;
            const cy = band + (PH - 28 - band - dh) / 2;
            const page = out.addPage([PW, PH]);
            const size = { width: w * sc, height: h * sc };
            const at = rot === 90 ? { x: cx, y: cy + dh, rotate: degrees(-90) }
                : rot === 180 ? { x: cx + dw, y: cy + dh, rotate: degrees(180) }
                    : rot === 270 ? { x: cx + dw, y: cy, rotate: degrees(90) }
                        : { x: cx, y: cy };
            if (ep) page.drawPage(ep, { ...size, ...at });
            else page.drawImage(img, { ...size, ...at });
            drawBand(page, slots, stampText);
            slots.forEach((s) => labels.add(s.label));
        };
        const skipped = [];
        // Backup belongs to the day pages it backs up — a summaries-only PDF
        // carries none.
        const backupFiles = wantTickets ? doc.files : [];
        // The scans come down from SharePoint together rather than one after
        // another; they are bound below in the document's own order, so what
        // the client reads is unchanged. A download that fails is carried as
        // its error and reported as a skipped file, same as before.
        const DL_CONCURRENCY = 4;
        const downloads = new Array(backupFiles.length);
        if (backupFiles.length) {
            let got = 0;
            const queue = backupFiles.map((f, i) => ({ f, i }));
            say(`downloading ${backupFiles.length} backup file${backupFiles.length === 1 ? '' : 's'}`, 0, backupFiles.length);
            const worker = async () => {
                while (queue.length) {
                    const { f, i } = queue.shift();
                    try {
                        const r = await fetch(tcFileUrl(f)); // eslint-disable-line no-await-in-loop
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        downloads[i] = { bytes: new Uint8Array(await r.arrayBuffer()) }; // eslint-disable-line no-await-in-loop
                    } catch (e) {
                        downloads[i] = { error: e };
                    }
                    got += 1;
                    say(`downloading backup — ${got} of ${backupFiles.length}`, got, backupFiles.length);
                }
            };
            await Promise.all(Array.from({ length: Math.min(DL_CONCURRENCY, backupFiles.length) }, worker));
        }
        for (let fi = 0; fi < backupFiles.length; fi += 1) {
            const f = backupFiles[fi];
            try {
                say(`adding backup — ${f.name}`, fi, backupFiles.length);
                if (downloads[fi]?.error) throw downloads[fi].error;
                const { bytes } = downloads[fi];
                const head = String.fromCharCode(...bytes.subarray(0, 4));
                const map = pageMap[f.id] || {};
                if (head === '%PDF') {
                    const src = await PDFDocument.load(bytes, { ignoreEncryption: true }); // eslint-disable-line no-await-in-loop
                    const idx = src.getPageIndices().filter((i) => belongs(map[i + 1]));
                    if (!idx.length) { skipped.push(`${f.name} (no pages for these days)`); continue; }
                    const srcPages = src.getPages();
                    const embedded = await out.embedPdf(src, idx); // eslint-disable-line no-await-in-loop
                    embedded.forEach((ep, j) => {
                        const p = map[idx[j] + 1];
                        const rot0 = ((srcPages[idx[j]].getRotation().angle % 360) + 360) % 360;
                        const rot = (rot0 + (Number(p?.rotation) || 0)) % 360;
                        placeScan({ ep }, rot, slotsFor(p), `Backup — ${f.name} · page ${idx[j] + 1} of ${srcPages.length}`);
                    });
                } else if ((bytes[0] === 0xFF && bytes[1] === 0xD8) || head.slice(1) === 'PNG') {
                    const img = head.slice(1) === 'PNG' ? await out.embedPng(bytes) : await out.embedJpg(bytes); // eslint-disable-line no-await-in-loop
                    const p = map[1];
                    placeScan({ img }, ((Number(p?.rotation) || 0) % 360 + 360) % 360, slotsFor(p), `Backup — ${f.name}`);
                } else {
                    skipped.push(`${f.name} (not a PDF or image)`);
                }
            } catch (e) {
                skipped.push(`${f.name} (${e?.message || e})`);
            }
        }
        say('writing the PDF', backupFiles.length, backupFiles.length);
        const pages = out.getPageCount();
        const bytes = await out.save();
        // Where each day's hours, split, classes and equipment came from —
        // read after the build so a card that landed mid-way is what's named.
        const sources = Object.fromEntries(dates.map((dd) => [dd, daySources(dd, src)]));
        // The narrow parts say so in the file name — three PDFs of the same
        // week in one Downloads folder have to be tellable apart.
        // A crew's send names itself off its own cells, so it reads "… Day"
        // rather than the document's "Day+Night".
        const base = docFileName(cells, jobKey, { supplement: Boolean(only) });
        const name = part === 'all' || part === 'day' || part === 'night' ? base
            : base.replace(/\.pdf$/i, `${part === 'summary' ? ' Week Summary' : ' Day Tickets'}.pdf`);
        return { name, bytes, skipped, labels: [...labels], pages, sources, src, empty };
    };

    // ── Field Ticket Reconciliation ──
    // What HeavyJob said when a ticket went out, written beside the hours the
    // ticket printed (PC.Field_Ticket_Reconciliation — billingRecon.js). The
    // cards stay editable after the client signs and the invoice bills JDE,
    // which posts whatever the card says at payroll time; the Billing tab's
    // Reconciliation view reads these rows back against the cards now and the
    // JDE lines. The day pulls and tickets are the ones the packet was just
    // built from (dayCache), so the snapshot is exactly what went out. The
    // HeavyJob side takes the job rules (fold, names) but not the reviewed
    // time card — ticket vs HeavyJob on a fresh snapshot IS the review.
    const captureRecon = async (doc, part, meta = {}) => {
        const src = normalizeSrc(doc.src);
        const cells = part === 'all' ? sortCells(doc.cells) : partCells(doc.cells, part);
        const shiftsOn = {};
        cells.forEach((k) => {
            const { d, sh } = splitCell(k);
            (shiftsOn[d] = shiftsOn[d] || []).push(sh);
        });
        const R = live.current.rules || {};
        // A supplement's snapshot is its named people against their own
        // HeavyJob lines; the rest of the day was recorded with its ticket.
        const only = isSupplementDoc(doc) ? (doc.only || []) : null;
        const ids = only ? onlyIds(doc) : null;
        const rows = [];
        for (const d of Object.keys(shiftsOn).sort()) {
            const raw = await fetchDayRaw(d); // eslint-disable-line no-await-in-loop
            const info = await fetchDay(d, src); // eslint-disable-line no-await-in-loop
            const hjAll = foldRows(applyPeople(raw.rows || [], R.people), R.fold).rows;
            rows.push(...reconRowsForDay({
                date: d,
                shifts: shiftsOn[d],
                ticket: narrowTicket(dayTicket(info, d, src), only),
                hjRows: ids ? hjAll.filter((r) => ids.has(String(r.Staff_ID || ''))) : hjAll,
                cards: raw.feed?.cards || [],
                basis: raw.feed?.basis || null,
            }));
        }
        if (!rows.length) throw new Error('none of these days/shifts have hours to record');
        const res = await fieldTicketReconCapture({
            job: String(jobKey),
            docId: part === 'all' ? doc.id : partDocId(doc.id, part),
            part,
            envelopeRowId: meta.envelopeRowId || null,
            source: meta.source || 'send',
            // A crew send is the daily ticket, the week summary send the
            // weekly one; Record reads the rest off the envelopes later.
            daily: part === 'summary' ? null : meta.status || null,
            weekly: part === 'summary' ? meta.status || null : 'Pending',
            by: userName || userEmail || '',
            rows,
        });
        if (!res) throw new Error('no response from the server (is fieldTicketReconciliation deployed here?)');
        if (res.error) throw new Error(res.error);
        return { ...res, rows: rows.length };
    };
    // The Ticket Map's "Record HJ snapshot": a ticket that went out before
    // the table existed, or on paper, recorded against the cards as they
    // stand now and tied to its envelope when it has one.
    const recordRecon = async (doc, part) => {
        const env = sigByDoc[part === 'all' ? doc.id : partDocId(doc.id, part)] || null;
        return captureRecon(doc, part, {
            source: 'manual',
            envelopeRowId: env?.RowID || null,
            status: envStatus(env) || 'Draft',
        });
    };

    // One envelope per send — the day crew's tickets, the night crew's, and
    // the week summary — keyed by the part's own docId (partDocId) and dated
    // the last day that part covers, so it files into that week.
    const sendDocPacket = async (doc, form, pk) => {
        if (!sigs.configured) throw new Error('DocuSign is not configured on this environment yet');
        const part = form.part || 'all';
        const cells = part === 'all' ? sortCells(doc.cells) : partCells(doc.cells, part);
        const dates = cells.map((k) => splitCell(k).d).sort();
        // A test send goes to me alone under a docId no document owns — see
        // testSigners in billingTicketDocs.js.
        const test = form.test === true;
        // The signer map decides who signs; the form's name/email only carry
        // a job that has no map yet.
        const mapped = test
            ? testSigners(settings, cells, { name: userName, email: userEmail })
            : Array.isArray(form.signers) && form.signers.length ? form.signers : docSigners(settings, cells);
        if (test && !mapped.length) throw new Error('your login has no email to send the test to');
        // Only the lines the packet really printed: a document can cover a
        // shift that had no hours, and DocuSign refuses the whole envelope
        // when one anchor string is missing from the document.
        const signers = signersForLabels(mapped, pk.labels);
        if (!signers.length) {
            throw new Error(mapped.length
                ? `this packet has no signature line for ${mapped.map((x) => x.name || x.email).join(', ')} — check the job's signer map`
                : 'nobody is mapped to sign this packet');
        }
        const res = await sendBillingSignature({
            job: String(jobKey),
            workDate: dates[dates.length - 1],
            shift: 'Doc',
            docId: test ? testDocId(partDocId(doc.id, part)) : partDocId(doc.id, part),
            signers,
            signerName: test ? '' : String(form.name || '').trim(),
            signerEmail: test ? '' : String(form.email || '').trim(),
            cc: test ? [] : String(form.cc || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean),
            subject: test ? `[TEST] ${form.subject || ''}`.slice(0, 100) : form.subject,
            message: form.message,
            documentName: test ? `TEST - ${pk.name}` : pk.name,
            documentBase64: toBase64(pk.bytes),
            sentBy: userName,
            sentByEmail: userEmail,
            draft: form.draft === true,
        });
        if (res?.error) throw new Error(res.error);
        loadSigs(jobKey);
        // The envelope is out; a snapshot that fails to save is said, not
        // thrown — the Ticket Map's Record HJ snapshot can write it after.
        // A week summary on its own takes none: its days' snapshots are
        // their tickets', and a newer one would stand in for them.
        if (!test && !isSummaryDoc(doc)) {
            try {
                await captureRecon(doc, part, {
                    source: form.draft === true ? 'draft' : 'send',
                    envelopeRowId: res?.rowId || null,
                    status: form.draft === true ? 'Draft' : 'Sent',
                });
            } catch (e) {
                showToast(`Sent — but the reconciliation snapshot did not save (${e?.message || e}). Use Record HJ snapshot on the Ticket Map.`, 'warn');
            }
        }
        // Who it really went to — the caller's own list may name somebody the
        // packet had no line for, and that person is not on the envelope —
        // and the envelope it went out under, for the packet archive.
        return { signers, rowId: res?.rowId || null, envelopeId: res?.envelopeId || null, status: res?.status || '' };
    };

    // ── Force Report (PDF) ──
    // Drawn to read like the client's own VoltaGrid MP Report page — the same
    // layout the Excel export fills and the preview mirrors: green craft
    // bands side by side, vertical column headings, gold TOTAL WORKING
    // column, the Summary of Total Employees block and the signature lines.
    // Deliberately no dollars — this one goes to the client.
    const exportForce = async (d) => {
        try {
            const info = await fetchDay(d);
            const groups = rollUp(peopleFrom(info.rows || [], ratemap));
            if (!groups.length) { showToast('No HeavyJob hours to report for this day.', 'warn'); return; }

            const heads = {};
            let unclassified = 0;
            groups.forEach((g) => {
                if (g.key === 'unclassified') unclassified += g.head;
                else heads[g.key] = g.head;
            });
            const direct = TIER3.filter((t) => t.dir === 'Direct');
            const indirect = TIER3.filter((t) => t.dir === 'Indirect');
            const headOf = (t) => heads[String(t.code)] || 0;
            const dTot = direct.reduce((a, t) => a + headOf(t), 0);
            const iTot = indirect.reduce((a, t) => a + headOf(t), 0);
            const dash = (n) => (n ? String(n) : '-');

            const { jsPDF } = await import('jspdf');
            const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
            const W = doc.internal.pageSize.getWidth();
            const M = 36;
            const GREEN = [141, 198, 63];
            const GOLD = [229, 209, 92];
            const RED = [192, 0, 0];
            const LINE = [127, 127, 127];

            // Masthead — the client's logo where their sheet carries it.
            try {
                const lr = await fetch(`${process.env.PUBLIC_URL || ''}/templates/voltagrid-logo.png`);
                if (lr.ok) {
                    const b = await lr.arrayBuffer();
                    doc.addImage(new Uint8Array(b), 'PNG', M, M - 6, 108, 32);
                }
            } catch (x) { /* logo is decoration — the report stands without it */ }
            doc.setFont('helvetica', 'bold').setFontSize(15).setTextColor(0);
            doc.text('Daily Force Report', W / 2, M + 12, { align: 'center' });
            doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor('#64748b');
            doc.text(`Run time===>  ${moment().format('M/D/YYYY H:mm')}`, W - M, M - 2, { align: 'right' });

            const lineAt = (x1, x2, y0) => { doc.setDrawColor(0).setLineWidth(0.9).line(x1, y0, x2, y0); };
            let y = M + 40;
            doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(0);
            doc.text('Contractor', M, y);
            doc.text('Strike', M + 170, y, { align: 'center' });
            lineAt(M + 95, M + 245, y + 3);
            doc.text('Project Name:', W - M - 260, y);
            doc.text(String(jobTitle(info)), W - M - 90, y, { align: 'center' });
            lineAt(W - M - 180, W - M, y + 3);
            y += 22;
            doc.text('LOCATION:', M, y);
            lineAt(M + 62, M + 220, y + 3);
            doc.text('SHIFT:', W / 2 - 80, y);
            doc.text(SHIFT_DEFAULT, W / 2 + 10, y, { align: 'center' });
            lineAt(W / 2 - 40, W / 2 + 60, y + 3);
            doc.text('DATE:', W - M - 160, y);
            doc.text(moment(d, 'YYYY-MM-DD').format('MM/DD/YY'), W - M - 55, y, { align: 'center' });
            lineAt(W - M - 110, W - M, y + 3);
            y += 16;

            // One craft band: green title cell + 7 vertical headings, a row
            // per craft, the gold TOTAL row closing it. Returns nothing —
            // both bands share the fixed row grid so they stay in step.
            const CW = { craft: 130, n: 24, tot: 30 };
            const bandW = CW.craft + CW.n * 3 + CW.tot + CW.n * 3;
            const HDR_H = 74;
            const ROW_H = 15;
            const cols = (x0) => {
                const xs = [x0, x0 + CW.craft];
                [CW.n, CW.n, CW.n, CW.tot, CW.n, CW.n, CW.n].forEach((w) => xs.push(xs[xs.length - 1] + w));
                return xs;
            };
            const HEADS = ['Job Site', 'Fab Shop', 'Other', 'TOTAL WORKING', 'ABSENT', 'HIRED', 'TERMINATED/ CUT-BACK'];
            const band = (x0, title, rows, totalLabel, total) => {
                const xs = cols(x0);
                // Header
                doc.setFillColor(...GREEN);
                doc.rect(xs[0], y, CW.craft, HDR_H, 'F');
                doc.setFillColor(...GOLD);
                doc.rect(xs[4], y, CW.tot, HDR_H, 'F');
                doc.setDrawColor(...LINE).setLineWidth(0.5);
                for (let i = 0; i < xs.length - 1; i += 1) doc.rect(xs[i], y, xs[i + 1] - xs[i], HDR_H);
                doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(0);
                doc.text(title, xs[0] + CW.craft / 2, y + HDR_H / 2 + 3, { align: 'center' });
                doc.setFontSize(6.2);
                HEADS.forEach((h, i) => {
                    doc.setTextColor(...(h === 'ABSENT' ? RED : [0, 0, 0]));
                    const cx = xs[i + 1] + (xs[i + 2] - xs[i + 1]) / 2 + 2;
                    doc.text(h, cx, y + HDR_H - 5, { angle: 90 });
                });
                // Rows
                let ry = y + HDR_H;
                doc.setFontSize(8).setTextColor(0);
                rows.forEach(([label, n]) => {
                    doc.setFillColor(...GOLD);
                    doc.rect(xs[4], ry, CW.tot, ROW_H, 'F');
                    doc.setDrawColor(...LINE).setLineWidth(0.5);
                    for (let i = 0; i < xs.length - 1; i += 1) doc.rect(xs[i], ry, xs[i + 1] - xs[i], ROW_H);
                    doc.setFont('helvetica', 'normal');
                    doc.text(label, xs[0] + 4, ry + ROW_H - 4);
                    const vals = [dash(n), '-', '-', dash(n), '-', '-', '-'];
                    vals.forEach((v, i) => {
                        doc.setTextColor(...(i === 4 ? RED : [0, 0, 0]));
                        doc.text(v, xs[i + 1] + (xs[i + 2] - xs[i + 1]) / 2, ry + ROW_H - 4, { align: 'center' });
                    });
                    doc.setTextColor(0);
                    ry += ROW_H;
                });
                // TOTAL row
                doc.setFillColor(...GOLD);
                doc.rect(xs[0], ry, bandW, ROW_H, 'F');
                doc.setDrawColor(...LINE).setLineWidth(0.5);
                for (let i = 0; i < xs.length - 1; i += 1) doc.rect(xs[i], ry, xs[i + 1] - xs[i], ROW_H);
                doc.setFont('helvetica', 'bold');
                doc.text(totalLabel, xs[0] + 4, ry + ROW_H - 4);
                const tvals = [dash(total), '-', '-', dash(total), '-', '-', '-'];
                tvals.forEach((v, i) => {
                    doc.setTextColor(...(i === 4 ? RED : [0, 0, 0]));
                    doc.text(v, xs[i + 1] + (xs[i + 2] - xs[i + 1]) / 2, ry + ROW_H - 4, { align: 'center' });
                });
                doc.setTextColor(0);
                return ry + ROW_H;
            };

            const dRows = direct.map((t) => [t.name, headOf(t)]).concat([['Sub-Contractor Name', 0]]);
            const iRows = indirect.map((t) => [t.name, headOf(t)]);
            const gap = W - 2 * M - 2 * bandW;
            const bandBottom = band(M, 'DIRECT CRAFT', dRows, 'TOTAL DIRECT', dTot);
            band(M + bandW + gap, 'INDIRECT CRAFT', iRows, 'TOTAL INDIRECT', iTot);
            y = bandBottom + 18;

            // Summary of Total Employees, bottom right like the sheet.
            const SX = W - M - 250;
            const sumRow = (label, v, fill, big) => {
                doc.setDrawColor(...LINE).setLineWidth(0.5);
                if (fill) { doc.setFillColor(...fill); doc.rect(SX + 160, y, 90, 14, 'F'); }
                doc.rect(SX, y, 160, 14);
                doc.rect(SX + 160, y, 90, 14);
                doc.setFont('helvetica', big ? 'bold' : 'normal').setFontSize(8.5).setTextColor(0);
                doc.text(label, SX + 4, y + 10);
                doc.setFont('helvetica', 'bold');
                doc.text(String(v), SX + 205, y + 10, { align: 'center' });
                y += 14;
            };
            doc.setFont('helvetica', 'bold').setFontSize(9);
            doc.text('SUMMARY OF TOTAL EMPLOYEES', SX + 125, y + 4, { align: 'center' });
            y += 10;
            sumRow('Total Working DIRECT', dTot, GREEN);
            sumRow('Total Working IN-DIRECT', iTot);
            sumRow('Total Absent', 0);
            sumRow('Total Hired', 0);
            sumRow('Total Terminated or Cut-Back', 0);
            sumRow('Total', dTot + iTot, null, true);

            // Signature block — same labels the DocuSign anchors use.
            y += 26;
            doc.setDrawColor(0).setLineWidth(1.2);
            doc.line(M, y, M + 260, y);
            doc.line(M + 300, y, M + 380, y);
            doc.setFont('helvetica', 'normal').setFontSize(8.5);
            doc.text('Customer Representative - Signature', M, y + 11);
            doc.text('Date', M + 300, y + 11);
            y += 34;
            doc.setLineWidth(1.2).line(M, y, M + 260, y);
            doc.text('Customer Representative - Print', M, y + 11);

            const warn = forceUnclassifiedWarning(unclassified);
            if (warn) {
                doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor('#b45309');
                doc.text(warn, M, y + 30);
                showToast(warn, 'warn');
            }

            doc.save(`Daily Force Report ${moment(d, 'YYYY-MM-DD').format('M.D.YY')}.pdf`);
        } catch (e) {
            showToast('Force Report export failed — ' + (e?.message || e), 'error');
        }
    };

    // Filed to the job site's Timecards folder under project controls'
    // Month/WE<M.D.YY> convention, named the way the per-day tickets there
    // are - the DocuSign link gets sent against this filed copy.
    const saveTicketToSharePoint = async () => {
        if (savingTicket) return;
        try {
            const f = await buildTicket();
            if (!f) { showToast('No HeavyJob hours to ticket for this day.', 'warn'); return; }
            const over = ticketOverflowWarning(f.overflow);
            if (over) showToast(over, 'warn');
            setSavingTicket(true);
            const res = await saveJobTicketFile({
                job: String(jobKey), date: previewDate, name: ticketName(), contentBase64: toBase64(f.buffer),
            });
            if (res?.error || !res?.saved) throw new Error(res?.error || 'save failed');
            showToast(`Saved to SharePoint - ${res.path}`, 'success');
            syncDocs(jobKey);
        } catch (e) {
            showToast('SharePoint save failed - ' + (e?.message || e), 'error');
        } finally {
            setSavingTicket(false);
        }
    };

    // ── Force Report file — the client's own workbook, like the ticket ──
    const forceName = (d = previewDate) => `Daily Force Report ${moment(d, 'YYYY-MM-DD').format('M.D.YY')}.xlsx`;
    // The Equipment sheet comes off project controls' PM tracker, read once
    // per job. A job with no tracker, or a failed read, gets the report
    // without the sheet and a note saying so — never a blocked send.
    const equipLogFetch = useRef(null);
    const fetchEquipLog = () => {
        const k = String(jobKey || '');
        if (equipLogFetch.current?.k !== k) {
            equipLogFetch.current = {
                k,
                p: Promise.resolve().then(() => loadEquipmentLog({ job: k }))
                    .then((res) => ({ data: res || null, error: res ? null : 'no response from the server' }))
                    .catch((e) => {
                        equipLogFetch.current = null; // try again next time
                        return { data: null, error: String(e?.message || e) };
                    }),
            };
        }
        return equipLogFetch.current.p;
    };
    const buildForce = async (d = previewDate) => {
        const [info, log] = await Promise.all([fetchDay(d), fetchEquipLog()]);
        const eq = log.data?.found ? equipLogRollup(log.data.rows, d, { equipAs: equipRowAs(jobKey) }) : null;
        const f = await buildForceReportFile({
            rows: info.rows || [],
            ratemap,
            date: d,
            jobKey,
            jobName: jobTitle(info),
            client: jobClient(info),
            shift: SHIFT_DEFAULT,
            equipment: eq?.items,
        });
        if (!f) return f;
        const equipNote = log.error ? `Equipment log could not be read (${log.error}) - the report has no Equipment sheet.`
            : !log.data?.found ? 'No PM Tracker equipment log on the job site - the report has no Equipment sheet.'
                : equipUnmappedWarning(eq.unmapped);
        return { ...f, equipNote };
    };
    const exportForceExcel = async (d) => {
        try {
            const f = await buildForce(d);
            if (!f) { showToast('No HeavyJob hours to report for this day.', 'warn'); return; }
            const warn = forceUnclassifiedWarning(f.unclassified);
            if (warn) showToast(warn, 'warn');
            if (f.equipNote) showToast(f.equipNote, 'warn');
            const blob = new Blob([f.buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = forceName(d);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            showToast('Force Report export failed — ' + (e?.message || e), 'error');
        }
    };

    // ── Sending the Force Report out ──
    // The Daily Force Report is emailed straight to the client (workbook
    // attached, no signature request), to recipients that default from the
    // job's billing settings. Opening the dialog pulls the day's HeavyJob
    // detail if it is not cached yet, because the file cannot be built
    // without it. Signature sends are not here: time sheets go out as Ticket
    // Map documents, which have to pass the Claude check first.
    const openSend = (d, sh, sheet, mv, kind = 'Force') => {
        if (versionLocked || kind !== 'Force') return;
        loadDay(d);
        setSendPrompt({ d, sh, sheet, mv, kind });
        setSendForm({
            to: String(settings?.forceTo || settings?.signerEmail || ''),
            cc: String(settings?.forceCc || settings?.signerCc || ''),
            subject: `Daily Force Report ${moment(d, 'YYYY-MM-DD').format('M/D/YY')} — Job ${jobKey}`,
            message: `Attached is the Daily Force Report for `
                + `${moment(d, 'YYYY-MM-DD').format('dddd, MMMM D, YYYY')}.`,
        });
    };

    const submitSend = async () => {
        if (sending || !sendPrompt || !sendForm || sendPrompt.kind !== 'Force') return;
        const { d } = sendPrompt;

        const to = String(sendForm.to || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean);
        const cc = String(sendForm.cc || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean);
        if (!to.length) {
            showToast('At least one recipient email is required.', 'warn');
            return;
        }
        const bad = [...to, ...cc].filter((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));
        if (bad.length) {
            showToast(`Not a valid email address: ${bad.join(', ')}`, 'warn');
            return;
        }
        setSending(true);
        try {
            const f = await buildForce(d);
            if (!f) throw new Error('no HeavyJob hours to report for this day');
            const over = forceUnclassifiedWarning(f.unclassified);
            if (over) showToast(over, 'warn');
            if (f.equipNote) showToast(f.equipNote, 'warn');
            const res = await sendForceReportEmail({
                job: String(jobKey),
                workDate: d,
                to,
                cc,
                subject: sendForm.subject,
                message: sendForm.message,
                filename: forceName(d),
                contentBase64: toBase64(f.buffer),
                sentBy: userName,
                sentByEmail: userEmail,
            });
            if (res?.error) throw new Error(res.error);
            recordForceSend(d, to.join(', '));
            showToast(`Emailed to ${to.join(', ')}.`, 'success');
            setSendPrompt(null);
            setSendForm(null);
        } catch (e) {
            showToast(`Send failed — ${e?.message || e}`, 'error');
        } finally {
            setSending(false);
        }
    };

    // ── Pieces ──
    // Plain render functions, not components: defining components inside the
    // parent gives them a new identity every render, and React remounts
    // their DOM each time — clicks land on detached nodes and inputs lose
    // focus. Functions called inline have no identity to lose.

    const moveButtons = (sheet) => (versionLocked ? null : (MOVES[sheet.st] || []).map((mv) => (
        <Pressable
            key={mv.label}
            // Signature sends go through the Ticket Map: the day's sheet
            // becomes (or opens) a ticket document, which has to pass the
            // Claude check before it can go out.
            onPress={() => (mv.sign
                ? (setPreview(null), openMap({ cells: [cellKey(sheet.d, sheet.sh)] }))
                : (setMovePrompt({ sheet, mv }), setMoveNote('')))}
            style={{
                borderWidth: 1, borderColor: mv.back ? '#e2e8f0' : '#0f172a', borderRadius: 4,
                paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, marginLeft: 6, cursor: 'pointer',
                backgroundColor: mv.back ? '#fff' : '#0f172a',
            }}
        >
            <Text style={{ fontSize: 11, fontWeight: '600', color: mv.back ? '#475569' : '#fff' }}>{mv.label}</Text>
        </Pressable>
    )));

    // What the client has done with the sheet, and the handful of things
    // worth doing about it from here. Lives in the preview modal alongside
    // the ticket the envelope carries — the day rows stay one line each, so
    // the only envelope news they carry is its status word on the chip.
    const renderEnvelope = (env) => {
        const busy = sigBusy === env.RowID;
        const closed = envClosed(env.Status);
        const when = env.Completed_At_Utc || env.Declined_At_Utc || env.Voided_At_Utc
            || env.Delivered_At_Utc || env.Sent_At_Utc;
        const act = (label, onPress, danger) => (
            <Pressable
                key={label}
                onPress={onPress}
                disabled={busy}
                style={{
                    borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4, backgroundColor: '#fff',
                    paddingTop: 2, paddingBottom: 2, paddingLeft: 7, paddingRight: 7, marginLeft: 6,
                    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                }}
            >
                <Text style={{ fontSize: 10, fontWeight: '600', color: danger ? '#991b1b' : '#475569' }}>{label}</Text>
            </Pressable>
        );
        const prog = signerProgress(env);
        const tone = { signed: '#166534', declined: '#991b1b', opened: '#075985', waiting: '#64748b' };
        const glyph = { signed: '\u2713', declined: '\u2715', opened: '\u25D4', waiting: '\u00B7' };
        return (
            <>
                <View style={{ marginLeft: 8 }}><EnvBadge status={env.Status} /></View>
                {when ? (
                    <Text style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>{fmtUtc(when)}</Text>
                ) : null}
                {/* Who has signed and who has not — the envelope only says
                    "completed" once the LAST of them has. */}
                {prog.total > 1 || (prog.total === 1 && prog.list[0].role) ? prog.list.map((r) => (
                    <View
                        key={r.email}
                        title={`${r.email}${r.role ? ` · ${r.role === 'All' ? 'signs all' : `${r.role} tickets`}` : ''}${r.at ? ` · ${fmtUtcLong(r.at)}` : ''}${r.reason ? ` · ${r.reason}` : ''}`}
                        style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 999, paddingTop: 1, paddingBottom: 1, paddingLeft: 7, paddingRight: 7, marginLeft: 6, backgroundColor: r.status === 'signed' ? '#f0fdf4' : '#fff' }}
                    >
                        <Text style={{ fontSize: 10, fontWeight: '600', color: tone[r.status] || tone.waiting }}>
                            {`${glyph[r.status] || ''} ${r.name || r.email}${r.status === 'signed' && r.at ? ` ${fmtUtc(r.at)}` : r.status === 'waiting' ? ' — waiting' : r.status === 'opened' ? ' — opened' : r.status === 'declined' ? ' — declined' : ''}`}
                        </Text>
                    </View>
                )) : null}
                {/* A declined sheet without its reason on screen is just a
                    red badge somebody has to go chase. */}
                {env.Declined_Reason ? (
                    <Text style={{ fontSize: 10, color: '#991b1b', marginLeft: 6 }} title={env.Declined_Reason}>
                        {`“${String(env.Declined_Reason).slice(0, 60)}”`}
                    </Text>
                ) : null}
                {String(env.Status).toLowerCase() === 'created' && !versionLocked
                    ? act('Send now', () => runSigAction(env, 'send')) : null}
                {!closed && String(env.Status).toLowerCase() !== 'created' && !versionLocked
                    ? act('Remind', () => runSigAction(env, 'resend')) : null}
                {!closed && !versionLocked ? act('Void', () => {
                    const reason = window.prompt('Why is this envelope being voided?');
                    if (reason && reason.trim()) runSigAction(env, 'void', { reason: reason.trim() });
                }, true) : null}
                {act(env.Signed_Doc_Url ? 'Signed PDF' : 'Document', () => (env.Signed_Doc_Url
                    ? window.open(env.Signed_Doc_Url, '_blank', 'noopener,noreferrer')
                    : openSigDoc(env)))}
                {!closed ? act('Refresh', () => runSigAction(env, 'refresh')) : null}
                {/* A signed copy the client's site would not take — Refresh
                    on a completed envelope files whatever is still owed. */}
                {closed && owesClientCopy(env) ? act('Retry client copy', () => runSigAction(env, 'refresh')) : null}
            </>
        );
    };

    // ── The day's deliverables ──
    // A work day produces the Day time sheet and the Night time sheet (the
    // Force Report is parked), and the row's job is to say what state each
    // is in — not to be the place they are worked. Both actions open onto
    // the same preview modal, or straight onto the file. An untracked sheet
    // reads "not tracked": tracking it starts in its preview, where whoever
    // is starting it can see what they are picking up.
    //
    // A night with nothing on it — no tracked sheet, no envelope, no ticket
    // that went out, no QA — is left blank: most days have no night crew,
    // and a column of "not tracked" says nothing. Its preview is still one
    // click away (the Day / Night switch in the preview's header), and a job
    // with no night paperwork at all drops the column.
    const shiftHasDoc = (day, sh) => {
        if (day.sheets[sh] || sigBy[`${day.d}_${sh}`] || qaByCell[`${day.d}_${sh}`]) return true;
        const c = cellDocStatus(day.d, sh, coverage, sigByDoc);
        return Boolean(c && docWentOut(c.doc, sigByDoc));
    };
    const showNight = days.some((day) => shiftHasDoc(day, 'Night'));

    // ── The grid's cells ──
    // One model per day and shift: a word, a tone and the long title. A
    // ticket document covering the cell speaks for it: its envelope, else
    // its own stage — so a four-day packet signed once reads Signed on all
    // four days. Only once the document has actually gone to the client: a
    // draft being put together in the Ticket Map does not colour the grid
    // (docWentOut) — the map shows drafts, the tracker shows sends. An
    // envelope out to several people reads as how many have signed. A day
    // with hours in HeavyJob and nothing sent for it reads MISSING.
    const today = moment().format('YYYY-MM-DD');
    const envLabel = (e) => {
        const p = signerProgress(e);
        return !envClosed(e.Status) && p.total > 1 && p.signed ? `${p.signed}/${p.total} signed` : envInfo(e.Status).label;
    };
    const toneOf = (st) => {
        const s = String(st || '').toLowerCase();
        if (s === 'completed' || s === 'signed') return 'good';
        if (s === 'declined' || s === 'failed') return 'bad';
        if (s === 'sent' || s === 'delivered' || s === 'sent for signature') return 'out';
        return 'draft';
    };
    const sheetCell = (day, sh) => {
        if (sh === 'Night' && !shiftHasDoc(day, sh)) return { blank: true };
        const { d } = day;
        const sheet = day.sheets[sh];
        const env = sigBy[`${d}_${sh}`];
        const stage = sheet ? sheet.st : null;
        const cov0 = cellDocStatus(d, sh, coverage, sigByDoc);
        const cov = cov0 && docWentOut(cov0.doc, sigByDoc) ? cov0 : null;
        const covLabel = (c) => {
            if (c.stage === 'Signed' || c.stage === 'Declined') return c.stage;
            const live = c.env && !['voided', 'expired', 'failed'].includes(String(c.env.Status).toLowerCase()) ? c.env : null;
            if (!live) return c.stage === 'Sent for Signature' ? 'Sent' : c.stage;
            const p = cellProgress(c.envs);
            return p.total > 1 && p.signed ? `${p.signed}/${p.total} signed` : envInfo(live.Status).label;
        };
        const covTitle = (c) => [
            `On ticket ${docName(c.doc.cells)} — open the Ticket Map to see it`,
            ...c.envs.map((x) => {
                const p = signerProgress(x.env);
                return `${PART_SHORT[x.part] || x.part}: ${envInfo(x.env.Status).label}${p.total ? ` (${p.signed}/${p.total} signed)` : ''}`;
            }),
        ].join('\n');
        const qa = qaByCell[`${d}_${sh}`];
        const missing = !sheet && !cov && !env && sh === 'Day' && hasDetail(d) && d < today;
        const status = cov ? covLabel(cov) : env ? envLabel(env) : stage || (missing ? 'Missing' : 'not tracked');
        const tone = cov ? (cov.stage === 'Signed' ? 'good' : cov.stage === 'Declined' ? 'bad' : cov.env ? toneOf(cov.env.Status) : cov.stage === 'Validated' ? 'good' : 'draft')
            : env ? toneOf(env.Status) : stage ? (stage === 'Signed' ? 'good' : stage === 'Declined' ? 'bad' : stage === 'Sent for Signature' ? 'out' : 'draft')
                : missing ? 'bad' : 'none';
        const short = status === 'Sent for Signature' ? 'Sent' : status === 'Out for signature' ? 'Sent' : status === 'Opened by client' ? 'Opened'
            : status === 'Draft — not sent' ? 'Draft' : status === 'not tracked' ? '—' : status;
        const title = cov ? covTitle(cov) : missing ? `${hrs(day.heet?.API_Hours ?? day.heet?.HJ_Hours ?? day.heet?.Hours)} hours in HeavyJob and no ${sh.toLowerCase()} ticket has gone out` : status;
        return { label: short, tone, title, status, cov, env, sheet, qa, missing };
    };

    // ── The week's summary and invoice ──
    // Weekly paper on a daily grid: the summary that went out over the days
    // and the invoice billing them, each drawn once across the days it
    // covers. Read off the envelopes and the Billing tab's summaries
    // (billingAt), so a send made by script shows as soon as its envelope
    // row exists. A week with hours and no summary reads MISSING once the
    // week has ended; the week in progress reads as open.
    const mdOf = (d) => moment(d, 'YYYY-MM-DD').format('M/D');
    const openEnvDoc = (env) => (env.Signed_Doc_Url
        ? window.open(env.Signed_Doc_Url, '_blank', 'noopener,noreferrer')
        : openSigDoc(env));
    const dayHours = (day) => {
        const r = day.heet;
        if (!r) return 0;
        if (r.API_Hours != null) return Number(r.API_Hours) || 0;
        const mis = dayMismatch(r);
        const useHJ = mis < 0 || (r.In_HEET !== 1 && r.In_HJ === 1 && r.In_Actuals !== 1);
        return Number(useHJ ? r.HJ_Hours : r.Hours) || 0;
    };
    const INV_TONE = { done: 'good', out: 'out', bad: 'bad', draft: 'draft' };

    // Oldest first across the grid.
    const gridCols = useMemo(() => [...days].reverse().map((day) => ({ d: day.d, we: weekEnding(day.d), day })), [days]);
    const gridRows = (() => {
        const hours = {};
        const docsRow = {};
        const sheets = { Day: {}, Night: showNight ? {} : null };
        gridCols.forEach(({ d, day }) => {
            const r = day.heet;
            if (r) {
                let label; let title; let warn = false;
                if (r.API_Staff != null) {
                    const behind = r.In_HEET === 1 && Math.abs((Number(r.HEET_Hours ?? r.Hours) || 0) - (Number(r.API_Hours) || 0)) > 0.5;
                    title = `${pNum(r.API_Staff)} staff, ${hrs(r.API_Hours)} hours on ${pNum(r.API_Cards)} HeavyJob card${r.API_Cards === 1 ? '' : 's'} (live)`
                        + `${r.API_Approved ? `, ${r.API_Approved} approved` : ', none approved yet'}`
                        + `${r.In_HEET === 1 ? ` — HEET has ${pNum(r.HEET_Staff ?? r.Staff)} staff / ${hrs(r.HEET_Hours ?? r.Hours)} hours${behind ? ' (behind the cards; the ticket uses the cards)' : ''}` : ' — HEET has not pulled this day yet'}`;
                    label = `${h0(r.API_Hours)}${behind ? ' ≠' : ''}`;
                    hours[d] = { label, people: pNum(r.API_Staff), title, level: rampLevel(Number(r.API_Hours) || 0) };
                } else {
                    const mis = dayMismatch(r);
                    const useHJ = Boolean(mis < 0 || (r.In_HEET !== 1 && r.In_HJ === 1 && r.In_Actuals !== 1));
                    const p = useHJ ? r.HJ_Staff : r.Staff;
                    const h = useHJ ? r.HJ_Hours : r.Hours;
                    title = mis ? `HeavyJob ${hrs(r.HJ_Hours)} h / ${pNum(r.HJ_Staff)} staff vs HEET ${hrs(r.HEET_Hours)} h / ${pNum(r.HEET_Staff)} staff — ${mis < 0
                        ? 'HEET has not pulled the full day yet; missing people ride into the ticket from the HeavyJob raw feed'
                        : 'HEET is ABOVE the HeavyJob raw feed — possible double load, check before sending'}`
                        : inHeet(d) ? `${pNum(r.Staff)} staff, ${hrs(r.Hours)} hours in HEET`
                        : r.In_Actuals === 1 ? `${pNum(r.Staff)} staff, ${hrs(r.Hours)} hours — HEET has purged this day, detail comes from actual cost`
                        : `${pNum(r.HJ_Staff)} staff, ${hrs(r.HJ_Hours)} hours in HeavyJob — HEET has not pulled this day yet, detail comes from the raw feed as unapproved straight-time`;
                    warn = Boolean(mis);
                    hours[d] = { label: `${h0(h)}${mis ? ' ≠' : ''}`, people: pNum(p), title, level: rampLevel(Number(h) || 0), warn };
                }
            }
            const docs = docsByDate[d] || [];
            if (docs.length) docsRow[d] = { count: docs.length, title: docs.map((f) => f.name).join('\n') };
            sheets.Day[d] = sheetCell(day, 'Day');
            if (sheets.Night) sheets.Night[d] = sheetCell(day, 'Night');
        });

        // Summary spans: consecutive days of one week under the same summary
        // envelope (or none) make one cell.
        const summary = [];
        const invoice = [];
        gridCols.forEach(({ d, we }, i) => {
            const b = billingAt(d);
            const sKey = `${we}|${b.summary ? b.summary.env.RowID : ''}`;
            const last = summary[summary.length - 1];
            if (last && last.k === sKey) { last.to = i; last.hoursSum += dayHours(gridCols[i].day); return; }
            summary.push({ k: sKey, key: `s${i}`, from: i, to: i, we, s: b.summary, hoursSum: dayHours(gridCols[i].day) });
        });
        summary.forEach((x) => {
            const s = x.s; const env = s ? s.env : null;
            if (env) {
                const st = String(env.Status || '').toLowerCase();
                const p = signerProgress(env);
                const word = st === 'completed' ? 'Signed' : st === 'declined' ? 'Declined' : st === 'created' ? 'Draft' : 'Sent';
                x.label = `${word}${st === 'completed' && env.Completed_At_Utc ? ` ${moment.utc(env.Completed_At_Utc).local().format('M/D')}` : ''}`;
                // Out: who it is waiting on, so the cell says where it sits.
                const waitOn = ['sent', 'delivered'].includes(st) ? p.waiting.map((r) => r.name) : [];
                x.sub = waitOn.length ? `waiting on ${waitOn.join(', ')}` : p.total ? `${p.signed} of ${p.total} signed` : '';
                x.tone = toneOf(st);
                x.title = [
                    `Week summary ${mdOf(s.from)}–${mdOf(s.to)}${env.Document_Name ? ` — ${env.Document_Name}` : ''}`,
                    `Sent ${fmtUtc(env.Sent_At_Utc)}${env.Sent_By ? ` by ${env.Sent_By}` : ''}`,
                    ...(st === 'completed' ? [`Signed ${fmtUtc(env.Completed_At_Utc)}`] : []),
                    ...p.list.map((r) => `${r.name}: ${r.status}`),
                ].join('\n');
            } else if (!x.hoursSum && gridCols.slice(x.from, x.to + 1).some(({ d }) => hoursUnknown(d))) {
                // "no hours" would be a claim; nothing has answered for these days yet.
                x.label = x.to - x.from >= 1 ? (heet.loading || hjDates.loading ? 'loading…' : 'hours not loaded') : '—';
                x.tone = 'none'; x.title = 'HeavyJob hours for these days have not loaded — see the note above the grid'; x.selectable = false;
            } else if (!x.hoursSum) {
                x.label = x.to - x.from >= 1 ? 'no hours' : '—'; x.tone = 'none'; x.title = 'No HeavyJob time in these days'; x.selectable = false;
            } else if (x.we >= today) {
                x.label = 'week open'; x.sub = `${h0(x.hoursSum)} hr so far`; x.tone = 'warn'; x.title = 'The week is still running — the summary goes out after its last day';
            } else {
                x.label = 'Missing'; x.sub = `${h0(x.hoursSum)} hr`; x.tone = 'bad'; x.title = `${hrs(x.hoursSum)} hours in HeavyJob and no week summary has gone out over them`;
            }
        });

        // Invoice spans: consecutive days under the same set of invoices.
        gridCols.forEach(({ d }, i) => {
            const list = billingAt(d).invoices;
            const iKey = list.map((v) => v.no).join(',');
            const last = invoice[invoice.length - 1];
            if (last && last.k === iKey) { last.to = i; return; }
            invoice.push({ k: iKey, key: `i${i}`, from: i, to: i, list });
        });
        invoice.forEach((x) => {
            const v = x.list[0] || null;
            if (!v) { x.label = x.to - x.from >= 1 ? 'not invoiced' : '—'; x.tone = 'none'; x.title = 'No invoice bills these days yet'; return; }
            const info = invoiceStatus(v, signerProgress);
            const p = v.env ? signerProgress(v.env) : null;
            x.label = `${v.no} · ${info.label}${x.list.length > 1 ? ` +${x.list.length - 1}` : ''}`;
            const invWait = p && info.tone === 'out' ? p.waiting.map((r) => r.name) : [];
            x.sub = invWait.length ? `waiting on ${invWait.join(', ')}` : p && p.total ? `${p.signed} of ${p.total} signed` : '';
            x.tone = INV_TONE[info.tone] || 'draft';
            x.title = x.list.map((y) => {
                const i2 = invoiceStatus(y, signerProgress);
                const who = y.env ? signerProgress(y.env).list.map((r) => `${r.name}: ${r.status}`).join(', ') : '';
                return [
                    `Invoice ${y.no} — ${mdOf(y.from)}–${mdOf(y.to)} — ${i2.label}`,
                    y.status ? `Billing summary: ${y.status}` : 'Not on a Billing summary in Fusion',
                    y.env ? `Approval sent ${fmtUtc(y.env.Sent_At_Utc)}${who ? ` — ${who}` : ''}` : 'Not routed for approval',
                ].join('\n');
            }).join('\n\n');
        });
        // Lump-sum invoices (the PB4 mobilization) bill no work day, so they
        // sit in a row of their own under the grid rather than on any date.
        const lumps = lumpInvoices({ envelopes: sigs.list, invoices }).map((v, i) => {
            const info = invoiceStatus(v, signerProgress);
            const p = signerProgress(v.env);
            const waitOn = info.tone === 'out' ? p.waiting.map((r) => r.name) : [];
            return {
                key: `lump${v.no}`, no: v.no, env: v.env, idx: i,
                label: `${v.no}${v.what ? ` · ${v.what}` : ''} · ${info.label}`,
                sub: waitOn.length ? `waiting on ${waitOn.join(', ')}` : p.total ? `${p.signed} of ${p.total} signed` : '',
                tone: INV_TONE[info.tone] || 'draft',
                title: [`Invoice ${v.no}${v.what ? ` — ${v.what}` : ''} (lump sum, no work days)`, `Approval sent ${fmtUtc(v.env.Sent_At_Utc)}`, ...p.list.map((r) => `${r.name}: ${r.status}`)].join('\n'),
            };
        });
        return { hours, docs: docsRow, sheets, summary, invoice, lumps };
    })();

    // ── The detail strip ──
    // What the selected cell is, and everything you can do to it. The
    // controls are the day list's old per-row icons, in one place.
    const signerLine = (env) => {
        const p = signerProgress(env);
        return p.list.length ? p.list.map((r) => `${r.name}: ${r.status}`).join(' · ') : '';
    };
    const stripBtn = (label, onPress, title, disabled) => (
        <Pressable
            key={label}
            onPress={() => !disabled && onPress()}
            disabled={disabled}
            title={title}
            style={{
                paddingTop: 5, paddingBottom: 5, paddingLeft: 12, paddingRight: 12, marginRight: 8, marginBottom: 4,
                borderRadius: 10, borderWidth: 1, borderStyle: 'solid', borderColor: '#e5e5e5', backgroundColor: '#ffffff',
                opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
            }}
        >
            <Text style={{ fontSize: 12, color: '#0a0a0a', fontWeight: '500', whiteSpace: 'nowrap' }}>{label}</Text>
        </Pressable>
    );
    const docLinks = (d) => {
        const docs = docsByDate[d] || [];
        if (!docs.length) return <Text style={{ fontSize: 11, color: '#737373' }}>{spDocs.data?.folderFound ? 'No scans filed for this day in SharePoint.' : ''}</Text>;
        return docs.map((f) => (
            <Pressable key={f.id} onPress={() => f.webUrl && window.open(f.webUrl, '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer' }}>
                <Text style={{ fontSize: 11, color: '#1d518c', paddingTop: 1, paddingBottom: 1 }}>{`${f.path ? `${f.path}/` : ''}${f.name}`}</Text>
            </Pressable>
        ));
    };
    const stripHead = (text, sub) => (
        <View style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#0a0a0a' }}>{text}</Text>
            {sub ? <Text style={{ fontSize: 11, color: '#525252', whiteSpace: 'pre-line' }}>{sub}</Text> : null}
        </View>
    );
    const gridDetail = (() => {
        const sel = gridSel;
        if (!sel) return null;
        const dayOf = (d) => days.find((x) => x.d === d);
        if (sel.kind === 'sheet') {
            const day = dayOf(sel.d);
            if (!day) return null;
            const c = sheetCell(day, sel.sh);
            const sh = sel.sh;
            return (
                <View>
                    {stripHead(`${moment(sel.d, 'YYYY-MM-DD').format('ddd M/D/YY')} — ${sh} sheet: ${c.status}`, c.cov ? c.title : c.env ? signerLine(c.env) : c.missing ? c.title : '')}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        {stripBtn('Preview', () => openPreview(sel.d, sh), `Preview the ${sh.toLowerCase()} time sheet — ${fmtShort(sel.d)}`)}
                        {stripBtn('Export Excel', () => hasDetail(sel.d) && exportTicket(sel.d, sh), hasDetail(sel.d)
                            ? `Export the ${sh.toLowerCase()} field ticket (Excel) — ${fmtShort(sel.d)}`
                            : 'No detail for this day in HEET, actual cost, or the HeavyJob raw feed', !hasDetail(sel.d))}
                        {c.cov ? stripBtn('Open on the Ticket Map', () => openMap({ docId: c.cov.doc.id }), `On ticket ${docName(c.cov.doc.cells)}`) : null}
                        {c.qa ? (
                            <Pressable onPress={() => openPreview(sel.d, sh)} title={`Labor QA on the ${sh.toLowerCase()} sheet — open the preview for the findings`} style={{ marginRight: 8, cursor: 'pointer' }}>
                                <QaTag areas={c.qa.areas} />
                            </Pressable>
                        ) : null}
                    </View>
                    <View style={{ marginTop: 4 }}>{docLinks(sel.d)}</View>
                </View>
            );
        }
        if (sel.kind === 'day') {
            const day = dayOf(sel.d);
            const h = day ? gridRows.hours[sel.d] : null;
            return (
                <View>
                    {stripHead(`${moment(sel.d, 'YYYY-MM-DD').format('ddd M/D/YY')}${h ? ` — ${h.people} people, ${h.label} hours` : ' — no HeavyJob time'}`, h ? h.title : '')}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        {stripBtn('Preview day sheet', () => openPreview(sel.d, 'Day'), `Preview the day time sheet — ${fmtShort(sel.d)}`)}
                    </View>
                    <View style={{ marginTop: 4 }}>{docLinks(sel.d)}</View>
                </View>
            );
        }
        if (sel.kind === 'summary') {
            const x = gridRows.summary.find((y) => y.key === sel.key);
            if (!x) return null;
            const s = x.s; const env = s ? s.env : null;
            const from = gridCols[x.from]?.d; const to = gridCols[x.to]?.d;
            return (
                <View>
                    {stripHead(`Week summary ${mdOf(from)}–${mdOf(to)}: ${x.label}${x.sub ? ` — ${x.sub}` : ''}`, env ? x.title : x.title)}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        {env ? stripBtn('Open the summary', () => openEnvDoc(env), `Open the week summary ${mdOf(s.from)}–${mdOf(s.to)}${env.Signed_Doc_Url ? ' (signed copy)' : ''}`) : null}
                        {s?.docId ? stripBtn('Open on the Ticket Map', () => openMap({ docId: s.docId }), 'Open the Ticket Map on this summary') : null}
                        {!env ? stripBtn('Open the Ticket Map', () => openMap(null), 'Build and send the week summary from the Ticket Map') : null}
                    </View>
                </View>
            );
        }
        if (sel.kind === 'lump') {
            const x = (gridRows.lumps || []).find((y) => y.key === sel.key);
            if (!x) return null;
            return (
                <View>
                    {stripHead(`Invoice ${x.label}${x.sub ? ` — ${x.sub}` : ''}`, x.title)}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        {stripBtn(`Open invoice ${x.no}'s approval packet`, () => openEnvDoc(x.env), `Open invoice ${x.no}'s approval packet${x.env.Signed_Doc_Url ? ' (approved copy)' : ''}`)}
                    </View>
                </View>
            );
        }
        if (sel.kind === 'invoice') {
            const x = gridRows.invoice.find((y) => y.key === sel.key);
            if (!x) return null;
            const from = gridCols[x.from]?.d; const to = gridCols[x.to]?.d;
            return (
                <View>
                    {stripHead(`Invoice ${mdOf(from)}–${mdOf(to)}: ${x.label}${x.sub ? ` — ${x.sub}` : ''}`, x.title)}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        {x.list.filter((v) => v.env).map((v) => stripBtn(`Open invoice ${v.no}'s approval packet`, () => openEnvDoc(v.env), `Open invoice ${v.no}'s approval packet${v.env.Signed_Doc_Url ? ' (approved copy)' : ''}`))}
                    </View>
                </View>
            );
        }
        return null;
    })();

    const feedsNoteNow = feedsNote({ heet, cards: hjDates, sigs });

    return (
        <>
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: '#0a0a0a' }}>Days with HeavyJob time and what has been sent</Text>
                    {/* The day list runs from here, so a week the live feeds
                        have already rolled past can still be tracked and
                        signed. Stored per job ('tcstart'). */}
                    <View style={{ flexGrow: 1 }} />
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginRight: 6 }}>Tracking from</Text>
                    {createElement('input', {
                        type: 'date',
                        value: projStart || '',
                        disabled: versionLocked,
                        title: 'The first day this job shows here — days before it are hidden even when they have hours.',
                        onChange: (e) => {
                            const v = String(e.target.value || '');
                            if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
                            saveRows([{ rowId: 'tcstart', fieldName: 'tcstart', value: v }]);
                            showToast(v ? `Tracking this job from ${fmtShort(v)}.` : 'Tracking start cleared — the day list follows the feeds.', 'success');
                        },
                        style: { fontSize: 11, padding: 3, borderRadius: 4, border: '1px solid #cbd5e1' },
                    })}
                    {/* SharePoint resyncs on its own every 5 minutes, so it only
                        speaks up when the folder can't be read — press to retry. */}
                    {spDocs.data?.error || (spDocs.data && !spDocs.data.folderFound) ? (
                        <Pressable onPress={() => !spDocs.loading && syncDocs(jobKey)} style={{ marginLeft: 12, cursor: 'pointer' }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: '#b45309', whiteSpace: 'nowrap' }}>
                                {spDocs.loading ? 'Syncing…' : spDocs.data.error ? 'SharePoint sync failed · retry' : 'No Timecards folder · retry'}
                            </Text>
                        </Pressable>
                    ) : null}
                    <View style={{ marginLeft: 12 }}>
                        <OutlineButton label={`Tickets${ticketDocs.length ? ` (${ticketDocs.length})` : ''}`} onPress={() => openMap(null)} />
                    </View>
                </View>

                {/* The hours feeds' state. With a tracking start set the grid
                    draws before either feed answers, so without this a slow or
                    failed load looked like a job with no time at all. */}
                {feedsNoteNow ? (
                    <View
                        style={feedsNoteNow.failed ? {
                            flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 2, marginBottom: 8,
                            paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, borderRadius: 10,
                            backgroundColor: '#fdf4dc', borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(161, 98, 7, 0.25)',
                        } : { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 2, marginBottom: 8 }}
                    >
                        <Text style={{ fontSize: 12, color: feedsNoteNow.failed ? '#854d0e' : '#737373' }}>{feedsNoteNow.text}</Text>
                        {feedsNoteNow.failed ? (
                            <Pressable onPress={retryFeeds} title="Ask the feeds that failed again" style={{ marginLeft: 10, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#1d518c', whiteSpace: 'nowrap' }}>Retry now</Text>
                            </Pressable>
                        ) : null}
                    </View>
                ) : null}

                {heet.loading && !days.length ? <SkeletonRows rows={4} cells={[{ width: 150 }, { width: 200 }, { width: 260 }]} /> : null}

                {days.length ? (
                    <CoverageGrid cols={gridCols} rows={gridRows} selected={gridSel} onSelect={setGridSel} detail={gridDetail} />
                ) : null}
                {!heet.loading && !days.length ? (
                    <Text style={{ fontSize: 13, color: '#64748b', paddingTop: 14, paddingBottom: 6 }}>
                        No HeavyJob days in the window and nothing tracked yet — set &ldquo;Tracking from&rdquo; above to the first day this job should show.
                    </Text>
                ) : null}
            </View>

            {/* ── Ticket Map — which document covers each day/shift, and the
                 build / Claude check / send flow for one. ── */}
            {/* Keyed on the hand-off: a new one while the map is open starts it over on its days. */}
            {mapOpen ? (
                <TicketMap
                    key={mapOpen.nonce || 'map'}
                    jobKey={jobKey}
                    versionLocked={versionLocked}
                    userName={userName}
                    settings={settings}
                    days={days.map((x) => ({ d: x.d, hasDetail: hasDetail(x.d) }))}
                    docs={ticketDocs}
                    sigByDoc={sigByDoc}
                    envelopes={sigs.list}
                    legacyStatus={(d, sh) => {
                        const e = sigBy[`${d}_${sh}`];
                        if (e) return { label: envInfo(e.Status).label };
                        const s = tracked[sheetKey(d, sh)];
                        return s ? { label: s.st } : null;
                    }}
                    files={spDocs.data?.files || []}
                    filesLoading={spDocs.loading}
                    fileUrl={tcFileUrl}
                    fileDate={tcFileDate}
                    focus={mapOpen.focus}
                    dataKey={`${Object.keys(rosterShift).length}|${JSON.stringify(shiftOv)}|${Object.keys(staffCls || {}).length}`}
                    onClose={() => setMapOpen(null)}
                    saveRows={saveRows}
                    cellTickets={cellTickets}
                    cellPayload={cellPayloadFor}
                    buildPacket={buildDocPacket}
                    daySources={daySources}
                    roster={{ loading: roster.loading, error: roster.error, found: Boolean(roster.data?.found), file: roster.data?.file || null }}
                    sourceFiles={{ staffList: staffClsFile, roster: roster.data?.file || null }}
                    equipWeeks={equipWeeks}
                    importTimecard={importReviewedTimecard}
                    sendPacket={sendDocPacket}
                    recordRecon={recordRecon}
                    renderEnvelope={renderEnvelope}
                    userEmail={userEmail}
                    uploadFile={uploadFileToJob}
                    onFilesChanged={() => syncDocs(jobKey)}
                    sigFlow={sigFlow}
                    setSigFlow={setSigFlow}
                    docusignDemo={/appdemo\./i.test(sigs.consoleUrlBase)}
                />
            ) : null}

            {/* ── Preview — the deliverable on screen, and everything you do
                 to it. The chips only report state; the work happens here:
                 advance the stage, put the ticket in front of the client for
                 signature, chase or void that envelope, read the trail. ── */}
            {preview ? (() => {
                const isForce = preview.kind === 'Force';
                const sheet = isForce ? null : tracked[sheetKey(preview.d, preview.kind)];
                const env = isForce ? null : sigBy[`${preview.d}_${preview.kind}`];
                const forceDoc = isForce ? forceDocFor(preview.d) : null;
                const filedShowing = Boolean(forceDoc && !showGenForce);
                return (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.45)', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, paddingTop: 14, paddingBottom: 18, paddingLeft: 18, paddingRight: 18, width: '94%', maxWidth: 1000, maxHeight: '92%', overflow: 'auto' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>
                                {`${isForce ? 'Daily Force Report' : `${preview.kind} Time Sheet`} — ${fmtDay(preview.d)}`}
                            </Text>
                            {/* The other crew's sheet for the same day — the way
                                onto a night the day list leaves blank. */}
                            {!isForce ? (
                                <View style={{ flexDirection: 'row', marginLeft: 12, borderWidth: 1, borderStyle: 'solid', borderColor: '#cbd5e1', borderRadius: 4, overflow: 'hidden' }}>
                                    {SHIFTS.map((sh) => (
                                        <Pressable
                                            key={sh}
                                            onPress={() => sh !== preview.kind && openPreview(preview.d, sh)}
                                            title={sh === preview.kind ? undefined : `Switch to the ${sh.toLowerCase()} sheet for ${fmtShort(preview.d)}`}
                                            style={{
                                                paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9,
                                                backgroundColor: sh === preview.kind ? '#0f172a' : '#ffffff',
                                                cursor: sh === preview.kind ? 'default' : 'pointer',
                                            }}
                                        >
                                            <Text style={{ fontSize: 11, fontWeight: '600', color: sh === preview.kind ? '#ffffff' : '#475569' }}>{sh}</Text>
                                        </Pressable>
                                    ))}
                                </View>
                            ) : null}
                            <View style={{ flexGrow: 1 }} />
                            {isForce ? (
                                <>
                                    <View style={{ marginRight: 8 }}>
                                        <OutlineButton label="Export Excel" onPress={() => exportForceExcel(preview.d)} disabled={!force || !force.length} />
                                    </View>
                                    <View style={{ marginRight: 8 }}>
                                        <OutlineButton label="Export PDF" onPress={() => exportForce(preview.d)} disabled={!force || !force.length} />
                                    </View>
                                </>
                            ) : (
                                <>
                                    <View style={{ marginRight: 8 }}><OutlineButton label="Export Excel" onPress={() => exportTicket(preview.d, preview.kind)} disabled={!shiftTicket || (!shiftTicket.labor.length && !shiftTicket.equipment.length)} /></View>
                                    <View style={{ marginRight: 8 }}><OutlineButton label="Export PDF" onPress={() => exportTicketPdf(preview.d, preview.kind)} disabled={!shiftTicket || (!shiftTicket.labor.length && !shiftTicket.equipment.length)} /></View>
                                    {/* The whole pay week this day belongs to, one signable
                                        packet: summary cover + each day's ticket. */}
                                    <View style={{ marginRight: 8 }}><OutlineButton label="Week PDF" onPress={() => exportWeekPdf(preview.d, preview.kind)} /></View>
                                    {sigs.configured && !versionLocked ? (
                                        <View style={{ marginRight: 8 }}>
                                            <OutlineButton
                                                label="Week Ticket"
                                                onPress={() => {
                                                    const cells = weekDaysOf(preview.d).days.map((x) => cellKey(x, preview.kind));
                                                    setPreview(null);
                                                    openMap({ cells });
                                                }}
                                            />
                                        </View>
                                    ) : null}
                                    <View style={{ marginRight: 8 }}>
                                        <OutlineButton label={savingTicket ? 'Saving...' : 'Save to SharePoint'} onPress={saveTicketToSharePoint} disabled={!shiftTicket || (!shiftTicket.labor.length && !shiftTicket.equipment.length) || savingTicket || versionLocked} />
                                    </View>
                                </>
                            )}
                            <Pressable onPress={() => setPreview(null)} style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 13, color: '#475569', fontWeight: '700' }}>Close</Text>
                            </Pressable>
                        </View>

                        {/* The Force Report is not a signature deliverable —
                            it goes to the client as a plain email with the
                            workbook attached, and the send record lives on
                            the day's 'tcforce' row. */}
                        {isForce ? (() => {
                            const sent = forceSends[preview.d] || [];
                            const last = sent.length ? sent[sent.length - 1] : null;
                            return (
                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingTop: 6, paddingBottom: 8, borderTopWidth: 1, borderBottomWidth: 1, borderColor: '#f1f5f9', marginBottom: 10 }}>
                                {!versionLocked ? (
                                    <Pressable
                                        onPress={() => openSend(preview.d, SHIFT_DEFAULT, null, null, 'Force')}
                                        style={{
                                            borderWidth: 1, borderColor: '#0f172a', borderRadius: 4,
                                            paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8,
                                            backgroundColor: '#0f172a', cursor: 'pointer',
                                        }}
                                    >
                                        <Text style={{ fontSize: 11, fontWeight: '600', color: '#fff' }}>
                                            {last ? 'Email Again' : 'Email to Client'}
                                        </Text>
                                    </Pressable>
                                ) : null}
                                {last ? (
                                    <Text style={{ fontSize: 11, color: '#475569', marginLeft: 8 }}>
                                        {`Emailed to ${last.to} — ${last.t}${last.u ? ` by ${last.u}` : ''}`}
                                    </Text>
                                ) : (
                                    <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                                        Not emailed to the client yet.
                                    </Text>
                                )}
                            </View>
                            );
                        })() : null}

                        {/* The sheet's own state and its next moves — the
                            DocuSign send is one of them, which is why the
                            client's ticket is on screen right below it. */}
                        {!isForce ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingTop: 6, paddingBottom: 8, borderTopWidth: 1, borderBottomWidth: 1, borderColor: '#f1f5f9', marginBottom: 10 }}>
                                {sheet ? (
                                    <>
                                        <Badge status={sheet.st} />
                                        {moveButtons(sheet)}
                                        {env ? renderEnvelope(env) : null}
                                    </>
                                ) : (
                                    <>
                                        <Text style={{ fontSize: 11, color: '#94a3b8', marginRight: 8 }}>
                                            {`This work day's ${String(preview.kind).toLowerCase()} sheet is not tracked yet.`}
                                        </Text>
                                        {!versionLocked ? (
                                            <Pressable
                                                onPress={() => track(preview.d, preview.kind)}
                                                style={{ borderWidth: 1, borderColor: '#0f172a', backgroundColor: '#0f172a', borderRadius: 4, paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10, cursor: 'pointer' }}
                                            >
                                                <Text style={{ fontSize: 11, fontWeight: '600', color: '#fff' }}>Track this sheet</Text>
                                            </Pressable>
                                        ) : null}
                                    </>
                                )}
                            </View>
                        ) : null}

                        {/* Where the envelope stands, as three ticks — the
                            send/sign/file journey at a glance. */}
                        {env ? <SigProgress env={env} /> : null}

                        <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                            {filedShowing
                                ? 'The filed copy from SharePoint — the report of record for this day. '
                                  + 'The generated render is still available behind View generated, above.'
                                : isForce
                                ? 'Rendered the way the exported Daily Force Report reads — the client\u2019s own '
                                  + 'workbook with the day\u2019s heads counted into it. Review it here, then email it '
                                  + 'to the client above — the recipients default from the job\u2019s billing settings.'
                                : `Rendered the way the exported Field Ticket PDF reads — the ${String(preview.kind).toLowerCase()}-shift `
                                  + 'cut of the crew\u2019s phase hours, priced. Review it here, then send it for '
                                  + 'signature above; DocuSign carries this exact PDF and the signed copy files itself back to SharePoint. '
                                  + 'Export Excel still fills the client\u2019s own Multi Phase Field Ticket workbook.'}
                        </Text>

                        {/* Where the day/night split came from, and what the
                            other sheet holds — the Shift column's D/N toggle
                            moves a person between the two sheets. */}
                        {!isForce && ticket ? (() => {
                            const other = preview.kind === 'Night' ? 'Day' : 'Night';
                            const otherCount = ticket.labor.filter((p) => Boolean(p.night) === (other === 'Night')).length;
                            const ovCount = Object.keys(shiftOv[preview.d] || {}).length;
                            const card = cardFor(preview.d);
                            // A source that FAILED to read is said in red: what is on
                            // screen is then only the HeavyJob cards, and the exports
                            // refuse until it reads (sourcesReady).
                            const cardErr = cards[weekEndFor(preview.d)]?.error;
                            const srcFailed = Boolean(cardErr || (roster.error && !card));
                            const src = cardErr
                                ? `Could not read the reviewed time card (${cardErr}) — this is the HeavyJob cards only, and nothing exports until it reads`
                                : roster.error && !card
                                ? `Could not read the attendance roster (${roster.error}) — shifts here are the HeavyJob cards only, and nothing exports until it reads`
                                : card
                                ? `Hours, shifts and classifications from the reviewed time card (${card.file?.name || 'Timecards'}${card.file?.modifiedBy ? `, ${card.file.modifiedBy}` : ''})`
                                : roster.data?.found
                                ? `Shifts from the attendance roster (${roster.data.file?.name || 'Attendance & Force Report'}) — no reviewed time card for this week yet, so hours are the ${dayInfo.feed?.basis === 'cards' ? 'live HeavyJob cards' : 'HeavyJob feed'}`
                                : roster.loading ? 'Loading the attendance roster…'
                                : 'No attendance roster found on SharePoint — shifts come from the HeavyJob cards';
                            // Anyone none of the sources places — not on the reviewed
                            // card, no roster row, or a blank "Days or Nights" cell —
                            // silently inherits the HeavyJob card, and that field is
                            // often keyed to one shift for a whole job-week. Name them
                            // here, on the sheet they landed on, so the split gets
                            // fixed before the ticket goes out.
                            const placed = overridesFor(preview.d);
                            const unplaced = (card || roster.data?.found)
                                ? ticket.labor.filter((x) => x.id && !placed[String(x.id)])
                                : [];
                            return (
                                <>
                                    <Text style={{ fontSize: 10, color: srcFailed ? '#b91c1c' : '#94a3b8', fontWeight: srcFailed ? '600' : '400', marginTop: -6, marginBottom: unplaced.length ? 4 : 10 }}>
                                        {`${src}${ovCount ? `, ${ovCount} manual override${ovCount === 1 ? '' : 's'} on this day` : ''}.`
                                            + `${otherCount ? ` ${otherCount} ${otherCount === 1 ? 'person is' : 'people are'} on the ${other.toLowerCase()} sheet.` : ''}`}
                                    </Text>
                                    {unplaced.length ? (
                                        <Text style={{ fontSize: 10, color: '#b45309', marginBottom: 10 }}>
                                            {`${unplaced.length} placed by the HeavyJob card, not by the reviewed time card or the roster: `
                                                + `${unplaced.map((x) => x.name).join(', ')}. `
                                                + 'Check the Shift column, or have project controls add them to the time card.'}
                                        </Text>
                                    ) : null}
                                </>
                            );
                        })() : null}

                        {/* How this ticket was built and what to look at
                            before it goes out — the checks the packet scripts
                            print before a send, off the same data the ticket
                            reads (billingCardSource contextNotes). */}
                        {!isForce && dayInfo.rows ? (() => {
                            const ctx = dayContext(preview.d, ticket);
                            if (!ctx.notes.length) return null;
                            const order = { fail: 0, warn: 1, info: 2 };
                            const notes = ctx.notes.slice().sort((a, b) => order[a.level] - order[b.level]);
                            const warnN = notes.filter((n) => n.level !== 'info').length;
                            const color = { fail: '#b91c1c', warn: '#b45309', info: '#475569' };
                            const mark = { fail: '✕', warn: '!', info: '·' };
                            return (
                                <View style={{ borderWidth: 1, borderColor: warnN ? '#fde68a' : '#e2e8f0', backgroundColor: warnN ? '#fffbeb' : '#f8fafc', borderRadius: 6, paddingTop: 8, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, marginBottom: 10 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                        <Text style={{ fontSize: 10, fontWeight: '700', letterSpacing: 0.5, color: '#64748b', flexGrow: 1 }}>
                                            {`BUILT FROM${warnN ? ` · ${warnN} TO CHECK` : ''}`}
                                        </Text>
                                        <Pressable onPress={() => { loadDay(preview.d, true); loadHjDates(jobKey); }}>
                                            <Text style={{ fontSize: 10, fontWeight: '600', color: '#1e40af' }}>
                                                {dayCache[preview.d]?.loading ? 'Reloading…' : 'Reload from HeavyJob'}
                                            </Text>
                                        </Pressable>
                                    </View>
                                    {notes.map((n, i) => (
                                        <View key={i} style={{ flexDirection: 'row', paddingTop: 1, paddingBottom: 1 }}>
                                            <Text style={{ fontSize: 11, width: 14, color: color[n.level], fontWeight: '700' }}>{mark[n.level]}</Text>
                                            <Text style={{ fontSize: 11, color: color[n.level], flexShrink: 1 }}>{n.text}</Text>
                                        </View>
                                    ))}
                                </View>
                            );
                        })() : null}

                        {/* Labor QA on this sheet, before anything goes out —
                            the same rules the Ticket Map's Claude review gets. */}
                        {!isForce && dayInfo.rows ? (() => {
                            const k = `${preview.d}_${preview.kind}`;
                            const qa = qaByCell[k];
                            if (qa === null) return null;
                            return (
                                <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, paddingTop: 8, paddingBottom: 4, paddingLeft: 12, paddingRight: 12, marginBottom: 10 }}>
                                    {qa ? (
                                        <QaFindings
                                            areas={qa.areas}
                                            note={`${qa.people} ${qa.people === 1 ? 'person' : 'people'} · ${qa.prevD ? `compared with ${fmtShort(qa.prevD)}` : 'no earlier work day to compare'}`}
                                        />
                                    ) : (
                                        <Text style={{ fontSize: 11, color: '#94a3b8', paddingBottom: 4 }}>Running labor QA…</Text>
                                    )}
                                </View>
                            );
                        })() : null}

                        {/* A day whose report is already filed in SharePoint
                            shows the document of record — usually the signed
                            copy the webhook filed — with the generated render
                            a toggle away. */}
                        {filedShowing ? (
                            <View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                                    <Text style={{ fontSize: 11, fontWeight: '600', color: '#0f172a', marginRight: 10 }} title={forceDoc.name}>
                                        {forceDoc.name}
                                    </Text>
                                    {forceDoc.webUrl ? (
                                        <Pressable onPress={() => window.open(forceDoc.webUrl, '_blank', 'noopener,noreferrer')} style={{ marginRight: 10, cursor: 'pointer' }}>
                                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>Open in SharePoint</Text>
                                        </Pressable>
                                    ) : null}
                                    <Pressable onPress={() => setShowGenForce(true)} style={{ cursor: 'pointer' }}>
                                        <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>View generated</Text>
                                    </Pressable>
                                </View>
                                {createElement('iframe', {
                                    src: tcFileUrl(forceDoc),
                                    title: forceDoc.name,
                                    style: { width: '100%', height: 620, border: '1px solid #e2e8f0', borderRadius: 6, backgroundColor: '#f8fafc' },
                                })}
                            </View>
                        ) : null}
                        {isForce && forceDoc && showGenForce ? (
                            <Pressable onPress={() => setShowGenForce(false)} style={{ cursor: 'pointer', marginBottom: 6 }}>
                                <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>
                                    {`View the filed copy from SharePoint — ${forceDoc.name}`}
                                </Text>
                            </Pressable>
                        ) : null}
                        {isForce && filedShowing ? null : isForce ? (
                            dayInfo.loading || (!dayInfo.rows && !dayInfo.error) ? (
                                <SkeletonRows rows={6} cells={[{ width: 240 }, { width: 80 }, { width: 80 }, { width: 80 }]} />
                            ) : dayInfo.error ? (
                                <Text style={{ fontSize: 12, color: '#991b1b' }}>Could not load HeavyJob detail: {dayInfo.error}</Text>
                            ) : !force || !force.length ? (
                                <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                                    {heetBy[previewDate] && !hasDetail(previewDate)
                                        ? `The day shows ${pNum(heetBy[previewDate].Staff)} staff and ${h0(heetBy[previewDate].Hours)} hours, but none of HEET, actual cost, or the HeavyJob raw feed still carries the per-person detail the report needs.`
                                        : 'No HeavyJob hours on this day.'}
                                </Text>
                            ) : (() => {
                                // The client's VoltaGrid MP Report sheet, on screen: the
                                // same craft rows, heads under Job Site, the sheet's own
                                // "-" for every zero.
                                const heads = {};
                                let unclassified = 0;
                                force.forEach((g) => {
                                    if (g.key === 'unclassified') unclassified += g.head;
                                    else heads[g.key] = g.head;
                                });
                                const direct = TIER3.filter((t) => t.dir === 'Direct');
                                const indirect = TIER3.filter((t) => t.dir === 'Indirect');
                                const dTot = direct.reduce((a, t) => a + (heads[String(t.code)] || 0), 0);
                                const iTot = indirect.reduce((a, t) => a + (heads[String(t.code)] || 0), 0);
                                const bandHead = (title) => (
                                    <View style={{ flexDirection: 'row' }}>
                                        <View style={{ width: FR_COLS.craft, height: 96, backgroundColor: FR.green, borderColor: FR.line, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Text style={{ fontSize: 11, fontWeight: '700', textAlign: 'center' }}>{title}</Text>
                                        </View>
                                        {frVert('Job Site', FR_COLS.n)}
                                        {frVert('Fab Shop', FR_COLS.n)}
                                        {frVert('Other', FR_COLS.n)}
                                        {frVert('TOTAL WORKING', FR_COLS.tot, { fill: FR.gold })}
                                        {frVert('ABSENT', FR_COLS.n, { color: '#c00000' })}
                                        {frVert('HIRED', FR_COLS.n)}
                                        {frVert('TERMINATED/ CUT-BACK', FR_COLS.n)}
                                    </View>
                                );
                                const bandRow = (label, n, bold, fill) => (
                                    <View key={label} style={{ flexDirection: 'row' }}>
                                        {frCell(label, FR_COLS.craft, { left: true, bold, fill })}
                                        {frCell(frNum(n), FR_COLS.n, { bold, fill })}
                                        {frCell('-', FR_COLS.n, { bold, fill })}
                                        {frCell('-', FR_COLS.n, { bold, fill })}
                                        {frCell(frNum(n), FR_COLS.tot, { bold, fill: FR.gold })}
                                        {frCell('-', FR_COLS.n, { bold, fill, color: '#c00000' })}
                                        {frCell('-', FR_COLS.n, { bold, fill })}
                                        {frCell('-', FR_COLS.n, { bold, fill })}
                                    </View>
                                );
                                const sumRow = (label, v, fill, big) => (
                                    <View key={label} style={{ flexDirection: 'row' }}>
                                        {frCell(label, 190, { left: true, bold: big })}
                                        {frCell(String(v), 110, { bold: true, fill })}
                                    </View>
                                );
                                return (
                                    <View style={{ alignSelf: 'flex-start', width: FR_BAND_W * 2, backgroundColor: '#fff' }}>
                                        {/* Masthead — logo, title, run stamp. */}
                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                            <Image
                                                source={{ uri: `${process.env.PUBLIC_URL || ''}/templates/voltagrid-logo.png` }}
                                                style={{ width: 150, height: 44 }}
                                                resizeMode="contain"
                                                alt="VoltaGrid"
                                            />
                                            <View style={{ flexGrow: 1 }}>
                                                <Text style={{ fontSize: 15, fontWeight: '700', textAlign: 'center', color: '#000', paddingTop: 10 }}>Daily Force Report</Text>
                                            </View>
                                            <Text style={{ fontSize: 9, color: '#64748b', paddingTop: 2 }}>
                                                {`Run time===>  ${moment().format('M/D/YYYY H:mm')}`}
                                            </Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 14, marginBottom: 2 }}>
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#000', width: 90 }}>Contractor</Text>
                                            <Text style={{ fontSize: 11, color: '#000', width: 240, textAlign: 'center', borderBottomWidth: 1, borderColor: '#000', fontWeight: '700' }}>Strike</Text>
                                            <View style={{ flexGrow: 1 }} />
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#000', marginRight: 8 }}>Project Name:</Text>
                                            <Text style={{ fontSize: 11, color: '#000', width: 240, textAlign: 'center', borderBottomWidth: 1, borderColor: '#000', fontWeight: '700' }}>
                                                {jobTitle(dayInfo)}
                                            </Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 6, marginBottom: 10 }}>
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#000', marginRight: 8 }}>LOCATION:</Text>
                                            <Text style={{ fontSize: 11, color: '#000', width: 170, borderBottomWidth: 1, borderColor: '#000' }}> </Text>
                                            <View style={{ flexGrow: 1 }} />
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#000', marginRight: 8 }}>SHIFT:</Text>
                                            <Text style={{ fontSize: 11, color: '#000', width: 150, textAlign: 'center', borderBottomWidth: 1, borderColor: '#000', fontWeight: '700' }}>{SHIFT_DEFAULT}</Text>
                                            <View style={{ flexGrow: 1 }} />
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#000', marginRight: 8 }}>DATE:</Text>
                                            <Text style={{ fontSize: 11, color: '#000', width: 120, textAlign: 'center', borderBottomWidth: 1, borderColor: '#000', fontWeight: '700' }}>
                                                {moment(preview.d, 'YYYY-MM-DD').format('MM/DD/YY')}
                                            </Text>
                                        </View>
                                        {/* The two craft bands, Direct and Indirect side by side. */}
                                        <View style={{ flexDirection: 'row' }}>
                                            <View style={{ width: FR_BAND_W }}>
                                                {bandHead('DIRECT         CRAFT')}
                                                {direct.map((t) => bandRow(t.name, heads[String(t.code)] || 0))}
                                                {bandRow('Sub-Contractor Name', 0)}
                                                {bandRow('TOTAL DIRECT', dTot, true, FR.gold)}
                                            </View>
                                            <View style={{ width: FR_BAND_W }}>
                                                {bandHead('INDIRECT CRAFT')}
                                                {indirect.map((t) => bandRow(t.name, heads[String(t.code)] || 0))}
                                                {bandRow('TOTAL INDIRECT', iTot, true, FR.gold)}
                                            </View>
                                        </View>
                                        {/* Summary of Total Employees, bottom right like the sheet. */}
                                        <View style={{ flexDirection: 'row', marginTop: 14 }}>
                                            <View style={{ flexGrow: 1 }} />
                                            <View style={{ borderWidth: 1, borderColor: FR.line, borderRightWidth: 0, borderBottomWidth: 0 }}>
                                                <View style={{ borderColor: FR.line, borderRightWidth: 1, borderBottomWidth: 1 }}>
                                                    <Text style={{ fontSize: 11, fontWeight: '700', textAlign: 'center', paddingTop: 3, paddingBottom: 3, width: 300 }}>SUMMARY OF TOTAL EMPLOYEES</Text>
                                                </View>
                                                {sumRow('Total Working DIRECT', dTot, FR.green)}
                                                {sumRow('Total Working IN-DIRECT', iTot)}
                                                {sumRow('Total Absent', 0)}
                                                {sumRow('Total Hired', 0)}
                                                {sumRow('Total Terminated or Cut-Back', 0)}
                                                {sumRow('Total', dTot + iTot, undefined, true)}
                                            </View>
                                        </View>
                                        {/* Signature block — the export carries the same one for DocuSign. */}
                                        <View style={{ marginTop: 26, width: 420, borderBottomWidth: 2, borderColor: '#000' }} />
                                        <View style={{ flexDirection: 'row' }}>
                                            <Text style={{ fontSize: 10, color: '#000', width: 300 }}>Customer Representative - Signature</Text>
                                            <Text style={{ fontSize: 10, color: '#000' }}>Date</Text>
                                        </View>
                                        <View style={{ marginTop: 16, width: 420, borderBottomWidth: 2, borderColor: '#000' }} />
                                        <Text style={{ fontSize: 10, color: '#000' }}>Customer Representative - Print</Text>
                                        {unclassified ? (
                                            <Text style={{ fontSize: 11, color: '#b45309', marginTop: 12 }}>
                                                {forceUnclassifiedWarning(unclassified)}
                                            </Text>
                                        ) : null}
                                    </View>
                                );
                            })()
                        ) : (
                            dayInfo.loading || (!dayInfo.rows && !dayInfo.error) ? (
                            <SkeletonRows rows={6} cells={[{ width: 200 }, { width: 240 }, { width: 300 }]} />
                        ) : dayInfo.error ? (
                            <Text style={{ fontSize: 12, color: '#991b1b' }}>Could not load HeavyJob detail: {dayInfo.error}</Text>
                        ) : !ticket || (!ticket.labor.length && !ticket.equipment.length) ? (
                            <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                                {heetBy[previewDate] && !hasDetail(previewDate)
                                    ? `The day shows ${pNum(heetBy[previewDate].Staff)} staff and ${h0(heetBy[previewDate].Hours)} hours, but none of HEET, actual cost, or the HeavyJob raw feed still carries the per-person detail the ticket needs.`
                                    : 'No HeavyJob hours to ticket for this day.'}
                            </Text>
                        ) : !shiftTicket || (!shiftTicket.labor.length && !shiftTicket.equipment.length) ? (
                            <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                                {`No one is on the ${String(preview.kind).toLowerCase()} sheet for this day — `
                                    + `the whole crew is on the ${preview.kind === 'Night' ? 'day' : 'night'} sheet. `
                                    + 'Open that sheet and use its Shift column to move people over.'}
                            </Text>
                        ) : (() => {
                            // The exported Field Ticket PDF, on screen: same
                            // header, matrix, shift summary, disclaimer and
                            // signature block. The one thing the paper lacks
                            // is the Shift column — the preview's control for
                            // moving a person between the day and night
                            // sheets.
                            const ticket = shiftTicket; // eslint-disable-line no-shadow
                            // Only the codes this shift touched — same cut as
                            // the PDF, so the columns match the export.
                            const used = new Set();
                            ticket.labor.forEach((p) => Object.keys(p.hours).forEach((c) => { if (p.hours[c]) used.add(c); }));
                            ticket.equipment.forEach((e) => Object.keys(e.hours).forEach((c) => { if (e.hours[c]) used.add(c); }));
                            const codes = ticket.codes.filter((c) => used.has(c.code));
                            // Same pricing as the PDF and billing.js: sheet
                            // rate off the job's schedule, per diem on top.
                            const sched = mergeRateSchedule(userRows);
                            const rateByCls = {};
                            sched.labor.forEach((r) => {
                                if (r.name && pNum(r.wage) > 0) rateByCls[r.name] = { st: stRate(r.wage, sched.cfg), ot: otRate(r.wage, sched.cfg) };
                            });
                            const billFor = (p) => {
                                const r = rateByCls[p.cls];
                                if (!r) return null;
                                const ot = p.ot || 0;
                                return round2((p.total - ot) * r.st + ot * r.ot + (p.pdAmt || 0));
                            };
                            const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            const bills = ticket.labor.map(billFor);
                            const billTotal = round2(bills.reduce((a, b) => a + (b || 0), 0));
                            const laborTotal = ticket.labor.reduce((a, p) => a + p.total, 0);
                            const equipTotal = ticket.equipment.reduce((a, e) => a + e.total, 0);
                            const colSum = (list, code) => h2(list.reduce((a, x) => a + (x.hours[code] || 0), 0)) || '';
                            const sheetW = Math.max(720, TKW_LEFT + codes.length * TKW.code + TKW.tot + TKW.bill);
                            const headH = 58; // room for the rotated codes, like the PDF's head band
                            const cell = (w, extra) => ({
                                width: w, fontSize: 11, color: TK.ink, flexShrink: 0,
                                paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, ...extra,
                            });
                            const th = (label, w, extra) => (
                                <View key={label} style={{ width: w, flexShrink: 0, justifyContent: 'flex-end', paddingBottom: 7, paddingLeft: 4, paddingRight: 4 }}>
                                    <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff', letterSpacing: 0.6, ...extra }}>{label}</Text>
                                </View>
                            );
                            // Real phase codes run 6-8 characters — rotated
                            // 90° in the dark band, the way the PDF prints
                            // them.
                            const codeHead = (c) => (
                                <View key={c.code} style={{ width: TKW.code, height: headH, position: 'relative', flexShrink: 0 }}>
                                    <Text style={{
                                        position: 'absolute', bottom: 8, left: TKW.code / 2 + 5,
                                        transform: 'rotate(-90deg)', transformOrigin: 'left bottom',
                                        fontSize: 9, fontWeight: '700', color: '#fff', whiteSpace: 'nowrap',
                                    }}
                                    >
                                        {String(c.code)}
                                    </Text>
                                </View>
                            );
                            const section = (label, right) => (
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 18, marginBottom: 6 }}>
                                    <View style={{ width: 3, height: 11, backgroundColor: TK.blue, marginRight: 7 }} />
                                    <Text style={{ fontSize: 11, fontWeight: '700', color: TK.ink, letterSpacing: 1.2 }}>{label}</Text>
                                    <View style={{ flexGrow: 1 }} />
                                    {right ? <Text style={{ fontSize: 10, color: TK.mute }}>{right}</Text> : null}
                                </View>
                            );
                            const stats = [
                                ['LABOR HOURS', h2(laborTotal) || '0'],
                                ...(ticket.equipment.length ? [['EQUIPMENT HOURS', h2(equipTotal) || '0']] : []),
                                ['CREW SIZE', String(ticket.labor.length)],
                                ['EST. BILLABLE', usd(billTotal)],
                            ];
                            const jobLine = [`Job ${jobKey}`, jobClient(dayInfo), jobTitle(dayInfo)].filter(Boolean).join('   —   ');
                            return (
                            <View style={{ alignSelf: 'flex-start' }}>
                                {/* The PDF prints bare codes over the matrix, so
                                    the descriptions ride above the sheet as UI. */}
                                {codes.length ? (
                                    <Text style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8, maxWidth: sheetW }}>
                                        {`Phase codes: ${codes.map((c) => `${c.code} ${c.desc}`).join('  ·  ')}`}
                                    </Text>
                                ) : null}
                                <View style={{ width: sheetW, backgroundColor: '#fff', paddingBottom: 12 }}>
                                    {/* Header — both marks over the brand rule. */}
                                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Image
                                            source={{ uri: `${process.env.PUBLIC_URL || ''}/templates/strike-logo.png` }}
                                            style={{ width: 150, height: 40 }}
                                            resizeMode="contain"
                                            alt="Strike"
                                        />
                                        <Image
                                            source={{ uri: `${process.env.PUBLIC_URL || ''}/templates/voltagrid-logo.png` }}
                                            style={{ width: 140, height: 26 }}
                                            resizeMode="contain"
                                            alt="VoltaGrid"
                                        />
                                    </View>
                                    <View style={{ flexDirection: 'row', marginTop: 8 }}>
                                        <View style={{ flexGrow: 1, height: 3, backgroundColor: TK.blue }} />
                                        <View style={{ width: 42, height: 3, backgroundColor: TK.red, marginLeft: 5 }} />
                                    </View>
                                    {/* Title line: shift badge beside it, date right. */}
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
                                        <Text style={{ fontSize: 19, fontWeight: '700', color: TK.ink, letterSpacing: 2 }}>FIELD TICKET</Text>
                                        <View style={{
                                            backgroundColor: preview.kind === 'Night' ? TK.ink : TK.blue, borderRadius: 9,
                                            paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9, marginLeft: 12,
                                        }}
                                        >
                                            <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff', letterSpacing: 1 }}>{`${String(preview.kind).toUpperCase()} SHIFT`}</Text>
                                        </View>
                                        <View style={{ flexGrow: 1 }} />
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: TK.ink }}>
                                            {moment(previewDate, 'YYYY-MM-DD').format('dddd, MMMM D, YYYY')}
                                        </Text>
                                    </View>
                                    <Text style={{ fontSize: 11, color: TK.mute, marginTop: 4 }}>{jobLine}</Text>
                                    {/* ── Labor matrix ── */}
                                    {ticket.labor.length ? (
                                        <>
                                            {section('LABOR', `${ticket.labor.length} ${ticket.labor.length === 1 ? 'person' : 'people'} — ${h2(laborTotal) || 0} hours`)}
                                            <View style={{ flexDirection: 'row', alignItems: 'flex-end', backgroundColor: TK.ink, height: headH }}>
                                                {th('EMPLOYEE', TKW.name)}
                                                {th('ID', TKW.id, { textAlign: 'center' })}
                                                {th('CLASSIFICATION', TKW.cls)}
                                                {th('SHIFT', TKW.shift, { textAlign: 'center' })}
                                                {th('CREW LEAD', TKW.lead)}
                                                {['PD', 'TP', 'RR'].map((f) => th(f, TKW.flag, { textAlign: 'center' }))}
                                                {codes.map(codeHead)}
                                                {th('TOTAL', TKW.tot, { textAlign: 'center' })}
                                                {th('BILLABLE', TKW.bill, { textAlign: 'right' })}
                                            </View>
                                            {ticket.labor.map((p, pi) => (
                                                <View key={p.id} style={{ flexDirection: 'row', alignItems: 'center', borderTopWidth: pi ? 1 : 0, borderColor: TK.line, backgroundColor: pi % 2 ? TK.zebra : '#fff' }}>
                                                    <Text style={cell(TKW.name, { fontWeight: '600' })}>{p.name}</Text>
                                                    <Text style={cell(TKW.id, { textAlign: 'center' })}>{p.id}</Text>
                                                    <Text style={cell(TKW.cls)}>{p.cls}</Text>
                                                    {/* The Shift cell is the adjustment: D/N per person,
                                                        stored as the day's override. Moving someone off
                                                        this shift moves them to the other sheet. */}
                                                    {versionLocked ? (
                                                        <Text style={cell(TKW.shift, { textAlign: 'center' })}>{p.shift || ''}</Text>
                                                    ) : (
                                                        <View style={{ width: TKW.shift, flexShrink: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingLeft: 2, paddingRight: 2 }}>
                                                            {SHIFTS.map((s) => {
                                                                const on = (s === 'Night') === Boolean(p.night);
                                                                return (
                                                                    <Pressable
                                                                        key={s}
                                                                        onPress={() => !on && saveShift(preview.d, p.id, s)}
                                                                        title={on ? `On the ${s.toLowerCase()} sheet` : `Move ${p.name} to the ${s.toLowerCase()} sheet`}
                                                                        style={{
                                                                            width: 20, alignItems: 'center', borderRadius: 3, marginLeft: 1, marginRight: 1,
                                                                            backgroundColor: on ? TK.ink : '#f1f5f9', cursor: on ? 'default' : 'pointer',
                                                                        }}
                                                                    >
                                                                        <Text style={{ fontSize: 10, fontWeight: '700', color: on ? '#fff' : '#94a3b8' }}>{s === 'Day' ? 'D' : 'N'}</Text>
                                                                    </Pressable>
                                                                );
                                                            })}
                                                        </View>
                                                    )}
                                                    <Text style={cell(TKW.lead)}>{p.crewLead}</Text>
                                                    <Text style={cell(TKW.flag, { textAlign: 'center', fontWeight: '700' })}>{p.pd ? 'X' : ''}</Text>
                                                    <Text style={cell(TKW.flag, { textAlign: 'center', fontWeight: '700' })}>{p.tp ? 'X' : ''}</Text>
                                                    <Text style={cell(TKW.flag, { textAlign: 'center', fontWeight: '700' })}>{p.rr ? 'X' : ''}</Text>
                                                    {codes.map((c) => (
                                                        <Text key={c.code} style={cell(TKW.code, { textAlign: 'center' })}>{h2(p.hours[c.code]) || ''}</Text>
                                                    ))}
                                                    <Text style={cell(TKW.tot, { textAlign: 'center', fontWeight: '700' })}>{h2(p.total) || ''}</Text>
                                                    <Text style={cell(TKW.bill, { textAlign: 'right', fontWeight: '700' })}>{bills[pi] == null ? '—' : usd(bills[pi])}</Text>
                                                </View>
                                            ))}
                                            <View style={{ flexDirection: 'row', backgroundColor: TK.fill, borderTopWidth: 2, borderColor: TK.ink }}>
                                                <Text style={cell(TKW_LEFT, { fontWeight: '700', textAlign: 'right' })}>Totals</Text>
                                                {codes.map((c) => (
                                                    <Text key={c.code} style={cell(TKW.code, { fontWeight: '700', textAlign: 'center' })}>{colSum(ticket.labor, c.code)}</Text>
                                                ))}
                                                <Text style={cell(TKW.tot, { fontWeight: '700', textAlign: 'center' })}>{h2(laborTotal) || ''}</Text>
                                                <Text style={cell(TKW.bill, { fontWeight: '700', textAlign: 'right' })}>{usd(billTotal)}</Text>
                                            </View>
                                        </>
                                    ) : null}
                                    {/* ── Equipment — no dollars: HEET rows carry no tier. ── */}
                                    {ticket.equipment.length ? (
                                        <>
                                            {section('EQUIPMENT', `${ticket.equipment.length} ${ticket.equipment.length === 1 ? 'unit' : 'units'} — ${h2(equipTotal) || 0} hours`)}
                                            <View style={{ flexDirection: 'row', alignItems: 'flex-end', backgroundColor: TK.ink, height: headH }}>
                                                {th('EQUIPMENT', TKW_LEFT - 70)}
                                                {th('UNIT NO', 70, { textAlign: 'center' })}
                                                {codes.map(codeHead)}
                                                {th('TOTAL', TKW.tot, { textAlign: 'center' })}
                                            </View>
                                            {ticket.equipment.map((e, ei) => (
                                                <View key={e.unit || e.desc} style={{ flexDirection: 'row', alignItems: 'center', borderTopWidth: ei ? 1 : 0, borderColor: TK.line, backgroundColor: ei % 2 ? TK.zebra : '#fff' }}>
                                                    <Text style={cell(TKW_LEFT - 70, { fontWeight: '600' })}>{e.rental ? `${e.desc}  (Rental)` : e.desc}</Text>
                                                    <Text style={cell(70, { textAlign: 'center' })}>{e.unit}</Text>
                                                    {codes.map((c) => (
                                                        <Text key={c.code} style={cell(TKW.code, { textAlign: 'center' })}>{h2(e.hours[c.code]) || ''}</Text>
                                                    ))}
                                                    <Text style={cell(TKW.tot, { textAlign: 'center', fontWeight: '700' })}>{h2(e.total) || ''}</Text>
                                                </View>
                                            ))}
                                            <View style={{ flexDirection: 'row', backgroundColor: TK.fill, borderTopWidth: 2, borderColor: TK.ink }}>
                                                <Text style={cell(TKW_LEFT, { fontWeight: '700', textAlign: 'right' })}>Total Hours</Text>
                                                {codes.map((c) => (
                                                    <Text key={c.code} style={cell(TKW.code, { fontWeight: '700', textAlign: 'center' })}>{colSum(ticket.equipment, c.code)}</Text>
                                                ))}
                                                <Text style={cell(TKW.tot, { fontWeight: '700', textAlign: 'center' })}>{h2(equipTotal) || ''}</Text>
                                            </View>
                                        </>
                                    ) : null}
                                    {/* ── Shift summary — the numbers a signer checks ── */}
                                    <View style={{ flexDirection: 'row', backgroundColor: TK.fill, borderRadius: 6, marginTop: 18, paddingTop: 10, paddingBottom: 12, paddingLeft: 16, paddingRight: 16 }}>
                                        {stats.map(([label, value]) => (
                                            <View key={label} style={{ flexGrow: 1 }}>
                                                <Text style={{ fontSize: 8, fontWeight: '700', color: TK.mute, letterSpacing: 0.8 }}>{label}</Text>
                                                <Text style={{ fontSize: 17, fontWeight: '700', color: TK.blue, marginTop: 3 }}>{value}</Text>
                                            </View>
                                        ))}
                                    </View>
                                    {/* The disclaimer the client signs under. */}
                                    <Text style={{ fontSize: 9, color: TK.mute, marginTop: 14, maxWidth: 640 }}>
                                        {'Billable amounts shown are estimates priced from the applicable rate schedule. '
                                            + 'Overtime is an estimate: overtime and per diem are calculated on a weekly basis '
                                            + 'and will change based on each individual’s total weekly time. '
                                            + 'Final amounts are reconciled on the invoice.'}
                                    </Text>
                                    {/* Signature block — one line per signer for this shift, the
                                        same labels the DocuSign tabs anchor on. */}
                                    {signerSlots(signerMap, preview.kind, 'full', preview.d).map((slot) => (
                                        <View key={slot.label}>
                                            <View style={{ flexDirection: 'row', marginTop: 30 }}>
                                                <View style={{ width: 250, height: 18, borderBottomWidth: 1.5, borderColor: TK.ink }} />
                                                <View style={{ width: 60 }} />
                                                <View style={{ width: 110, height: 18, borderBottomWidth: 1.5, borderColor: TK.ink }} />
                                            </View>
                                            <View style={{ flexDirection: 'row' }}>
                                                <Text style={{ width: 250, fontSize: 10, color: TK.mute, paddingTop: 4 }}>{`${slot.label} - Signature`}</Text>
                                                <View style={{ width: 60 }} />
                                                <Text style={{ width: 110, fontSize: 10, color: TK.mute, paddingTop: 4 }}>Date</Text>
                                            </View>
                                            <View style={{ width: 250, height: 18, borderBottomWidth: 1.5, borderColor: TK.ink, marginTop: 20 }} />
                                            <View style={{ flexDirection: 'row', width: 250, justifyContent: 'space-between' }}>
                                                <Text style={{ fontSize: 10, color: TK.mute, paddingTop: 4 }}>{`${slot.label} - Print`}</Text>
                                                {slot.name ? <Text style={{ fontSize: 10, color: TK.mute, paddingTop: 4 }}>{slot.name}</Text> : null}
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            </View>
                            );
                        })()
                        )}

                        {/* ── The trail ──
                            The stage stamps are what project controls decided;
                            the signature requests under them are what the
                            client did. Kept as two lists rather than one
                            interleaved one, and every attempt is listed, so a
                            sheet that was declined twice before it stuck reads
                            that way. */}
                        {!isForce && sheet ? (
                            <View style={{ marginTop: 18, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 10 }}>
                                <Text style={{ fontSize: 10, fontWeight: '700', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4 }}>HISTORY</Text>
                                {sheet.h.map((e, j) => (
                                    <Text key={j} style={{ fontSize: 11, color: '#64748b', paddingTop: 1, paddingBottom: 1 }}>
                                        {`${e.t}  ${e.s} — ${e.u}${e.n ? `: ${e.n}` : ''}`}
                                    </Text>
                                ))}
                                {(sigAllBy[`${preview.d}_${preview.kind}`] || []).map((e, ei) => (
                                    <View key={e.RowID} style={{ paddingTop: 6 }}>
                                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#94a3b8' }}>
                                            {`Signature request ${ei + 1} — ${e.Signer_Email || 'no signer'}`
                                                + `${e.Sent_By ? ` · sent by ${e.Sent_By}` : ''}`}
                                        </Text>
                                        {(e.events || []).map((v) => (
                                            <Text key={v.RowID} style={{ fontSize: 11, color: '#64748b', paddingTop: 1, paddingBottom: 1 }}>
                                                {`${fmtUtcLong(v.Occurred_At_Utc)}  ${eventLabel(v.Event)}`
                                                    + ` — ${v.Actor || 'system'}${v.Note ? `: ${v.Note}` : ''}`}
                                            </Text>
                                        ))}
                                        {!(e.events || []).length ? (
                                            <Text style={{ fontSize: 11, color: '#94a3b8' }}>no events recorded yet</Text>
                                        ) : null}
                                    </View>
                                ))}
                            </View>
                        ) : null}

                        {/* The revision desk. It reads the sheet exactly as
                            the PDF would print it — and the labor QA that
                            just ran over it — so "who's on the wrong shift"
                            is answered off this document, and the fix is a
                            button rather than a hunt down the Shift column.
                            The Force Report is a headcount roll-up nobody
                            revises, so it doesn't get one. */}
                        {!isForce && shiftTicket && shiftTicket.labor.length ? (() => {
                            const ai = ticketAi(preview.d, preview.kind, shiftTicket);
                            const qa = qaByCell[`${preview.d}_${preview.kind}`];
                            return (
                                <View style={{ marginTop: 16 }}>
                                    <BillingAiChat
                                        section="fieldTicket"
                                        title={`Revise the ${String(preview.kind).toLowerCase()} ticket`}
                                        hint="e.g. move the night crew off this sheet, or what would keep this from being signed?"
                                        context={() => ai.context(qa ? qaItems(qa.areas).map((x) => ({
                                            level: x.level, text: x.text,
                                        })) : null)}
                                        actions={ai.actions}
                                        locked={ai.locked}
                                    />
                                </View>
                            );
                        })() : null}

                        {/* The Force Report's send trail — every email the
                            day's report has gone out under. */}
                        {isForce && (forceSends[preview.d] || []).length ? (
                            <View style={{ marginTop: 18, borderTopWidth: 1, borderColor: '#f1f5f9', paddingTop: 10 }}>
                                <Text style={{ fontSize: 10, fontWeight: '700', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4 }}>HISTORY</Text>
                                {(forceSends[preview.d] || []).map((e, ei) => (
                                    <Text key={ei} style={{ fontSize: 11, color: '#64748b', paddingTop: 1, paddingBottom: 1 }}>
                                        {`${e.t}  Emailed to ${e.to}${e.u ? ` — ${e.u}` : ''}`}
                                    </Text>
                                ))}
                            </View>
                        ) : null}
                    </View>
                </View>
                );
            })() : null}

            {/* ── Force Report email dialog ──
                Who it goes to, what it says, and a plain statement of what
                pressing the button will do — this one leaves the building,
                so it should never be ambiguous that a real client is about
                to get a real email. (Time sheet signature sends happen in the
                Ticket Map.) */}
            {sendPrompt && sendForm ? (() => {
                const info = dayViews[sendPrompt.d] || {};
                const fr = info.rows ? rollUp(peopleFrom(info.rows, ratemap)) : null;
                const ready = Boolean(fr && fr.length);
                const field = (label, key, opts = {}) => (
                    <View style={{ marginBottom: 8 }}>
                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.6, marginBottom: 3 }}>{label}</Text>
                        <TextInput
                            value={String(sendForm[key] ?? '')}
                            onChangeText={(v) => setSendForm((f) => ({ ...f, [key]: v }))}
                            multiline={opts.multiline}
                            style={{
                                fontSize: 12, color: '#0f172a', borderWidth: 1, borderColor: '#cbd5e1',
                                borderRadius: 4, paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8,
                                width: '100%', ...(opts.multiline ? { height: 64 } : {}),
                            }}
                        />
                    </View>
                );
                return (
                    <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.35)', alignItems: 'center', justifyContent: 'center', zIndex: 70 }}>
                        <View style={{ backgroundColor: '#fff', borderRadius: 8, paddingTop: 18, paddingBottom: 18, paddingLeft: 22, paddingRight: 22, width: 520, maxWidth: '94%', maxHeight: '92%', overflow: 'auto' }}>
                            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginBottom: 2 }}>
                                {`Email to Client — Daily Force Report ${fmtDay(sendPrompt.d)}`}
                            </Text>
                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                                {`Job ${jobKey} · ${forceName(sendPrompt.d)}`}
                            </Text>

                            {info.loading || (!info.rows && !info.error) ? (
                                <SkeletonRows rows={3} cells={[{ width: 200 }, { width: 240 }]} />
                            ) : info.error ? (
                                <Text style={{ fontSize: 12, color: '#991b1b', marginBottom: 10 }}>
                                    {`Could not load HeavyJob detail: ${info.error}`}
                                </Text>
                            ) : !ready ? (
                                <Text style={{ fontSize: 12, color: '#b45309', marginBottom: 10 }}>
                                    No HeavyJob hours for this day — there is no report to send.
                                </Text>
                            ) : (
                                <Text style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
                                    {`${fr.reduce((a, g) => a + g.head, 0)} people across ${fr.length} classification${fr.length === 1 ? '' : 's'}`}
                                </Text>
                            )}

                            {field('Send to (comma separated)', 'to')}
                            {field('Copy to (comma separated)', 'cc')}
                            {field('Subject', 'subject')}
                            {field('Message', 'message', { multiline: true })}

                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                                {`The report is attached as an Excel workbook and emailed to `
                                  + `${String(sendForm.to || '').trim() || 'the recipients'} straight away — `
                                  + 'no signature request. Set the default recipients under Rates & Rules.'}
                            </Text>

                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Pressable
                                    onPress={() => { setSendPrompt(null); setSendForm(null); }}
                                    style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14, marginRight: 8, cursor: 'pointer' }}
                                >
                                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#475569' }}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={submitSend}
                                    disabled={sending || !ready}
                                    style={{
                                        backgroundColor: '#0f172a', borderRadius: 4,
                                        paddingTop: 6, paddingBottom: 6, paddingLeft: 16, paddingRight: 16,
                                        cursor: sending || !ready ? 'default' : 'pointer',
                                        opacity: sending || !ready ? 0.5 : 1,
                                    }}
                                >
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>
                                        {sending ? 'Sending…' : 'Send Email'}
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    </View>
                );
            })() : null}

            {movePrompt ? (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.35)', alignItems: 'center', justifyContent: 'center', zIndex: 70 }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, paddingTop: 18, paddingBottom: 18, paddingLeft: 22, paddingRight: 22, width: 420, maxWidth: '92%' }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginBottom: 4 }}>
                            {`${movePrompt.mv.label} — ${fmtDay(movePrompt.sheet.d)} ${movePrompt.sheet.sh}`}
                        </Text>
                        {movePrompt.mv.to === 'Removed' ? (
                            <Text style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>
                                Removes this sheet from the tracker. Its history stays stored and tracking it again brings it back at Pending.
                            </Text>
                        ) : null}
                        <TextInput
                            value={moveNote}
                            onChangeText={setMoveNote}
                            placeholder="Note (optional)"
                            style={{ fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, marginBottom: 12 }}
                        />
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                            <Pressable onPress={() => { setMovePrompt(null); setMoveNote(''); }} style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14, marginRight: 8, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 12, color: '#475569', fontWeight: '600' }}>Cancel</Text>
                            </Pressable>
                            <Pressable onPress={applyMove} style={{ backgroundColor: '#0f172a', borderRadius: 4, paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>{movePrompt.mv.label}</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            ) : null}
        </>
    );
};

export default BillingTimecardApproval;
